import React, { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from 'xlsx';
import "./App.css";
import ProjectionsView from "./views/ProjectionsView";



const apiBase = process.env.NODE_ENV === "development"
  ? "http://localhost:3000"
  : "https://fantasyamp.com";


const positionOptions = ["All", "QB", "RB", "WR", "TE"];

const P4_SCHOOLS = new Set([
  // SEC
  'Alabama', 'Arkansas', 'Auburn', 'Florida', 'Georgia', 'Kentucky', 'LSU',
  'Mississippi State', 'Missouri', 'Ole Miss', 'South Carolina', 'Tennessee',
  'Texas', 'Texas A&M', 'Vanderbilt',
  // Big Ten
  'Illinois', 'Indiana', 'Iowa', 'Maryland', 'Michigan', 'Michigan State',
  'Minnesota', 'Nebraska', 'Northwestern', 'Ohio State', 'Oregon', 'Penn State',
  'Purdue', 'Rutgers', 'UCLA', 'USC', 'Washington', 'Wisconsin',
  // Big 12
  'Arizona', 'Arizona State', 'Baylor', 'BYU', 'Cincinnati', 'Colorado',
  'Houston', 'Iowa State', 'Kansas', 'Kansas State', 'Oklahoma', 'Oklahoma State',
  'TCU', 'Texas Tech', 'UCF', 'Utah', 'West Virginia',
  // ACC
  'Boston College', 'California', 'Clemson', 'Duke', 'Florida State',
  'Georgia Tech', 'Louisville', 'Miami', 'NC State', 'Pittsburgh', 'SMU',
  'Stanford', 'Syracuse', 'Virginia', 'Virginia Tech', 'Wake Forest',
  // Independent (P4 caliber)
  'Notre Dame',
]);

// Sleeper rookie ADP 2026 — ordered by ADP position
const SLEEPER_ROOKIE_ADP_2026 = [
  'Jeremiyah Love', 'Fernando Mendoza', 'Carnell Tate', 'Makai Lemon',
  'Jordyn Tyson', 'Kenyon Sadiq', 'KC Concepcion', 'Omar Cooper Jr.',
  'Jadarian Price', 'Denzel Boston', 'Ty Simpson', 'Eli Stowers',
  'Mike Washington Jr.', 'Jonah Coleman', 'Nicholas Singleton', 'Elijah Sarratt',
  'Emmett Johnson', 'Zachariah Branch', 'Chris Brazzell II', 'Chris Bell',
  'Malachi Fields', 'Kaytron Allen', "Ja'Kobi Lane", 'Antonio Williams',
  'Garrett Nussmeier', 'Germie Bernard', 'Skyler Bell', 'Max Klare',
  'Michael Trigg', 'Demond Claiborne', "Le'Veon Moss", 'Ted Hurst',
  "J'Mari Taylor", 'Cade Klubnik', 'Deion Burks', 'Jack Endries',
  'Carson Beck', 'Justin Joly', 'Josh Cameron', 'Jeff Caldwell',
  'Brenen Thompson', 'DJ Rogers', 'Eric McAlister', 'Joe Fagnano',
  'John Michael Gyllenborg', 'Dane Key', 'Cole Payton', "Dae'Quan Wright",
  'Malik Benson', 'Desmond Reid', 'Jordan Hudson', 'Matthew Hibner',
  'Rahsul Faison', 'Sam Roush', 'Dan Villari', 'Oscar Delp',
  'Noah Thomas', 'Eric Rivers', 'Luke Altmyer', 'Aaron Anderson',
  'Bauer Sharp', 'Marlin Klein', 'Taylen Green', 'Adam Randall',
  'Nate Boerkircher', 'Reggie Virgil', 'Caleb Douglas', 'Behren Morton',
  'Lake McRee', 'Kejon Owens', 'Drew Allar', 'CJ Donaldson',
  'Josh Cuevas', 'CJ Daniels',
];
// Normalize player names for fuzzy matching (strips periods, suffixes, apostrophes)
const normalizePlayerName = (name) =>
  name.toLowerCase()
    .replace(/[.''`]/g, '')
    .replace(/\s+(jr|sr|ii|iii|iv|v)$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
const sleeperAdpMap = new Map();
SLEEPER_ROOKIE_ADP_2026.forEach((name, i) => {
  sleeperAdpMap.set(normalizePlayerName(name), Math.max(1, 100 - i * 1.36));
});

// Helper function to get rank change emoji
const getRankChangeEmoji = (rank, priorRank) => {
  if (!priorRank || !rank) return null;

  const delta = priorRank - rank; // positive = moved up (better), negative = moved down (worse)

  if (delta >= 20) return '🚀';
  if (delta >= 10) return '📈';
  if (delta >= 5) return '⬆️';
  if (delta <= -20) return '💀';
  if (delta <= -10) return '📉';
  if (delta <= -5) return '⬇️';

  // Small changes (-4 to +4) are treated as "steady" - anchored
  return '⚓';
};

// Calculate composite AMP score with weights: Momentum 55%, Production 35%, Adjusted Competition 10%
// (Adj Comp weighted lightly since Momentum = projected draft capital already prices in competition level.)
const calculateCompositeAmpScore = (player) => {
  const momentum = player?.amp_momentum_score || 0;
  const production = player?.amp_production_score || 0;
  const analytics = player?.amp_analytics_score || 0;

  return ((momentum * 0.55) + (production * 0.35) + (analytics * 0.10)).toFixed(2);
};

// Get tier/grade based on composite AMP score
const getAmpTier = (score) => {
  if (score >= 85) return { tier: 'S', color: '#10b981', bg: '#d1fae5', label: 'Elite' };
  if (score >= 75) return { tier: 'A', color: '#3b82f6', bg: '#dbeafe', label: 'Excellent' };
  if (score >= 65) return { tier: 'B', color: '#8b5cf6', bg: '#ede9fe', label: 'Good' };
  if (score >= 55) return { tier: 'C', color: '#f59e0b', bg: '#fef3c7', label: 'Average' };
  return { tier: 'D', color: '#ef4444', bg: '#fee2e2', label: 'Below Average' };
};

// Parse draft pick into round and pick number
const parseDraftPick = (draftCapital) => {
  if (!draftCapital) return null;
  const match = draftCapital.match(/(\d+)\.(\d+)/);
  if (!match) return null;
  return { round: parseInt(match[1]), pick: parseInt(match[2]) };
};

// Get pick range label
const getPickRangeLabel = (round, startPick, endPick) => {
  const roundSuffix = round === 1 ? '1st' : round === 2 ? '2nd' : round === 3 ? '3rd' : `${round}th`;
  if (startPick === 1 && endPick === 4) return `${roundSuffix} Early`;
  if (startPick === 5 && endPick === 8) return `${roundSuffix} Mid`;
  if (startPick === 9 && endPick === 12) return `${roundSuffix} Late`;
  return `${roundSuffix} ${startPick}-${endPick}`;
};

const getDraftRoundColor = (round) => {
  const colors = {
    1: '#FFD700', // gold
    2: '#C0C0C0', // silver
    3: '#CD7F32', // bronze
    4: '#8B4513', // brown
    5: '#000000', // black
    6: '#8B5CF6', // purple
    7: '#14B8A6', // teal
  };
  return colors[round] || '#ef4444'; // red for undrafted/UDFA
};

const getPosRankAccent = (rank) => {
  if (rank === 1) return '#FFD700';
  if (rank <= 3) return '#FFC107';
  if (rank <= 6) return '#FFB300';
  if (rank <= 10) return '#FF9F00';
  if (rank <= 15) return '#E67E22';
  return '#B87333';
};

// Pick Range Detail Modal Component
const PickRangeModal = ({ pickRange, onClose }) => {
  if (!pickRange) return null;

  const hasScores = pickRange.hasScores;
  const avgScore = hasScores && pickRange.players.length > 0
    ? (pickRange.players.reduce((sum, p) => sum + parseFloat(calculateCompositeAmpScore(p)), 0) / pickRange.players.length).toFixed(2)
    : null;
  const tier = hasScores && avgScore ? getAmpTier(avgScore) : null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
          borderRadius: "20px",
          maxWidth: "900px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          padding: "0",
        }}
      >
        {/* Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)",
            padding: "24px",
            borderBottom: "2px solid rgba(255, 215, 0, 0.3)",
            zIndex: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h2 style={{ margin: 0, color: "#fff", fontSize: "1.8rem" }}>
                {pickRange.year} {pickRange.label}
              </h2>
              <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
                {hasScores && tier ? (
                  <div
                    style={{
                      background: tier.bg,
                      color: tier.color,
                      padding: "6px 14px",
                      borderRadius: "8px",
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      border: `2px solid ${tier.color}`,
                    }}
                  >
                    Avg: {avgScore}
                  </div>
                ) : (
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      color: "#FFD700",
                      padding: "6px 14px",
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      border: "2px solid rgba(255, 215, 0, 0.4)",
                    }}
                  >
                    Scores Coming Soon
                  </div>
                )}
                <div style={{ color: "#FFD700", fontSize: "0.95rem", fontWeight: "600" }}>
                  {pickRange.players.length} Players
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                fontSize: "1.5rem",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Players Grid */}
        <div style={{ padding: "24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            {pickRange.players.map((player) => {
              const playerScore = hasScores ? calculateCompositeAmpScore(player) : null;
              const playerTier = hasScores && playerScore ? getAmpTier(playerScore) : null;

              // Default border color for players without scores
              const borderColor = hasScores && playerTier ? playerTier.color : "#667eea";
              const bgColor = hasScores && playerTier ? playerTier.bg : "rgba(102, 126, 234, 0.1)";

              return (
                <div
                  key={player.player_id}
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                    border: `2px solid ${borderColor}`,
                  }}
                >
                  {/* Player Image */}
                  {(player.headshot_url || player.image) ? (
                    <img
                      src={player.headshot_url || player.image}
                      alt={`${player.first_name} ${player.last_name}`}
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        marginBottom: "12px",
                        border: `3px solid ${borderColor}`,
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: bgColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px",
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: borderColor,
                        border: `3px solid ${borderColor}`,
                      }}
                    >
                      {player.first_name[0]}{player.last_name[0]}
                    </div>
                  )}

                  {/* Draft Pick */}
                  <div
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      color: "#667eea",
                      marginBottom: "6px",
                    }}
                  >
                    {player.draft_pick_capital}
                  </div>

                  {/* Player Name */}
                  <div
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#1a202c",
                      marginBottom: "4px",
                    }}
                  >
                    {player.first_name} {player.last_name}
                  </div>

                  {/* Position */}
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#4a5568",
                      marginBottom: hasScores ? "8px" : "0",
                    }}
                  >
                    {player.position}
                  </div>

                  {/* AMP Score - Only show if year has scores */}
                  {hasScores && playerTier && (
                    <div
                      style={{
                        background: playerTier.bg,
                        color: playerTier.color,
                        padding: "6px 10px",
                        borderRadius: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "700",
                        border: `1.5px solid ${playerTier.color}`,
                      }}
                    >
                      {playerScore}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Player Comparison Modal Component
const ComparisonModal = ({ players, onClose, onRemovePlayer, isPaid }) => {
  if (players.length === 0) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return { bg: "#d1fae5", text: "#065f46" };
    if (score >= 60) return { bg: "#fef9c3", text: "#92400e" };
    return { bg: "#fee2e2", text: "#991b1b" };
  };

  const getBestInCategory = (category) => {
    return Math.max(...players.map(p => p?.[category] || 0));
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        overflowY: "auto",
        animation: "fadeIn 0.3s ease",
      }}
      onClick={onClose}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
          borderRadius: "20px",
          maxWidth: "1400px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          animation: "slideUp 0.4s ease",
          padding: "0",
        }}
      >
        {/* Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)",
            padding: "24px",
            borderBottom: "2px solid rgba(255, 215, 0, 0.3)",
            zIndex: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <h2
            style={{
              color: "#FFD700",
              fontSize: "1.8rem",
              fontWeight: "800",
              margin: 0,
              textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            }}
          >
            ⚖️ Player Comparison ({players.length})
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              color: "white",
              fontSize: "1.5rem",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              cursor: "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#FF4444";
              e.currentTarget.style.transform = "rotate(90deg)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "rotate(0deg)";
            }}
          >
            ×
          </button>
        </div>

        {/* Comparison Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(players.length, 3)}, 1fr)`,
            gap: "20px",
            padding: "24px",
          }}
        >
          {players.map((player) => (
            <div
              key={player.id}
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                position: "relative",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
              }}
            >
              {/* Remove Button */}
              <button
                onClick={() => onRemovePlayer(player.id)}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "#fee2e2",
                  border: "none",
                  color: "#991b1b",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#991b1b";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fee2e2";
                  e.currentTarget.style.color = "#991b1b";
                }}
              >
                ×
              </button>

              {/* Player Images */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                {player.image && (
                  <img
                    src={player.image}
                    alt={player.name}
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid #667eea",
                    }}
                  />
                )}
                {(player.nfl_team_image || player.team_image) && (
                  <img
                    src={player.nfl_team_image || player.team_image}
                    alt={`${player.nfl_team || player.team} logo`}
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </div>

              {/* Player Name */}
              <h3
                style={{
                  fontSize: "1.3rem",
                  fontWeight: "800",
                  textAlign: "center",
                  marginBottom: "8px",
                  color: "#1a202c",
                }}
              >
                {player.first_name} {player.last_name}
              </h3>

              {/* Position & Draft Capital */}
              <div
                style={{
                  textAlign: "center",
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "#4a5568",
                  marginBottom: "16px",
                }}
              >
                {player?.metrics?.position} | {player?.draft_pick_capital || "—"}
              </div>

              {/* Physical Stats */}
              <div
                style={{
                  background: "#f7fafc",
                  borderRadius: "12px",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontSize: "0.85rem", fontWeight: "700", marginBottom: "8px", color: "#667eea" }}>
                  📏 PHYSICAL
                </div>
                <div style={{ fontSize: "0.9rem", color: "#1a202c" }}>
                  <div><strong>Height:</strong> {player?.height_str || "—"}</div>
                  <div><strong>Weight:</strong> {player?.metrics?.weight ? `${player.metrics.weight} lbs` : "—"}</div>
                  <div><strong>{player?.nfl_team ? "NFL Team" : "School"}:</strong> {player?.nfl_team || player?.metrics?.college_team || "—"}</div>
                </div>
              </div>

              {/* AMP Scores - 2026 for all, 2027/2028 for logged-in users, 2029 never */}
              {(player?.nfl_draft_year === 2026 || ((player?.nfl_draft_year === 2027 || player?.nfl_draft_year === 2028) && isLoggedIn)) && (
                <div
                  style={{
                    background: "#f7fafc",
                    borderRadius: "12px",
                    padding: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ fontSize: "0.85rem", fontWeight: "700", marginBottom: "8px", color: "#667eea" }}>
                    ⚡ AMP SCORES
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {["amp_analytics_score", "amp_momentum_score", "amp_production_score"].map((scoreKey, idx) => {
                      const labels = ["Adjusted Competition", "Momentum", "Production"];
                      const score = player?.[scoreKey] || 0;
                      const colors = getScoreColor(score);
                      const isBest = score === getBestInCategory(scoreKey) && score > 0;

                      return (
                        <div
                          key={scoreKey}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>{labels[idx]}</span>
                          <div
                            style={{
                              background: colors.bg,
                              color: colors.text,
                              padding: "4px 12px",
                              borderRadius: "8px",
                              fontWeight: "700",
                              fontSize: "0.9rem",
                              border: isBest ? "2px solid #FFD700" : "2px solid transparent",
                              boxShadow: isBest ? "0 0 8px rgba(255, 215, 0, 0.5)" : "none",
                            }}
                          >
                            {score} {isBest && "👑"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Consensus Tier */}
              {player?.consensus_tier && (
                <div
                  style={{
                    background: "#f7fafc",
                    borderRadius: "12px",
                    padding: "12px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "0.85rem", fontWeight: "700", marginBottom: "4px", color: "#667eea" }}>
                    📊 CONSENSUS
                  </div>
                  <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#1a202c" }}>
                    {player.consensus_tier}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [filter, setFilter] = useState("All");
  const [teamFilter, setTeamFilter] = useState("All");
  const [p4Filter, setP4Filter] = useState(false);
  const [rankingMode, setRankingMode] = useState('standard'); // 'standard' | 'cff'
  const [players, setPlayers] = useState([]);
  const [message, setMessage] = useState("");
  const [yearFilter, setYearFilter] = useState(2026);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedPlayer, setHighlightedPlayer] = useState(null);
  const [isPaid, setIsPaid] = useState(process.env.NODE_ENV === 'development' ? 1 : 0); // 0 = not paid/not logged in, 1 = paid
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [cognitoSub, setCognitoSub] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showQuickLegend, setShowQuickLegend] = useState(false);
  const [showQuickHits, setShowQuickHits] = useState(false); // Default to closed
  const [quickHits, setQuickHits] = useState([]);
  const [quickHitsLoading, setQuickHitsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isBasicMode, setIsBasicMode] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobileView = windowWidth < 768;
  const [mobileVisibleRounds, setMobileVisibleRounds] = useState(4);
  const [viewMode, setViewMode] = useState('rankings'); // 'rankings', 'pick-value', or 'mock-draft'
  const [scoringFormat, setScoringFormat] = useState('SF'); // 'SF' = superflex, '1QB' = 1QB
  const [tePremium, setTePremium] = useState(false); // TE Premium boost
  const [hideAmpScores, setHideAmpScores] = useState(false); // Hide A/M/P pills on rankings cards
  const [selectedPickRange, setSelectedPickRange] = useState(null);
  const [sleeperUsername, setSleeperUsername] = useState('');
  const [sleeperUserId, setSleeperUserId] = useState(null);
  const [sleeperLeagues, setSleeperLeagues] = useState(null);
  const [sleeperLeagueId, setSleeperLeagueId] = useState('');
  const [sleeperLeagueInfo, setSleeperLeagueInfo] = useState(null);
  const [sleeperSelectedOwner, setSleeperSelectedOwner] = useState(null);
  const [sleeperDraftBoard, setSleeperDraftBoard] = useState(null);
  const FREE_SYNCED_MOCKS = 10; // unpaid users get this many Sleeper-synced mocks, then it's paywalled
  const [syncedMockCount, setSyncedMockCount] = useState(0);
  const [sleeperLoading, setSleeperLoading] = useState(false);
  const [sleeperError, setSleeperError] = useState(null);
  const [mockDraftActive, setMockDraftActive] = useState(false);
  const [mockDraftPaused, setMockDraftPaused] = useState(false);
  const [mockDraftResults, setMockDraftResults] = useState({});
  const [mockDraftCurrentPick, setMockDraftCurrentPick] = useState(-1);
  const [mockDraftComplete, setMockDraftComplete] = useState(false);
  const [mockDraftReverseRound3, setMockDraftReverseRound3] = useState(false);
  const [creatorMockMode, setCreatorMockMode] = useState(false); // paid: every pick is made manually, for draft videos
  const [summaryTeamOwnerId, setSummaryTeamOwnerId] = useState(null);
  const [mockDraftSearch, setMockDraftSearch] = useState('');
  const [mockDraftPositionFilter, setMockDraftPositionFilter] = useState('All');
  const [draftStrategyCollapsed, setDraftStrategyCollapsed] = useState(window.innerWidth < 768);
  const [showDraftReadMe, setShowDraftReadMe] = useState(false);
  const [strategyExpandedPick, setStrategyExpandedPick] = useState(null);
  const [draftStrategyOwnerId, setDraftStrategyOwnerId] = useState(null);
  const [draftValueBased, setDraftValueBased] = useState(true);
  const [mockDraftSpeed, setMockDraftSpeed] = useState(750);
  const [mockDraftYear, setMockDraftYear] = useState(2026);
  const [mockDraftSubMode, setMockDraftSubMode] = useState('live'); // 'live' | 'projections'
  const [sleeperTradedPicks, setSleeperTradedPicks] = useState(null);
  const [sleeperBaseDraftBoard, setSleeperBaseDraftBoard] = useState(null);
  const [sleeperTeamNeeds, setSleeperTeamNeeds] = useState(null);
  const [draftMode, setDraftMode] = useState(null); // null | 'sleeper' | 'guest'
  const [guestNumTeams, setGuestNumTeams] = useState(12);
  const [guestNumRounds, setGuestNumRounds] = useState(3);
  const [guestDraftPosition, setGuestDraftPosition] = useState(1);
  const [guestDraftType, setGuestDraftType] = useState('snake');
  const [guestDraftSubMode, setGuestDraftSubMode] = useState(null); // null | 'regular' | 'dynasty' | 'cff'
  const [isCffDraft, setIsCffDraft] = useState(false);
  const [pickValueOpen, setPickValueOpen] = useState({});
  const [pickValueYear, setPickValueYear] = useState(2027);
  const [showTeamNeeds, setShowTeamNeeds] = useState(true);
  const [sleeperRosters, setSleeperRosters] = useState(null);
  const [sleeperPlayerNames, setSleeperPlayerNames] = useState(null);
  const [playerValues, setPlayerValues] = useState(null);
  const [rosterModalOwnerId, setRosterModalOwnerId] = useState(null);
  const [showRosterPanel, setShowRosterPanel] = useState(true);
  const [overridingPickIndex, setOverridingPickIndex] = useState(null);
  const [reassigningPickIndex, setReassigningPickIndex] = useState(null);
  const [mobileLandscapeDismissed, setMobileLandscapeDismissed] = useState(false);
  const [showMockPaywall, setShowMockPaywall] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null); // { feature, title, message, requiresLogin }
  const [showDraftLimitModal, setShowDraftLimitModal] = useState(false);
  const [showDraftLimitWarning, setShowDraftLimitWarning] = useState(false);
  const MOCK_DRAFT_LIMIT = 100;
  const mockDraftCount = isPaid === 1 ? 0 : parseInt(localStorage.getItem('ampMockDraftCount') || '0', 10);
  const mockDraftLimitReached = isPaid !== 1 && mockDraftCount >= MOCK_DRAFT_LIMIT;
  const [showChangeTeamDropdown, setShowChangeTeamDropdown] = useState(false);
  const [showChangeLeagueDropdown, setShowChangeLeagueDropdown] = useState(false);
  const [overrideSearch, setOverrideSearch] = useState('');
  const [useSleeperAdp, setUseSleeperAdp] = useState(false);
  const [tierBuilderData, setTierBuilderData] = useState(null);
  const [tierBuilderSearch, setTierBuilderSearch] = useState('');
  const [tierBuilderAddTier, setTierBuilderAddTier] = useState('F');
  const [tierBuilderPosFilter, setTierBuilderPosFilter] = useState('All');
  const [tierBuilderSaveStatus, setTierBuilderSaveStatus] = useState(null);
  const tierBuilderSaveTimer = useRef(null);
  const tierBuilderDataRef = useRef(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioAvailablePlayers, setAudioAvailablePlayers] = useState(null);
  const audioRef = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Keep tier builder data ref in sync for debounced save
  tierBuilderDataRef.current = tierBuilderData;

  // Close roster modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') { setRosterModalOwnerId(null); setOverridingPickIndex(null); setOverrideSearch(''); setReassigningPickIndex(null); setPresentationMode(false); } };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Countdown to NFL Draft
  useEffect(() => {
    const draftDate = new Date('2026-04-23T20:00:00-04:00'); // April 23, 2026 8PM EST

    const updateCountdown = () => {
      const now = new Date();
      const difference = draftDate - now;

      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Dark Mode Toggle
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  // Memoize formatted timestamp to avoid recalculation on every render
  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdated) return 'Loading...';
    const date = new Date(lastUpdated);
    date.setHours(date.getHours() + 5);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' EST';
  }, [lastUpdated]);

  // Analytics logging function
  const logUserAction = async (eventType, eventData = {}) => {
    // Only log if user is logged in
    if (!cognitoSub) return;

    try {
      const logPayload = {
        cognito_sub: cognitoSub,
        event_type: eventType,
        event_data: eventData,
        timestamp: new Date().toISOString(),
        is_paid: isPaid,
      };

      // Send log to backend (non-blocking, don't await)
      fetch(`${API_BASE}/log-user-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logPayload),
      }).catch(err => {
        // Silently fail - don't interrupt user experience
        console.error('Analytics logging error:', err);
      });
    } catch (error) {
      console.error('Analytics logging error:', error);
    }
  };

  // Styled replacement for alert()-based upgrade/login prompts.
  // Logs a paywall_hit so we can see which gates users actually run into.
  // ctaLabel optionally overrides the default upgrade button text.
  const openUpgradePrompt = (feature, title, message, requiresLogin = false, ctaLabel = null) => {
    logUserAction('paywall_hit', { feature });
    setUpgradePrompt({ feature, title, message, requiresLogin, ctaLabel });
  };

  // Determine API base URL based on environment
  // TEMPORARY: Using production API for testing (uncomment localhost when Flask is running)
  const API_BASE = 'https://fantasyamp.com/api';
  // const API_BASE = process.env.NODE_ENV === 'development'
  //   ? 'http://localhost:5000/api'  // Your Flask dev server
  //   : 'https://fantasyamp.com/api';

  // Determine redirect URI based on environment
  // NOTE: This must EXACTLY match what's configured in Cognito and Flask PROSPECT_REDIRECT_URI
  // TEMPORARY: Using production redirect for testing
  const REDIRECT_URI = 'https://fantasyamp.com/prospects/';
  // const REDIRECT_URI = process.env.NODE_ENV === 'development'
  //   ? 'http://localhost:3000'  // Match Cognito config
  //   : 'https://fantasyamp.com/prospects/';

  // Build Cognito login URL (using /login/continue endpoint)

  const COGNITO_LOGIN_URL =
  `https://us-east-2bt1vny2zw.auth.us-east-2.amazoncognito.com/login` +
  `?client_id=2tp6v26kknh4c0kqono9fpeivd` +
  `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
  `&response_type=code` +
  `&scope=email+openid+phone`;

  // Handle Cognito callback and check user status
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Prevent multiple simultaneous auth checks
      if (window.isAuthenticating) {
        console.log('Auth already in progress, skipping');
        return;
      }
      window.isAuthenticating = true;

      try {
        // Check if returning from Cognito login (code in URL)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

      if (code) {
        try {
          console.log('Attempting token exchange with code:', code);

          // If we're not on /prospects, redirect there with the code (only once)
          if (window.location.pathname !== '/prospects' && !sessionStorage.getItem('redirected')) {
            sessionStorage.setItem('redirected', 'true');
            window.location.href = `/prospects?code=${code}`;
            return;
          }
          sessionStorage.removeItem('redirected');

          // Exchange auth code for tokens using your existing endpoint
          const response = await fetch(`${API_BASE}/exchange-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code: code })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Token exchange failed:', response.status);
            console.error('Error details:', errorText);

            // Clean URL even on error to prevent retry loops
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }

          const tokens = await response.json();
          console.log('Token exchange successful!');

          // Decode ID token to extract cognito_sub
          // ID tokens are JWT with 3 parts: header.payload.signature
          if (tokens.id_token) {
            const tokenParts = tokens.id_token.split('.');
            const payload = JSON.parse(atob(tokenParts[1]));
            const cognitoSubValue = payload.sub;

            // Store cognito_sub
            localStorage.setItem('cognito_sub', cognitoSubValue);
            localStorage.setItem('id_token', tokens.id_token);
            localStorage.setItem('access_token', tokens.access_token);

            setCognitoSub(cognitoSubValue);
            setIsLoggedIn(true);

            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);

            // Fetch user data directly instead of reloading
            try {
              const userUrl = `${API_BASE}/get-user?cognito_sub=${cognitoSubValue}`;
              console.log('Fetching user from:', userUrl);
              const userResponse = await fetch(userUrl);

              if (userResponse.ok) {
                const userData = await userResponse.json();
                console.log('User data received:', userData);

                if (userData.is_paid) {
                  console.log('Setting isPaid to 1 (all rounds unlocked)');
                  setIsPaid(1);
                  setSelectedRound(null);
                  logUserAction('login', { is_paid: 1 });
                } else {
                  console.log('Setting isPaid to 0 (rounds 1 & 2 unlocked)');
                  setIsPaid(0);
                  logUserAction('login', { is_paid: 0 });

                  // Check if we should show upgrade prompt
                  const showUpgradePrompt = localStorage.getItem('showUpgradePrompt');
                  if (showUpgradePrompt === 'true') {
                    localStorage.removeItem('showUpgradePrompt');
                    openUpgradePrompt('post_login_upgrade', 'Upgrade to AMPLive', 'AMPLive unlocks full mock drafts, the Tier Builder, and Creator Mode.');
                  }
                }
              }
            } catch (error) {
              console.error('Error fetching user data after login:', error);
            }
          }
        } catch (error) {
          console.error('Auth callback error:', error);
        }
      }

      // Check if user is already logged in
      const storedCognitoSub = localStorage.getItem('cognito_sub');
      console.log('Stored cognito_sub:', storedCognitoSub);

      if (storedCognitoSub) {
        setCognitoSub(storedCognitoSub);
        setIsLoggedIn(true);
        // Keep Round 1 default for unpaid users, will change to null for paid after fetch
        console.log('User logged in, fetching user data...');

        // Fetch user's is_paid status from Flask API
        try {
          const userUrl = `${API_BASE}/get-user?cognito_sub=${storedCognitoSub}`;
          console.log('Fetching user from:', userUrl);

          const response = await fetch(userUrl);

          if (!response.ok) {
            console.error(`Failed to fetch user: ${response.status}`);
            const errorText = await response.text();
            console.error('Error response:', errorText);
            return;
          }

          const userData = await response.json();
          console.log('User data received:', userData);
          console.log('is_paid value:', userData.is_paid);

          if (userData.is_paid) {
            console.log('Setting isPaid to 1 (all rounds unlocked)');
            setIsPaid(1); // Paid user - unlock all rounds
            setSelectedRound(null); // Default to "All Rounds" for paid users

            // Log login event for paid user
            logUserAction('login', { is_paid: 1 });
          } else {
            console.log('Setting isPaid to 0 (rounds 1 & 2 unlocked)');
            setIsPaid(0); // Unpaid user - unlock rounds 1 & 2
            // Keep selectedRound as "1" for unpaid users

            // Log login event for free user
            logUserAction('login', { is_paid: 0 });

            // Check if we should show upgrade prompt
            const showUpgradePrompt = localStorage.getItem('showUpgradePrompt');
            if (showUpgradePrompt === 'true') {
              localStorage.removeItem('showUpgradePrompt');
              openUpgradePrompt('post_login_upgrade', 'Upgrade to AMPLive', 'AMPLive unlocks full mock drafts, the Tier Builder, and Creator Mode.');
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        console.log('No user logged in');
      }
      } finally {
        window.isAuthenticating = false;
        setAuthChecked(true);
      }
    };

    checkAuthStatus();
  }, [API_BASE]);

  // Force Round 1 for non-logged-in users viewing dynasty or 2026
  useEffect(() => {
    if (authChecked && !isLoggedIn && (yearFilter === 1 || yearFilter === 2026)) {
      setSelectedRound("1");
    }
  }, [authChecked, isLoggedIn, yearFilter]);

  // Stop audio playback on year change or unmount
  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, [yearFilter]);

  // Fetch list of players with available audio
  useEffect(() => {
    fetch(`${API_BASE}/get-audio-list`)
      .then(res => res.json())
      .then(data => {
        if (data.players) {
          setAudioAvailablePlayers(new Set(data.players));
        }
      })
      .catch(() => setAudioAvailablePlayers(new Set()));
  }, [API_BASE]);

  // Comparison handlers
  const togglePlayerSelection = (player) => {
    setSelectedPlayers((prev) => {
      const isSelected = prev.find((p) => p.id === player.id);
      if (isSelected) {
        logUserAction('comparison_remove', {
          player_id: player.id,
          player_name: `${player.first_name} ${player.last_name}`
        });
        return prev.filter((p) => p.id !== player.id);
      } else {
        if (prev.length >= 5) {
          alert("You can compare up to 5 players at a time");
          return prev;
        }
        logUserAction('comparison_add', {
          player_id: player.id,
          player_name: `${player.first_name} ${player.last_name}`,
          total_selected: prev.length + 1
        });
        return [...prev, player];
      }
    });
  };

  const removeFromComparison = (playerId) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.id !== playerId));
  };

  const clearAllSelections = () => {
    setSelectedPlayers([]);
    setShowComparison(false);
  };

const yearOptions = useMemo(() => {
  return [
    ...Array.from(
      new Set(players.map((p) => p.nfl_draft_year).filter(Boolean))
    ).sort((a, b) => {
      if (a === 1) return -1; // Dynasty first
      if (b === 1) return 1;
      if (a === 2026) return -1; // 2026 second
      if (b === 2026) return 1;
      return a - b;
    })
    // "Transfer Portal" // Add Transfer Portal after draft years
  ];
}, [players]);

  // Fetch players
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        console.log('Fetching players from:', `${API_BASE}/get-prospect-rankings`);
        const res = await fetch(`${API_BASE}/get-prospect-rankings`, {
          method: "GET",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const playerData = Array.isArray(data) ? data : data.players ?? [];
        console.log('Players loaded:', playerData.length);
        setPlayers(playerData);

        // Extract update_ts from the first player if available
        if (playerData.length > 0 && playerData[0].update_ts) {
          setLastUpdated(playerData[0].update_ts);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("fetch rankings error:", err);
        console.error("API_BASE:", API_BASE);
        setMessage("Failed to load rankings");
      }
    })();
    return () => controller.abort();
  }, [API_BASE]);

  // Fetch quick hits
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const url = `${API_BASE}/get-quick-hits`;
        console.log('Fetching quick hits from:', url);
        const res = await fetch(url, {
          method: "GET",
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('Quick hits response status:', res.status);

        if (!res.ok) {
          const errorText = await res.text();
          console.error('Quick hits fetch failed:', res.status, errorText);
          throw new Error(`HTTP ${res.status}: ${errorText}`);
        }

        const data = await res.json();
        console.log('Quick hits data received:', data);
        console.log('Quick hits count:', Array.isArray(data) ? data.length : 'not an array');

        if (Array.isArray(data)) {
          setQuickHits(data);
        } else {
          console.error('Quick hits data is not an array:', data);
          setQuickHits([]);
        }

        setQuickHitsLoading(false);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Quick hits fetch error:", err);
        console.error("Error details:", err.message);
        setQuickHits([]);
        setQuickHitsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [API_BASE]);

  // Add position rank (sorted by overall rank within each position AND year)
  const playersWithPositionRank = useMemo(() => {
    // Group players by position AND draft year
    const byPositionAndYear = {};
    players.forEach((p) => {
      const pos = p?.metrics?.position || "UNK";
      const year = p?.nfl_draft_year || "UNK";
      const key = `${pos}-${year}`;
      if (!byPositionAndYear[key]) byPositionAndYear[key] = [];
      byPositionAndYear[key].push(p);
    });

    // Sort each group by overall rank (lower rank = better)
    Object.keys(byPositionAndYear).forEach((key) => {
      byPositionAndYear[key].sort((a, b) => {
        // Players without rank go to the end
        if (!a.rank && !b.rank) return 0;
        if (!a.rank) return 1;
        if (!b.rank) return -1;

        // Lower rank number = higher position
        return a.rank - b.rank;
      });
    });

    // Assign positional ranks
    const rankedPlayers = players.map((p) => {
      const pos = p?.metrics?.position || "UNK";
      const year = p?.nfl_draft_year || "UNK";
      const key = `${pos}-${year}`;
      const rank = byPositionAndYear[key].indexOf(p) + 1;
      return { ...p, positionRank: `${pos}${rank}` };
    });

    return rankedPlayers;
  }, [players]);

  // Re-rank players for format adjustments (1QB: QBs drop 15, TE Premium: TEs rise 2-5)
  const formatAdjustedPlayers = useMemo(() => {
    if (scoringFormat === 'SF' && !tePremium) return playersWithPositionRank;

    // Group by draft year so re-ranking happens within each class
    const byYear = {};
    playersWithPositionRank.forEach(p => {
      const yr = p.nfl_draft_year || 'UNK';
      if (!byYear[yr]) byYear[yr] = [];
      byYear[yr].push(p);
    });

    const result = [];
    Object.keys(byYear).forEach(yr => {
      const yearPlayers = byYear[yr]
        .filter(p => p.rank != null)
        .sort((a, b) => a.rank - b.rank);
      const unranked = byYear[yr].filter(p => p.rank == null);

      const totalInYear = yearPlayers.length;
      // Round 1 = roughly picks 1-12 in a 12-team league; use top 12 as "round 1"
      const round1Cutoff = Math.min(12, totalInYear);

      // Assign adjusted sort scores
      const withScore = yearPlayers.map(p => {
        let adjustment = 0;
        const pos = p.metrics?.position;
        // 1QB: QBs drop 15 spots
        if (scoringFormat === '1QB' && pos === 'QB') adjustment += 15;
        // TE Premium: TEs rise (negative = move up). Round 1 TEs get -2, others get -5
        // Floor: no TE should end up in the top 5
        if (tePremium && pos === 'TE') {
          adjustment -= (p.rank <= round1Cutoff) ? 2 : 5;
          if (p.rank + adjustment < 6) adjustment = 6 - p.rank;
        }
        return { ...p, _sortScore: p.rank + adjustment };
      });

      // Re-sort by adjusted score
      withScore.sort((a, b) => a._sortScore - b._sortScore);

      // Re-assign rank 1, 2, 3... and recalculate draft_pick_capital
      withScore.forEach((p, i) => {
        const newRank = i + 1;
        const round = Math.ceil(newRank / 12);
        const pick = ((newRank - 1) % 12) + 1;
        const newDraftPickCapital = `${round}.${String(pick).padStart(2, '0')}`;
        result.push({ ...p, rank: newRank, draft_pick_capital: newDraftPickCapital, _sortScore: undefined });
      });

      // Also re-compute positionRank
      const posCounts = {};
      withScore.forEach((p, i) => {
        const pos = p.metrics?.position || 'UNK';
        posCounts[pos] = (posCounts[pos] || 0) + 1;
        result[result.length - withScore.length + i].positionRank = `${pos}${posCounts[pos]}`;
      });

      unranked.forEach(p => result.push(p));
    });

    return result;
  }, [playersWithPositionRank, scoringFormat, tePremium]);

  // Compute real-time positional needs for a team, accounting for mock draft picks
  const getEffectiveNeeds = (ownerId) => {
    const baseNeeds = sleeperTeamNeeds?.teams?.[ownerId]?.needs || {};
    const benchmarks = { QB: 3, RB: 6, WR: 7, TE: 2 };
    const baseCounts = sleeperTeamNeeds?.teams?.[ownerId]?.counts || {};

    // Count positions drafted in mock for this team
    const draftedCounts = { QB: 0, RB: 0, WR: 0, TE: 0 };
    const picks = sleeperDraftBoard?.picks || [];
    picks.forEach((pick, idx) => {
      const result = mockDraftResults[idx];
      if (result && pick.current_owner_id === ownerId) {
        const pos = result.player.position || result.player.metrics?.position;
        if (pos && draftedCounts[pos] !== undefined) draftedCounts[pos]++;
      }
    });

    // Recalculate needs with drafted players factored in
    const effective = {};
    for (const [pos, bench] of Object.entries(benchmarks)) {
      const totalCount = (baseCounts[pos] || 0) + (draftedCounts[pos] || 0);
      effective[pos] = Math.max(0, bench - totalCount) / bench;
    }
    return effective;
  };

  // Mock draft auto-advance effect
  useEffect(() => {
    if (!mockDraftActive || mockDraftPaused || mockDraftComplete) return;
    if (!sleeperDraftBoard?.picks) return;

    const picks = sleeperDraftBoard.picks;
    const nextPickIndex = mockDraftCurrentPick + 1;

    if (nextPickIndex >= picks.length) {
      setMockDraftComplete(true);
      setSummaryTeamOwnerId(sleeperSelectedOwner);
      if (isPaid !== 1) {
        const newCount = mockDraftCount + 1;
        localStorage.setItem('ampMockDraftCount', String(newCount));
        if (newCount >= 90 && newCount < MOCK_DRAFT_LIMIT) {
          setShowDraftLimitWarning(true);
        }
      }

      // Log full draft results
      const draftResults = picks.map((pick, idx) => {
        const result = mockDraftResults[idx];
        if (!result) return null;
        return {
          pick_index: idx,
          pick_label: pick.pick_label,
          round: pick.round,
          owner_id: pick.current_owner_id,
          is_user_pick: !!result.isUserPick,
          is_overridden: !!result.isOverridden,
          player_id: result.player.id,
          player_name: `${result.player.first_name} ${result.player.last_name}`,
          position: result.player.metrics?.position || result.player.position || '',
          rank: result.player.rank,
          amp_score: calculateCompositeAmpScore(result.player) || null,
        };
      }).filter(Boolean);

      logUserAction('mock_draft_complete', {
        draft_end_time: new Date().toISOString(),
        draft_year: mockDraftYear,
        league_id: sleeperLeagueId,
        league_name: sleeperLeagueInfo?.league_name || null,
        num_teams: sleeperDraftBoard?.num_teams || null,
        draft_type: sleeperDraftBoard?.draft_type || null,
        selected_owner: sleeperSelectedOwner,
        value_based: draftValueBased,
        creator_mode: creatorMockMode,
        total_picks: draftResults.length,
        user_picks: draftResults.filter(p => p.is_user_pick),
        all_picks: draftResults,
      });
      return;
    }

    const nextPick = picks[nextPickIndex];

    if (nextPick.is_my_pick || creatorMockMode) {
      setMockDraftCurrentPick(nextPickIndex);
      setMockDraftPaused(true);
      return;
    }

    const timer = setTimeout(() => {
      setMockDraftResults(prev => {
        const draftedIds = new Set(Object.values(prev).map(r => r.player.id));
        const available = isCffDraft
          ? formatAdjustedPlayers
              .filter(p => p.cff_rank != null && [2027, 2028, 2029].includes(p.nfl_draft_year) && !draftedIds.has(p.id))
              .sort((a, b) => (a.cff_rank || 9999) - (b.cff_rank || 9999))
          : formatAdjustedPlayers
              .filter(p => p.nfl_draft_year === mockDraftYear && p.rank != null && !draftedIds.has(p.id))
              .sort((a, b) => a.rank - b.rank);

        if (available.length === 0) return prev;

        // Build rank-to-ampRank lookup from players who have playerValues
        const rankToAmpRank = {};
        available.forEach(p => {
          const n = (p.first_name + ' ' + p.last_name).trim();
          const ar = playerValues?.[n]?.amp_rank;
          if (ar != null && p.rank != null) rankToAmpRank[p.rank] = Number(ar);
        });
        const ranksWithValues = Object.keys(rankToAmpRank).map(Number).sort((a, b) => a - b);
        const estimateAmpRank = (rank) => {
          if (ranksWithValues.length === 0) return Math.max(0, 200 - rank);
          // Find nearest rank that has an amp_rank value
          let closest = ranksWithValues[0];
          for (const r of ranksWithValues) { if (Math.abs(r - rank) < Math.abs(closest - rank)) closest = r; }
          return rankToAmpRank[closest];
        };

        // When CFF draft, pick top available by cff_rank with light randomization
        let picked;
        if (isCffDraft) {
          if (nextPickIndex < 5) {
            picked = Math.random() < 0.92 ? available[0] : available[Math.min(1, available.length - 1)];
          } else if (nextPick.round >= 4) {
            const pool = available.slice(0, Math.min(6, available.length));
            picked = pool[Math.floor(Math.random() * pool.length)];
          } else {
            const pool = available.slice(0, 3);
            const roll = Math.random();
            picked = roll < 0.55 ? pool[0] : roll < 0.85 ? pool[Math.min(1, pool.length - 1)] : pool[Math.min(2, pool.length - 1)];
          }
          if (!picked) picked = available[0];
        } else if (useSleeperAdp) {
          // Score purely by Sleeper ADP position; unlisted players get -1
          const sleeperScored = available.map(player => {
            const name = (player.first_name + ' ' + player.last_name).trim();
            const score = sleeperAdpMap.get(normalizePlayerName(name)) ?? -1;
            return { player, score };
          }).sort((a, b) => b.score - a.score);

          // Pick from top few with random variation
          const listed = sleeperScored.filter(s => s.score > 0);
          const pool = listed.length > 0 ? listed : sleeperScored;
          if (nextPickIndex < 5) {
            // Top 5: mostly chalk, small chance of #2
            picked = Math.random() < 0.85 ? pool[0]?.player : pool[Math.min(1, pool.length - 1)]?.player;
          } else if (nextPick.round >= 4) {
            // Round 4+: pick from top 5 available randomly
            const topN = pool.slice(0, Math.min(5, pool.length));
            picked = topN[Math.floor(Math.random() * topN.length)]?.player;
          } else {
            // Rounds 2-3: pick from top 3 weighted toward #1
            const topN = pool.slice(0, Math.min(3, pool.length));
            const roll = Math.random();
            picked = roll < 0.55 ? topN[0]?.player : roll < 0.85 ? topN[Math.min(1, topN.length - 1)]?.player : topN[Math.min(2, topN.length - 1)]?.player;
          }
          if (!picked) picked = pool[0]?.player;
        } else {
          // Standard AMP scoring path
          const needWeight = draftValueBased ? 0 : 1;
          const teamNeeds = getEffectiveNeeds(nextPick.current_owner_id);
          const scored = available.map(player => {
            const name = (player.first_name + ' ' + player.last_name).trim();
            const pv = playerValues?.[name];
            const ampRank = pv?.amp_rank != null ? Number(pv.amp_rank) : null;
            const pos = player.position || player.metrics?.position || '';
            const need = teamNeeds[pos] || 0;
            const baseScore = ampRank != null ? ampRank : estimateAmpRank(player.rank);
            const score = baseScore * (1 + need * needWeight);
            return { player, score };
          }).sort((a, b) => b.score - a.score);

          if (nextPickIndex < 5) {
            picked = Math.random() < 0.95 ? available[0] : scored[0]?.player;
          } else if (nextPick.round >= 4) {
            const pool = scored.slice(0, Math.min(8, scored.length));
            picked = pool[Math.floor(Math.random() * pool.length)]?.player;
          } else {
            const pool = scored.slice(0, 3);
            const roll = Math.random();
            picked = roll < 0.55 ? pool[0]?.player : roll < 0.85 ? pool[1]?.player : pool[2]?.player;
          }
          if (!picked) picked = scored[0]?.player;
        }

        logUserAction('mock_draft_pick', {
          pick_index: nextPickIndex,
          pick_label: nextPick.pick_label,
          round: nextPick.round,
          slot: nextPick.slot,
          is_user_pick: false,
          draft_year: mockDraftYear,
          league_id: sleeperLeagueId,
          owner_id: nextPick.current_owner_id,
          player_name: (picked.first_name + ' ' + picked.last_name).trim(),
          player_id: picked.id,
          position: picked.metrics?.position || picked.position || '',
        });
        return {
          ...prev,
          [nextPickIndex]: { player: picked, isUserPick: false },
        };
      });
      setMockDraftCurrentPick(nextPickIndex);
    }, mockDraftSpeed);

    return () => clearTimeout(timer);
  }, [mockDraftActive, mockDraftPaused, mockDraftComplete, mockDraftCurrentPick, mockDraftSpeed, sleeperDraftBoard, formatAdjustedPlayers, mockDraftYear, sleeperTeamNeeds, playerValues, draftValueBased, useSleeperAdp, creatorMockMode, isCffDraft]);

  // Mock draft: available players (filtered by year, sorted by rank, excluding already drafted)
  const mockDraftAvailablePlayers = useMemo(() => {
    const draftedIds = new Set(Object.values(mockDraftResults).map(r => r.player.id));
    if (isCffDraft) {
      return formatAdjustedPlayers
        .filter(p => p.cff_rank != null && [2027, 2028, 2029].includes(p.nfl_draft_year))
        .sort((a, b) => (a.cff_rank || 9999) - (b.cff_rank || 9999))
        .filter(p => !draftedIds.has(p.id));
    }
    return formatAdjustedPlayers
      .filter(p => p.nfl_draft_year === mockDraftYear && p.rank != null)
      .sort((a, b) => a.rank - b.rank)
      .filter(p => !draftedIds.has(p.id));
  }, [formatAdjustedPlayers, mockDraftYear, mockDraftResults, isCffDraft]);

  // Mock draft: search results for user pick dropdown
  const mockDraftSearchResults = useMemo(() => {
    let filtered = mockDraftAvailablePlayers;
    if (mockDraftPositionFilter !== 'All') {
      filtered = filtered.filter(p => (p.metrics?.position || p.position) === mockDraftPositionFilter);
    }
    if (mockDraftSearch.trim()) {
      const query = mockDraftSearch.toLowerCase();
      filtered = filtered.filter(p => {
        const firstName = p?.first_name?.toLowerCase() || '';
        const lastName = p?.last_name?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`;
        return firstName.includes(query) || lastName.includes(query) || fullName.includes(query);
      });
    }
    let results = filtered.slice(0, 30);
    // 2026 shows exact rankings for everyone; non-2026 shuffles player order for logged-out users (fallback)
    if (!isCffDraft && isPaid !== 1 && mockDraftYear !== 2026 && mockDraftYear !== 1) {
      results = [...results].map((p, i) => ({ p, noise: i + (Math.random() - 0.5) * 10 }))
        .sort((a, b) => a.noise - b.noise)
        .map(x => x.p);
    }
    return results;
  }, [mockDraftAvailablePlayers, mockDraftSearch, mockDraftPositionFilter, isPaid]);

  // Mock draft: top 5 recommendations when it's the user's turn
  const mockDraftRecommendations = useMemo(() => {
    if (!mockDraftPaused || !sleeperSelectedOwner) return [];

    const needs = getEffectiveNeeds(sleeperSelectedOwner);

    if (useSleeperAdp) {
      // Pure Sleeper ADP recommendations — no AMP data
      return mockDraftAvailablePlayers
        .map(player => {
          const name = (player.first_name + ' ' + player.last_name).trim();
          const score = sleeperAdpMap.get(normalizePlayerName(name)) ?? -1;
          if (score <= 0) return null;
          const pos = player.position || player.metrics?.position || '';
          const reason = score >= 80 ? 'Top Sleeper ADP'
                       : score >= 50 ? 'Strong Sleeper ADP'
                       : 'Sleeper ADP value';
          return { player, ampRank: score, score, need: 0, reason, pos };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
    }

    return mockDraftAvailablePlayers
      .map(player => {
        const name = (player.first_name + ' ' + player.last_name).trim();
        const pv = playerValues?.[name];
        const ampRank = pv?.amp_rank != null ? Number(pv.amp_rank) : null;
        const effectiveScore = ampRank != null ? ampRank : (player.rank ? Math.max(0, 101 - player.rank) : null);
        if (effectiveScore == null) return null;

        const pos = player.position || player.metrics?.position || '';
        const need = needs[pos] || 0;
        const needMult = draftValueBased ? 0 : 0.5;
        const score = effectiveScore * (1 + need * needMult);
        const reason = effectiveScore >= 80 ? 'Elite talent (BPA)'
                     : effectiveScore >= 60 ? 'Strong value'
                     : !draftValueBased && need >= 0.7 ? 'Fills major need'
                     : !draftValueBased && need >= 0.4 ? 'Addresses roster gap'
                     : 'Best available';

        return { player, ampRank: effectiveScore, score, need, reason, pos };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [mockDraftPaused, mockDraftAvailablePlayers, playerValues,
      sleeperSelectedOwner, mockDraftResults, sleeperTeamNeeds, sleeperDraftBoard, useSleeperAdp]);

  // Personalized Draft Plan — paid feature
  const personalizedDraftPlan = useMemo(() => {
    // Doesn't apply in Creator Mode: every pick is "the user's pick", so there's no single team to plan ahead for
    if (creatorMockMode) return null;
    if (!mockDraftActive || !sleeperDraftBoard?.picks || !sleeperSelectedOwner || !playerValues) return null;

    const picks = sleeperDraftBoard.picks;
    const posColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
    const benchmarks = { QB: 3, RB: 6, WR: 7, TE: 2 };
    const baseCounts = sleeperTeamNeeds?.teams?.[sleeperSelectedOwner]?.counts || {};

    // Find remaining user picks (current + future)
    const remainingUserPicks = picks
      .map((pick, idx) => ({ ...pick, idx }))
      .filter(p => p.is_my_pick && p.idx >= (mockDraftCurrentPick >= 0 ? mockDraftCurrentPick : 0) && !mockDraftResults[p.idx]);

    if (remainingUserPicks.length === 0) return null;

    // Current available players (already drafted removed)
    const draftedIds = new Set(Object.values(mockDraftResults).map(r => r.player.id));
    const allRanked = formatAdjustedPlayers
      .filter(p => p.nfl_draft_year === mockDraftYear && p.rank != null)
      .sort((a, b) => a.rank - b.rank);
    const currentAvailable = allRanked.filter(p => !draftedIds.has(p.id));

    // Compute league-relative base star ratings from existing rosters
    const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };
    const posStars = {};
    if (sleeperLeagueInfo?.teams) {
      const posScores = {};
      sleeperLeagueInfo.teams.forEach(tm => {
        const oid = tm.owner_id;
        const pids = sleeperRosters?.[oid] || [];
        const pls = pids.map(id => sleeperPlayerNames?.[id]).filter(Boolean);
        Object.entries(topNC).forEach(([pos, topN]) => {
          const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name?.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
          if (rks.length > 0) {
            if (!posScores[pos]) posScores[pos] = {};
            posScores[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
          }
        });
      });
      const baseStars = {};
      Object.entries(posScores).forEach(([pos, scores]) => {
        const vals = Object.values(scores);
        const mn = Math.min(...vals);
        const mx = Math.max(...vals);
        const rng = mx - mn;
        const sc = scores[sleeperSelectedOwner];
        if (sc != null) baseStars[pos] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
      });
      // Add round-based star bumps from mock-drafted players for user's team
      // 1st round = +1 star, 2nd round = +0.5 star, 3rd+ = +0.25 star
      const draftBumps = { QB: 0, RB: 0, WR: 0, TE: 0 };
      picks.forEach((pick, idx) => {
        const result = mockDraftResults[idx];
        if (result && pick.current_owner_id === sleeperSelectedOwner) {
          const pos = result.player.position || result.player.metrics?.position || '';
          if (pos && draftBumps[pos] !== undefined) {
            const round = pick.round || Math.floor(idx / (sleeperDraftBoard.num_teams || 12)) + 1;
            draftBumps[pos] += round === 1 ? 1 : round === 2 ? 0.5 : 0.25;
          }
        }
      });
      ['QB', 'RB', 'WR', 'TE'].forEach(pos => {
        const base = baseStars[pos] ?? 3;
        posStars[pos] = Math.min(5, Math.round((base + draftBumps[pos]) * 2) / 2);
      });
    }

    // Count positions already drafted by user's team in this mock
    const userDraftedCounts = { QB: 0, RB: 0, WR: 0, TE: 0 };
    picks.forEach((pick, idx) => {
      const result = mockDraftResults[idx];
      if (result && pick.current_owner_id === sleeperSelectedOwner) {
        const pos = result.player.position || result.player.metrics?.position;
        if (pos && userDraftedCounts[pos] !== undefined) userDraftedCounts[pos]++;
      }
    });

    // Simulate picks forward to estimate availability at each user pick
    const simulatedTakenIds = new Set(draftedIds);
    const simulatedDraftedCounts = { ...userDraftedCounts };
    const planPicks = [];

    remainingUserPicks.forEach((userPick, upIdx) => {
      const isCurrentPick = userPick.idx === mockDraftCurrentPick && mockDraftPaused;

      // For future picks: simulate AI picks between last known pick and this one
      if (!isCurrentPick) {
        const prevIdx = upIdx === 0 ? (mockDraftCurrentPick >= 0 ? mockDraftCurrentPick : -1) : (planPicks.length > 0 ? planPicks[planPicks.length - 1].pickIndex : -1);
        for (let i = prevIdx + 1; i < userPick.idx; i++) {
          if (mockDraftResults[i]) continue;
          const simPick = picks[i];
          if (simPick?.is_my_pick) continue;
          const simAvail = allRanked.filter(p => !simulatedTakenIds.has(p.id));
          if (simAvail.length > 0) {
            simulatedTakenIds.add(simAvail[0].id);
          }
        }
      }

      // Get available pool for this pick
      const availablePool = isCurrentPick
        ? currentAvailable
        : allRanked.filter(p => !simulatedTakenIds.has(p.id));

      // Compute needs with cumulative simulation (raw count-based)
      const needs = {};
      for (const [pos, bench] of Object.entries(benchmarks)) {
        const totalCount = (baseCounts[pos] || 0) + (simulatedDraftedCounts[pos] || 0);
        needs[pos] = Math.max(0, bench - totalCount) / bench;
      }

      // Combine count-based need with league-relative star weakness
      // Stars 0-1 = severe gap, 1.5-2 = moderate gap, 3+ = fine, 4+ = strength
      // starNeed: 0-1 scale where lower stars = higher need
      const combinedNeed = {};
      const posOrder = ['QB', 'RB', 'WR', 'TE'];
      posOrder.forEach(pos => {
        const countNeed = needs[pos] || 0;
        const stars = posStars[pos];
        const starNeed = stars != null ? Math.max(0, (5 - stars) / 5) : 0.5;
        // Weighted blend: 40% roster count gap, 60% league-relative weakness
        combinedNeed[pos] = countNeed * 0.4 + starNeed * 0.6;
      });

      // Urgency factor: fewer remaining picks = more weight on needs
      const picksLeft = remainingUserPicks.length - upIdx;
      // With 1 pick left, urgency is max. With 4+ picks, urgency is baseline.
      const urgencyMultiplier = picksLeft <= 1 ? 2.5 : picksLeft <= 2 ? 1.8 : picksLeft <= 3 ? 1.3 : 1.0;

      // Score available players
      let scored;
      if (useSleeperAdp) {
        // Pure Sleeper ADP scoring — no AMP data
        scored = availablePool.map(player => {
          const name = (player.first_name + ' ' + player.last_name).trim();
          const sleeperScore = sleeperAdpMap.get(normalizePlayerName(name)) ?? -1;
          if (sleeperScore <= 0) return null;
          const pos = player.position || player.metrics?.position || '';
          const reason = sleeperScore >= 80 ? 'Top Sleeper ADP'
                       : sleeperScore >= 50 ? 'Strong Sleeper ADP'
                       : 'Sleeper ADP value';
          return { player, position: pos, ampRank: sleeperScore, score: sleeperScore, need: 0, reason, stars: null };
        }).filter(Boolean).sort((a, b) => b.score - a.score);
      } else {
        // Find the best available player at each position to assess value
        const bestByPos = {};
        availablePool.forEach(player => {
          const pos = player.position || player.metrics?.position || '';
          if (!pos || !posOrder.includes(pos)) return;
          const name = (player.first_name + ' ' + player.last_name).trim();
          const ampRank = playerValues[name]?.amp_rank != null ? Number(playerValues[name].amp_rank) : null;
          if (ampRank != null && (!bestByPos[pos] || ampRank > bestByPos[pos])) {
            bestByPos[pos] = ampRank;
          }
        });

        scored = availablePool.map(player => {
          const name = (player.first_name + ' ' + player.last_name).trim();
          const pv = playerValues[name];
          const ampRank = pv?.amp_rank != null ? Number(pv.amp_rank) : null;
          const effectiveScore = ampRank != null ? ampRank : (player.rank ? Math.max(0, 101 - player.rank) : null);
          if (effectiveScore == null) return null;
          const pos = player.position || player.metrics?.position || '';
          const need = combinedNeed[pos] || 0;

          const needBoost = draftValueBased ? 0 : need * urgencyMultiplier * 1.5;
          const score = effectiveScore * (1 + needBoost);

          const stars = posStars[pos];
          let reason;
          if (draftValueBased) {
            reason = effectiveScore >= 85 ? 'Elite talent (BPA)'
                   : effectiveScore >= 70 ? 'Strong value'
                   : effectiveScore >= 55 ? 'Solid pick'
                   : 'Best available';
          } else {
            if (need >= 0.6) {
              reason = stars != null && stars <= 1.5 ? `Fills ${pos} gap (${stars}★)` : 'Fills major need';
            } else if (need >= 0.35) {
              reason = 'Addresses roster gap';
            } else if (effectiveScore >= 80) {
              reason = 'Elite talent (BPA)';
            } else {
              reason = 'Best available';
            }
          }

          return { player, position: pos, ampRank: effectiveScore, score, need, reason, stars: stars || null };
        }).filter(Boolean).sort((a, b) => b.score - a.score);
      }

      const recommendation = scored[0] || null;
      // Ensure alternatives include different positions when possible
      const altPositions = new Set();
      const alternatives = [];
      for (const s of scored.slice(1)) {
        if (alternatives.length >= 2) break;
        if (s.position !== recommendation?.position && !altPositions.has(s.position)) {
          alternatives.push(s);
          altPositions.add(s.position);
        }
      }
      // Fill remaining alt slots if we don't have 2 different-position alternatives
      if (alternatives.length < 2) {
        for (const s of scored.slice(1)) {
          if (alternatives.length >= 2) break;
          if (!alternatives.includes(s)) alternatives.push(s);
        }
      }

      // Determine position target
      const positionTarget = recommendation?.position || 'BPA';

      // Build plan text with context
      let planText = '';
      if (recommendation) {
        const playerName = (recommendation.player.first_name + ' ' + recommendation.player.last_name).trim();
        const stars = posStars[recommendation.position];
        const starsStr = stars != null ? ` (${stars}★ in league)` : '';
        if (draftValueBased) {
          planText = `BPA — ${playerName} is the highest-rated talent ${isCurrentPick ? 'available' : 'projected available'}. AMP rank: ${recommendation.ampRank}.`;
        } else if (recommendation.need >= 0.6) {
          planText = `Target ${recommendation.position}${starsStr} — your biggest gap. ${playerName} ${isCurrentPick ? 'is available' : 'projects to be available'}.`;
        } else if (recommendation.need >= 0.35) {
          planText = `Address ${recommendation.position} need${starsStr}. ${playerName} is strong value here.`;
        } else if (picksLeft > 1 && recommendation.need < 0.2) {
          planText = `BPA — your roster needs are manageable. ${playerName} is the best talent on the board.`;
        } else {
          planText = `BPA — no critical needs. ${playerName} is the best talent on the board.`;
        }
      }

      planPicks.push({
        pickLabel: userPick.pick_label,
        pickIndex: userPick.idx,
        round: userPick.round,
        isCurrentPick,
        positionTarget,
        recommendation,
        alternatives,
        planText,
        needs: { ...combinedNeed },
        picksLeft,
      });

      // Simulate this user pick for cumulative need calculation
      if (recommendation) {
        simulatedTakenIds.add(recommendation.player.id);
        if (recommendation.position && simulatedDraftedCounts[recommendation.position] !== undefined) {
          simulatedDraftedCounts[recommendation.position]++;
        }
        // Bump stars by round: 1st = +1, 2nd = +0.5, 3rd+ = +0.25
        if (posStars[recommendation.position] != null) {
          const simRound = userPick.round || Math.floor(userPick.idx / (sleeperDraftBoard.num_teams || 12)) + 1;
          const bump = simRound === 1 ? 1 : simRound === 2 ? 0.5 : 0.25;
          posStars[recommendation.position] = Math.min(5, Math.round((posStars[recommendation.position] + bump) * 2) / 2);
        }
      }
    });

    // Build summary
    const posTargets = planPicks.map(p => p.positionTarget).filter(Boolean);
    const keyNames = planPicks.slice(0, 2).map(p => {
      if (!p.recommendation) return null;
      return p.recommendation.player.last_name;
    }).filter(Boolean);
    const summary = posTargets.length > 0
      ? `Your plan: ${posTargets.join(' → ')}${keyNames.length > 0 ? `. Key targets: ${keyNames.join(', ')}.` : ''}`
      : '';

    return { picks: planPicks, summary, posColors };
  }, [mockDraftActive, mockDraftPaused, mockDraftCurrentPick, mockDraftResults,
      sleeperDraftBoard, sleeperSelectedOwner, playerValues, sleeperTeamNeeds,
      sleeperLeagueInfo, sleeperRosters, sleeperPlayerNames, creatorMockMode,
      formatAdjustedPlayers, mockDraftYear, useSleeperAdp]);

  // Override modal: available players for the overridden pick slot
  const overrideAvailablePlayers = useMemo(() => {
    if (overridingPickIndex == null) return [];
    // Players taken at every OTHER pick index (exclude the one being overridden)
    const takenIds = new Set(
      Object.entries(mockDraftResults)
        .filter(([idx]) => Number(idx) !== overridingPickIndex)
        .map(([, r]) => r.player.id)
    );
    const query = overrideSearch.toLowerCase().trim();
    return formatAdjustedPlayers
      .filter(p => p.nfl_draft_year === mockDraftYear && p.rank != null)
      .sort((a, b) => a.rank - b.rank)
      .filter(p => !takenIds.has(p.id))
      .filter(p => {
        if (!query) return true;
        const firstName = p?.first_name?.toLowerCase() || '';
        const lastName = p?.last_name?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`;
        return firstName.includes(query) || lastName.includes(query) || fullName.includes(query);
      })
      .slice(0, 20);
  }, [overridingPickIndex, mockDraftResults, formatAdjustedPlayers, mockDraftYear, overrideSearch]);

  // Search results for autocomplete
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return formatAdjustedPlayers
      .filter((p) => {
        // Check if name matches
        const firstName = p?.first_name?.toLowerCase() || "";
        const lastName = p?.last_name?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`;
        const nameMatches = firstName.includes(query) ||
                           lastName.includes(query) ||
                           fullName.includes(query);

        if (!nameMatches) return false;

        // Rankings are free for all draft classes. Check round access.
        if (!p.draft_pick_capital) return true; // Show players without draft capital

        const [roundStr] = p.draft_pick_capital.split(".");
        const round = parseInt(roundStr, 10);

        // Round access: logged-out see round 1; logged-in free see rounds 1-2; paid see all.
        return (isPaid === 1 || window.location.hostname === 'localhost') ? true : (isLoggedIn ? round <= 2 : round === 1);
      })
      .slice(0, 10); // Limit to 10 results
  }, [formatAdjustedPlayers, searchQuery, isLoggedIn, isPaid]);

  const handleDownloadExcel = () => {
    const rows = [];
    let rank = 1;
    Object.entries(playersByRound)
      .filter(([round]) => Number(round) <= 12)
      .filter(([round]) => {
        // TEMP: disabled for dev banner testing
        // if (!isLoggedIn && yearFilter === 2026 && Number(round) !== 1) return false;
        return true;
      })
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([round, subgroups]) => {
        if (selectedRound) {
          if (selectedRound.includes("-")) {
            const [start, end] = selectedRound.split("-").map(Number);
            const roundNum = Number(round);
            if (roundNum < start || roundNum > end) return;
          } else {
            if (String(selectedRound) !== String(round)) return;
          }
        }
        ['Early', 'Mid', 'Late'].forEach((sub) => {
          const players = subgroups[sub] || [];
          players.forEach((p) => {
            rows.push({
              Rank: rank++,
              Name: `${p.first_name} ${p.last_name}`,
              Position: p.metrics?.position || p.position || '',
              Team: p.nfl_team || p.metrics?.college_team || '',
              Round: Number(round),
              'Sub-round': sub,
              'Draft Capital': p.draft_pick_capital || '',
            });
          });
        });
      });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rankings');
    XLSX.writeFile(wb, `FantasyAMP_Rankings_${yearFilter}.xlsx`);
  };

  // Handle player selection from dropdown
  const handlePlayerSelect = (player) => {
    setSearchQuery("");
    setShowDropdown(false);

    // Auto-switch to the player's draft year
    if (player.nfl_draft_year) {
      setYearFilter(player.nfl_draft_year);
    }

    // Highlight the player temporarily
    setHighlightedPlayer(player.id);
    setTimeout(() => setHighlightedPlayer(null), 3000);

    // Scroll to player card
    setTimeout(() => {
      const element = document.getElementById(`player-${player.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Pre-draft strategy suggestion
  const draftStrategy = useMemo(() => {
    const targetOwnerId = draftStrategyOwnerId || sleeperSelectedOwner;
    if (!targetOwnerId || !sleeperDraftBoard?.picks || !playerValues || !sleeperRosters || !sleeperPlayerNames || !sleeperLeagueInfo?.teams) return null;

    const isMyTeam = targetOwnerId === sleeperSelectedOwner;
    const teamObj = sleeperLeagueInfo.teams.find(t => t.owner_id === targetOwnerId);
    const teamName = teamObj?.display_name || 'This team';

    const posClrs = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
    const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };

    // Calculate star ratings for the target owner (same logic as column headers)
    const posScores = {};
    sleeperLeagueInfo.teams.forEach(tm => {
      const oid = tm.owner_id;
      const pids = sleeperRosters[oid] || [];
      const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
      Object.entries(topNC).forEach(([pos, topN]) => {
        const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
        if (rks.length > 0) {
          if (!posScores[pos]) posScores[pos] = {};
          posScores[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
        }
      });
    });
    const stars = {};
    Object.entries(posScores).forEach(([pos, scores]) => {
      const vals = Object.values(scores);
      const mn = Math.min(...vals);
      const mx = Math.max(...vals);
      const rng = mx - mn;
      const sc = scores[targetOwnerId];
      if (sc != null) stars[pos] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
    });

    // Identify weaknesses: positions with <= 2 stars
    const posOrder = ['QB', 'RB', 'WR', 'TE'];
    const weaknesses = posOrder.filter(pos => stars[pos] != null && stars[pos] <= 2).sort((a, b) => (stars[a] || 0) - (stars[b] || 0));
    const strengths = posOrder.filter(pos => stars[pos] != null && stars[pos] >= 4);

    // Get this team's pick positions
    const teamPicks = sleeperDraftBoard.picks
      .map((pick, idx) => ({ ...pick, idx }))
      .filter(p => p.current_owner_id === targetOwnerId);

    // Get ranked players for this draft year
    const ranked = formatAdjustedPlayers
      .filter(p => p.nfl_draft_year === mockDraftYear && p.rank != null)
      .sort((a, b) => a.rank - b.rank);

    // For each of the team's picks, project who might be available (by rank range)
    const pickTargets = teamPicks.map(pick => {
      const overallPick = pick.idx + 1;
      const projected = ranked.slice(Math.max(0, overallPick - 3), overallPick + 4);
      const byPos = {};
      projected.forEach(p => {
        const pos = p.position || p.metrics?.position || '';
        if (!byPos[pos]) byPos[pos] = [];
        byPos[pos].push(p);
      });
      // Top candidates likely available around this pick (ranked from a few spots before to a few after)
      const candidates = ranked.slice(Math.max(0, overallPick - 4), overallPick + 6).slice(0, 6);
      return { pickLabel: pick.pick_label, round: pick.round, overallPick, projected, byPos, candidates, idx: pick.idx };
    });

    // Build strategy text — tone depends on slider + whether it's the user's team
    const lines = [];
    const isJuggernaut = weaknesses.length === 0;
    const leanNeed = !draftValueBased;
    const leanValue = draftValueBased;

    if (leanValue || isJuggernaut) {
      // Value-leaning or juggernaut — BPA focus
      const lowestPos = posOrder.filter(pos => stars[pos] != null).sort((a, b) => (stars[a] || 0) - (stars[b] || 0))[0];
      if (isMyTeam) {
        if (!isJuggernaut && weaknesses.length > 0) {
          lines.push({ type: 'need', text: `With a value-first approach, draft the best player available regardless of position. Your ${weaknesses.join(' and ')} needs are secondary — talent over fit.` });
        } else if (lowestPos && stars[lowestPos] < 4) {
          lines.push({ type: 'need', text: `Your roster is stacked across the board. ${lowestPos} is your relatively weakest group — if a top ${lowestPos} falls to you, it's worth considering. Otherwise, take the best player available and stay chalk.` });
        } else {
          lines.push({ type: 'need', text: `Your roster has no glaring holes. Draft best player available and take the chalk value — you're in a position of strength.` });
        }
      } else {
        if (!isJuggernaut && weaknesses.length > 0) {
          lines.push({ type: 'need', text: `With a value-first approach, ${teamName} would draft BPA regardless of their ${weaknesses.join(' and ')} weakness. Talent over fit.` });
        } else if (lowestPos && stars[lowestPos] < 4) {
          lines.push({ type: 'need', text: `${teamName} has a well-rounded roster with no major weaknesses. ${lowestPos} is their relatively weakest spot, but expect them to largely draft chalk and take the best player available.` });
        } else {
          lines.push({ type: 'need', text: `${teamName} is a juggernaut with no real holes. Expect them to take pure value — best player available regardless of position.` });
        }
      }

      // Show top BPA targets at each pick
      pickTargets.forEach(pt => {
        const topTwo = pt.candidates.slice(0, 2);
        if (topTwo.length > 0) {
          const names = topTwo.map(p => {
            const pos = p.position || p.metrics?.position || '';
            return `${p.first_name} ${p.last_name} (${pos})`;
          }).join(' or ');
          if (isMyTeam) {
            lines.push({ type: 'target', pos: topTwo[0].position || topTwo[0].metrics?.position || '', text: `At ${pt.pickLabel}, the best available could be ${names}.`, player: topTwo[0] });
          } else {
            lines.push({ type: 'target', pos: topTwo[0].position || topTwo[0].metrics?.position || '', text: `At ${pt.pickLabel}, expect ${teamName} to target ${names}.`, player: topTwo[0] });
          }
        }
      });
    } else if (leanNeed && !isJuggernaut) {
      // Need-leaning — address weaknesses aggressively
      const weakStr = weaknesses.join(' and ');
      if (isMyTeam) {
        lines.push({ type: 'need', text: `With a need-first approach, prioritize ${weakStr} aggressively — even if it means passing on higher-ranked players at other positions. Fill your holes before they become liabilities.` });
      } else {
        lines.push({ type: 'need', text: `With a need-first approach, expect ${teamName} to aggressively target ${weakStr} — they'll likely reach for positional fits over pure value.` });
      }

      weaknesses.forEach(pos => {
        const firstTarget = pickTargets.find(pt => pt.byPos[pos]?.length > 0);
        if (firstTarget) {
          const topPlayer = firstTarget.byPos[pos][0];
          if (isMyTeam) {
            lines.push({ type: 'target', pos, text: `${topPlayer.first_name} ${topPlayer.last_name} (${pos}) is projected near your ${firstTarget.pickLabel} pick — a strong need-based target.`, player: topPlayer });
          } else {
            lines.push({ type: 'target', pos, text: `${topPlayer.first_name} ${topPlayer.last_name} (${pos}) could be in play at ${firstTarget.pickLabel}.`, player: topPlayer });
          }
        }
      });

      if (strengths.length > 0) {
        if (isMyTeam) {
          lines.push({ type: 'strength', text: `Avoid doubling down at ${strengths.join(', ')} early — your strength there gives you the luxury of waiting.` });
        } else {
          lines.push({ type: 'strength', text: `${teamName} is stacked at ${strengths.join(', ')}.` });
        }
      }
    }

    if (isMyTeam) {
      lines.push({ type: 'note', text: leanNeed ? `Need-based drafting fills holes fast but can miss top-tier talent. Consider trading back if your targets are projected later.` : `Value-based drafting maximizes long-term upside. Trade for positional needs after the draft if holes remain.` });
    } else {
      lines.push({ type: 'note', text: leanNeed ? `Expect ${teamName} to prioritize positional need.` : `Expect ${teamName} to take the best player available.` });
    }

    return { stars, weaknesses, strengths, teamPicks, pickTargets, lines, posClrs, isMyTeam, teamName };
  }, [draftStrategyOwnerId, sleeperSelectedOwner, sleeperDraftBoard, playerValues, sleeperRosters, sleeperPlayerNames, sleeperLeagueInfo, formatAdjustedPlayers, mockDraftYear, draftValueBased]);

  // Mock draft handlers
  const resetMockDraft = () => {
    setMockDraftActive(false);
    setMockDraftPaused(false);
    setMockDraftResults({});
    setMockDraftCurrentPick(-1);
    setMockDraftComplete(false);
    setMockDraftSearch('');
    setMockDraftSpeed(750);
    setSummaryTeamOwnerId(null);
  };

  const buildGuestDraftBoard = () => {
    const GUEST_OWNER = 'guest_user';
    const picks = [];
    let overall = 1;
    for (let round = 1; round <= guestNumRounds; round++) {
      const isSnakeReverse = guestDraftType === 'snake' && round % 2 === 0;
      const isReversed = isSnakeReverse !== (mockDraftReverseRound3 && round === 3);
      for (let pickInRound = 1; pickInRound <= guestNumTeams; pickInRound++) {
        const slot = isReversed ? guestNumTeams + 1 - pickInRound : pickInRound;
        picks.push({
          idx: overall - 1,
          overall,
          round,
          slot,
          pick_in_round: pickInRound,
          pick_label: `${round}.${String(pickInRound).padStart(2, '0')}`,
          current_owner_id: slot === guestDraftPosition ? GUEST_OWNER : `team_${slot}`,
          is_my_pick: slot === guestDraftPosition,
        });
        overall++;
      }
    }
    const teams = Array.from({ length: guestNumTeams }, (_, i) => ({
      owner_id: i + 1 === guestDraftPosition ? GUEST_OWNER : `team_${i + 1}`,
      display_name: i + 1 === guestDraftPosition ? 'My Team' : `Team ${i + 1}`,
      name: i + 1 === guestDraftPosition ? 'My Team' : `Team ${i + 1}`,
    }));
    return { picks, num_teams: guestNumTeams, draft_type: guestDraftType, season: String(mockDraftYear), draft_order_set: true, teams };
  };

  // Load how many Sleeper-synced mocks this free account has already used.
  useEffect(() => {
    if (isLoggedIn && cognitoSub && isPaid !== 1 && window.location.hostname !== 'localhost') {
      fetch(`${API_BASE}/synced-mock-count?cognito_sub=${encodeURIComponent(cognitoSub)}`)
        .then(r => (r.ok ? r.json() : { count: 0 }))
        .then(d => setSyncedMockCount(d.count || 0))
        .catch(() => {});
    }
  }, [isLoggedIn, cognitoSub, isPaid, API_BASE]);

  const startGuestDraft = () => {
    if (!isLoggedIn) return;
    if (mockDraftLimitReached) { setShowDraftLimitModal(true); return; }
    const GUEST_OWNER = 'guest_user';
    const board = buildGuestDraftBoard();
    setSleeperDraftBoard(board);
    setSleeperSelectedOwner(GUEST_OWNER);
    setSleeperLeagueInfo({ teams: board.teams });
    setSleeperRosters({});
    setSleeperPlayerNames({});
  };

  const startMockDraft = () => {
    if (!isLoggedIn) return;
    // Sleeper-synced mocks are metered for free accounts (guest drafts are unlimited-free).
    const unlimited = isPaid === 1 || window.location.hostname === 'localhost';
    const isSyncedMock = sleeperSelectedOwner && sleeperSelectedOwner !== 'guest_user';
    if (isSyncedMock && !unlimited) {
      if (syncedMockCount >= FREE_SYNCED_MOCKS) {
        openUpgradePrompt('synced_mock_limit', `You've used your ${FREE_SYNCED_MOCKS} free synced mocks`, 'Upgrade to AMPLive for unlimited Sleeper-synced mock drafts with your real league. $10/mo, cancel anytime.');
        return;
      }
      setSyncedMockCount(c => c + 1);
      fetch(`${API_BASE}/increment-synced-mock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cognito_sub: cognitoSub }),
      }).catch(() => {});
      logUserAction('synced_mock_run', { count: syncedMockCount + 1 });
    }
    if (mockDraftLimitReached) { setShowDraftLimitModal(true); return; }
    resetMockDraft();
    const base = sleeperDraftBoard || sleeperBaseDraftBoard;
    if (base) {
      let picks = [...base.picks];

      // Apply 3rd round reversal if toggled on (linear or snake)
      if (mockDraftReverseRound3) {
        const numTeams = base.num_teams || 12;
        picks = picks.map(pick => {
          if (pick.round !== 3) return pick;
          // slot 1 picks last (3.12), slot 12 picks first (3.01)
          const newPickInRound = numTeams + 1 - pick.slot;
          return {
            ...pick,
            pick_in_round: newPickInRound,
            pick_label: `3.${String(newPickInRound).padStart(2, '0')}`,
            overall: (2 * numTeams) + newPickInRound,
          };
        });
      }

      // Sort by overall so simulation follows correct draft order (handles snake + reversal)
      picks.sort((a, b) => a.overall - b.overall);
      setSleeperDraftBoard({ ...base, picks });
    }
    setMockDraftActive(true);
    setDraftStrategyCollapsed(true);
  };

  const handleMockDraftUserPick = (player) => {
    const currentPick = sleeperDraftBoard?.picks?.[mockDraftCurrentPick];
    logUserAction('mock_draft_pick', {
      pick_index: mockDraftCurrentPick,
      pick_label: currentPick?.pick_label,
      round: currentPick?.round,
      slot: currentPick?.slot,
      is_user_pick: true,
      draft_year: mockDraftYear,
      league_id: sleeperLeagueId,
      owner_id: sleeperSelectedOwner,
      player_name: (player.first_name + ' ' + player.last_name).trim(),
      player_id: player.id,
      position: player.metrics?.position || player.position || '',
    });
    setMockDraftResults(prev => ({
      ...prev,
      [mockDraftCurrentPick]: { player, isUserPick: true },
    }));
    setMockDraftSearch('');
    setMockDraftPaused(false);
  };

  const handleOverridePick = (player) => {
    if (overridingPickIndex == null) return;
    setMockDraftResults(prev => {
      const existing = prev[overridingPickIndex] || {};
      return {
        ...prev,
        [overridingPickIndex]: { player, isUserPick: !!existing.isUserPick, isOverridden: true },
      };
    });
    setOverridingPickIndex(null);
    setOverrideSearch('');
  };

  const TIER_KEYS = ['S', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const [tierViewSource, setTierViewSource] = useState('mine'); // 'mine' | 'amp' (official AMP default tiers, read-only)
  const [revealMode, setRevealMode] = useState(false);
  const [revealCount, setRevealCount] = useState(0);
  const [revealPanelHidden, setRevealPanelHidden] = useState(false);
  const [revealInfoOpen, setRevealInfoOpen] = useState(false);

  // Reveal mode: right arrow advances to the next player (works with the
  // control panel hidden, for clean screen recordings); panel hide state
  // resets when leaving reveal mode.
  useEffect(() => {
    if (!revealMode) {
      setRevealPanelHidden(false);
      return;
    }
    const onKeyDown = (e) => {
      if (e.key !== 'ArrowRight') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const total = TIER_KEYS.reduce((sum, k) => sum + (tierBuilderData?.[k]?.length || 0), 0);
      setRevealCount(c => Math.min(c + 1, total));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [revealMode, tierBuilderData]);
  const [whatAreAmpScoresOpen, setWhatAreAmpScoresOpen] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);

  const initializeTierBuilderDefaults = () => {
    const ranked = formatAdjustedPlayers
      .filter(p => p.nfl_draft_year === yearFilter && p.rank != null)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 64);
    setTierBuilderData({
      S: ranked.slice(0, 9),
      A: ranked.slice(9, 18),
      B: ranked.slice(18, 27),
      C: ranked.slice(27, 36),
      D: ranked.slice(36, 45),
      E: ranked.slice(45, 54),
      F: ranked.slice(54, 64),
      G: [],
      H: [],
      I: [],
      J: [],
    });
  };

  const initializeTierBuilder = () => {
    if (isPaid === 1 && cognitoSub) {
      fetch(`${API_BASE}/get-tier-builder?cognito_sub=${encodeURIComponent(cognitoSub)}&year=${yearFilter}`)
        .then(res => {
          if (!res.ok) throw new Error('No saved data');
          return res.json();
        })
        .then(data => {
          const savedTiers = data.tier_data;
          const playerMap = {};
          formatAdjustedPlayers.forEach(p => { playerMap[p.id] = p; });
          const rehydrated = {};
          TIER_KEYS.forEach(k => {
            rehydrated[k] = (savedTiers[k] || [])
              .map(id => playerMap[id])
              .filter(Boolean);
          });
          setTierBuilderData(rehydrated);
        })
        .catch(() => {
          initializeTierBuilderDefaults();
        });
    } else {
      initializeTierBuilderDefaults();
    }
  };

  // Official AMP default tiers (support@fantasyamp.com's board), read-only for all users.
  // Falls back to rank-ordered default tiers if the endpoint isn't available.
  const loadAmpDefaultTiers = () => {
    if (tierBuilderSaveTimer.current) {
      clearTimeout(tierBuilderSaveTimer.current);
      tierBuilderSaveTimer.current = null;
    }
    fetch(`${API_BASE}/get-default-tiers?year=${yearFilter}`)
      .then(res => {
        if (!res.ok) throw new Error('No default tiers');
        return res.json();
      })
      .then(data => {
        const savedTiers = data.tier_data;
        const playerMap = {};
        formatAdjustedPlayers.forEach(p => { playerMap[p.id] = p; });
        const rehydrated = {};
        TIER_KEYS.forEach(k => {
          rehydrated[k] = (savedTiers[k] || [])
            .map(id => playerMap[id])
            .filter(Boolean);
        });
        setTierBuilderData(rehydrated);
      })
      .catch(() => {
        initializeTierBuilderDefaults();
      });
  };

  // Re-initialize tier builder when year changes while in tier-builder view
  useEffect(() => {
    if (viewMode === 'tier-builder' && !tierBuilderData && formatAdjustedPlayers.length > 0) {
      if (tierViewSource === 'amp') {
        loadAmpDefaultTiers();
      } else {
        initializeTierBuilder();
      }
    }
  }, [viewMode, tierBuilderData, yearFilter, formatAdjustedPlayers.length, tierViewSource]);



  const handleTierMove = (tierKey, playerIndex, direction) => {
    if (tierViewSource === 'amp') return; // AMP default board is read-only
    setTierBuilderData(prev => {
      if (!prev) return prev;
      const data = {};
      TIER_KEYS.forEach(k => { data[k] = [...prev[k]]; });
      const tierIdx = TIER_KEYS.indexOf(tierKey);
      if (direction === 'up') {
        if (tierIdx === 0) return prev;
        const aboveKey = TIER_KEYS[tierIdx - 1];
        const [player] = data[tierKey].splice(playerIndex, 1);
        data[aboveKey].push(player);
      } else if (direction === 'down') {
        if (tierIdx === TIER_KEYS.length - 1) return prev;
        const belowKey = TIER_KEYS[tierIdx + 1];
        const [player] = data[tierKey].splice(playerIndex, 1);
        data[belowKey].push(player);
      } else if (direction === 'left') {
        if (playerIndex === 0) return prev;
        const temp = data[tierKey][playerIndex - 1];
        data[tierKey][playerIndex - 1] = data[tierKey][playerIndex];
        data[tierKey][playerIndex] = temp;
      } else if (direction === 'right') {
        if (playerIndex === data[tierKey].length - 1) return prev;
        const temp = data[tierKey][playerIndex + 1];
        data[tierKey][playerIndex + 1] = data[tierKey][playerIndex];
        data[tierKey][playerIndex] = temp;
      }
      return data;
    });
    debouncedSaveTierBuilder();
  };

  const handleTierBuilderAdd = (player, targetTier) => {
    if (tierViewSource === 'amp') return; // AMP default board is read-only
    setTierBuilderData(prev => {
      if (!prev) return prev;
      // Check if player is already in any tier
      for (const k of TIER_KEYS) {
        if (prev[k].some(p => p.id === player.id)) return prev;
      }
      const data = {};
      TIER_KEYS.forEach(k => { data[k] = [...prev[k]]; });
      data[targetTier].push(player);
      return data;
    });
    setTierBuilderSearch('');
    debouncedSaveTierBuilder();
  };

  const handleTierBuilderRemove = (tierKey, playerIndex) => {
    if (tierViewSource === 'amp') return; // AMP default board is read-only
    setTierBuilderData(prev => {
      if (!prev) return prev;
      const data = {};
      TIER_KEYS.forEach(k => { data[k] = [...prev[k]]; });
      data[tierKey].splice(playerIndex, 1);
      return data;
    });
    debouncedSaveTierBuilder();
  };

  const saveTierBuilder = () => {
    const currentData = tierBuilderDataRef.current;
    if (tierViewSource === 'amp') return; // never save the AMP board over a user's own
    if (isPaid !== 1 || !cognitoSub || !currentData) return;
    const tierIds = {};
    TIER_KEYS.forEach(k => {
      tierIds[k] = (currentData[k] || []).map(p => p.id);
    });
    setTierBuilderSaveStatus('saving');
    fetch(`${API_BASE}/save-tier-builder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cognito_sub: cognitoSub, year: yearFilter, tier_data: tierIds }),
    })
      .then(() => {
        setTierBuilderSaveStatus('saved');
        setTimeout(() => setTierBuilderSaveStatus(null), 2000);
      })
      .catch(err => {
        console.error('Tier builder save error:', err);
        setTierBuilderSaveStatus(null);
      });
  };

  const debouncedSaveTierBuilder = () => {
    if (isPaid !== 1 || !cognitoSub) return;
    if (tierBuilderSaveTimer.current) clearTimeout(tierBuilderSaveTimer.current);
    tierBuilderSaveTimer.current = setTimeout(() => saveTierBuilder(), 2000);
  };

  const handleReassignPick = (pickIndex, newOwnerId, newOwnerName) => {
    setSleeperDraftBoard(prev => {
      if (!prev?.picks) return prev;
      const updatedPicks = [...prev.picks];
      updatedPicks[pickIndex] = {
        ...updatedPicks[pickIndex],
        current_owner_id: newOwnerId,
        current_owner_name: newOwnerName,
        is_my_pick: newOwnerId === sleeperSelectedOwner,
        was_traded: true,
      };
      return { ...prev, picks: updatedPicks };
    });
    setReassigningPickIndex(null);
  };

  const shareDraftResults = async () => {
    if (!sleeperDraftBoard?.picks || !sleeperLeagueInfo?.teams) return;
    const team = sleeperLeagueInfo.teams.find(t => t.owner_id === summaryTeamOwnerId);
    const teamName = team?.display_name || 'My Team';

    // Gather this team's picks
    const picks = [];
    (sleeperDraftBoard.picks || []).forEach((pick, idx) => {
      const result = mockDraftResults[idx];
      if (result && pick.current_owner_id === summaryTeamOwnerId) {
        const pos = result.player.metrics?.position || result.player.position || '';
        const name = `${result.player.first_name} ${result.player.last_name}`;
        const ampScore = calculateCompositeAmpScore(result.player);
        const headshot = result.player.headshot_url || result.player.image || null;
        picks.push({ pickLabel: pick.pick_label, name, pos, ampScore, isUserPick: result.isUserPick, headshot });
      }
    });

    // Preload headshot images
    const loadImage = (src) => new Promise((resolve) => {
      if (!src) { resolve(null); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
    const [headshotImages, bgImage] = await Promise.all([
      Promise.all(picks.map(p => loadImage(p.headshot))),
      loadImage('/amp_amped.jpg'),
    ]);

    const posColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
    const cardW = 600;
    const headerH = 80;
    const pickH = 48;
    const footerH = 50;
    const padX = 24;
    const imgSize = 32;
    const cardH = headerH + picks.length * pickH + footerH + 20;

    const canvas = document.createElement('canvas');
    canvas.width = cardW * 2;
    canvas.height = cardH * 2;
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);

    // Background
    const bg = ctx.createLinearGradient(0, 0, cardW, cardH);
    bg.addColorStop(0, '#1a1a2e');
    bg.addColorStop(1, '#16213e');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, cardW, cardH);

    // Background image overlay
    if (bgImage) {
      ctx.save();
      ctx.globalAlpha = 0.5;
      const scale = Math.max(cardW / bgImage.width, cardH / bgImage.height);
      const bw = bgImage.width * scale;
      const bh = bgImage.height * scale;
      ctx.drawImage(bgImage, (cardW - bw) / 2, (cardH - bh) / 2, bw, bh);
      ctx.restore();
    }

    // Gold accent line at top
    const accent = ctx.createLinearGradient(0, 0, cardW, 0);
    accent.addColorStop(0, '#FFD700');
    accent.addColorStop(1, '#FFA500');
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, cardW, 4);

    // Header
    ctx.fillStyle = '#FFD700';
    ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText(teamName, padX, 40);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '14px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText(`${mockDraftYear} Mock Draft — ${picks.length} Picks`, padX, 62);

    // Picks
    picks.forEach((p, i) => {
      const y = headerH + i * pickH;
      const rowCenterY = y + pickH / 2;

      // Alternating row bg
      if (i % 2 === 0) {
        ctx.fillStyle = 'rgba(255,255,255,0.03)';
        ctx.fillRect(0, y, cardW, pickH);
      }

      // Pick label
      ctx.fillStyle = 'rgba(255,215,0,0.7)';
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(p.pickLabel, padX, rowCenterY + 4);

      // Player headshot (circular)
      const imgX = padX + 50;
      const imgCenterX = imgX + imgSize / 2;
      const imgCenterY = rowCenterY;
      if (headshotImages[i]) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(imgCenterX, imgCenterY, imgSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(headshotImages[i], imgX, imgCenterY - imgSize / 2, imgSize, imgSize);
        ctx.restore();
        // Circle border
        ctx.strokeStyle = 'rgba(255,255,255,0.2)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(imgCenterX, imgCenterY, imgSize / 2, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Placeholder circle
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.beginPath();
        ctx.arc(imgCenterX, imgCenterY, imgSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Position badge (shifted right for headshot)
      const posW = 30;
      const posX = imgX + imgSize + 8;
      ctx.fillStyle = (posColors[p.pos] || '#888') + '33';
      ctx.beginPath();
      ctx.roundRect(posX, rowCenterY - 9, posW, 18, 4);
      ctx.fill();
      ctx.fillStyle = posColors[p.pos] || '#888';
      ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(p.pos, posX + 5, rowCenterY + 4);

      // Player name
      ctx.fillStyle = p.isUserPick ? '#FFD700' : '#fff';
      ctx.font = '600 15px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(p.name, posX + posW + 12, rowCenterY + 5);

      // AMP score on right (logged-in only, 2026-2028 only — 2029 has no scores yet)
      if (parseFloat(p.ampScore) > 0 && (isLoggedIn || window.location.hostname === 'localhost') && mockDraftYear !== 2029) {
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '12px -apple-system, BlinkMacSystemFont, sans-serif';
        const scoreText = `AMP ${Math.round(parseFloat(p.ampScore))}`;
        const scoreW = ctx.measureText(scoreText).width;
        ctx.fillText(scoreText, cardW - padX - scoreW, rowCenterY + 4);
      }
    });

    // Footer
    const footerY = headerH + picks.length * pickH + 10;
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(0, footerY, cardW, footerH);
    ctx.fillStyle = 'rgba(255,215,0,0.6)';
    ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('fantasyamp.com/prospects', padX, footerY + 30);
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '11px -apple-system, BlinkMacSystemFont, sans-serif';
    const dateText = new Date().toLocaleDateString();
    const dateW = ctx.measureText(dateText).width;
    ctx.fillText(dateText, cardW - padX - dateW, footerY + 30);

    // Download
    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mock-draft-${teamName.replace(/[^a-zA-Z0-9]/g, '-')}-${mockDraftYear}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const downloadMockDraftResults = () => {
    if (!sleeperDraftBoard?.picks) return;
    const rows = [['Pick', 'Round', 'Team', 'Player', 'Position', 'Rank', 'Picked By']];
    sleeperDraftBoard.picks.forEach((pick, idx) => {
      const result = mockDraftResults[idx];
      if (!result) return;
      rows.push([
        pick.pick_label,
        pick.round,
        pick.current_owner_name,
        `${result.player.first_name} ${result.player.last_name}`,
        result.player.metrics?.position || '',
        result.player.rank || '',
        result.isUserPick ? 'You' : 'AI',
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mock-draft-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Base filter
  const baseFiltered = useMemo(() => {
    if (rankingMode === 'cff') {
      return formatAdjustedPlayers.filter((p) => {
        if (p.cff_rank == null) return false;
        if (![2027, 2028, 2029].includes(p.nfl_draft_year)) return false;
        const matchPosition = filter === "All" || p?.metrics?.position === filter;
        const playerTeam = p.metrics?.college_team || p.college_team || '';
        const matchTeam = teamFilter === "All" || playerTeam === teamFilter;
        return matchPosition && matchTeam;
      }).sort((a, b) => (a.cff_rank || 9999) - (b.cff_rank || 9999));
    }
    const isProspect = yearFilter === 2027 || yearFilter === 2028 || yearFilter === 2029;
    return formatAdjustedPlayers.filter((p) => {
      const matchPosition = filter === "All" || p?.metrics?.position === filter;

      // Just use regular year filter
      const matchYear = !yearFilter || p.nfl_draft_year === yearFilter;

      const playerTeam = isProspect
        ? (p.metrics?.college_team || p.college_team || '')
        : (p.nfl_team || '');
      const matchTeam = teamFilter === "All" || playerTeam === teamFilter;
      const matchP4 = !p4Filter || P4_SCHOOLS.has(playerTeam);

      return matchPosition && matchYear && matchTeam && matchP4;
    }).slice(0, yearFilter === 2026 ? 96 : undefined);
  }, [formatAdjustedPlayers, filter, teamFilter, p4Filter, yearFilter, rankingMode]);

  // Positional rank within the current filtered set (WR1, WR2…)
  const posRankMap = useMemo(() => {
    const counts = {};
    const map = {};
    [...baseFiltered]
      .sort((a, b) => rankingMode === 'cff'
        ? (a.cff_rank || 9999) - (b.cff_rank || 9999)
        : (a.rank || 999) - (b.rank || 999))
      .forEach((p) => {
        const pos = p.metrics?.position || 'UNK';
        counts[pos] = (counts[pos] || 0) + 1;
        map[p.id] = counts[pos];
      });
    return map;
  }, [baseFiltered, rankingMode]);

  // Group by Round → Early/Mid/Late
  const playersByRound = useMemo(() => {
    const grouped = {};
    baseFiltered.forEach((p) => {
      if (!p.draft_pick_capital) return;
      const [roundStr, pickStr] = p.draft_pick_capital.split(".");
      const round = parseInt(roundStr, 10);
      const pick = parseInt(pickStr, 10);

      let sub;
      if (pick <= 4) sub = "Early";
      else if (pick <= 8) sub = "Mid";
      else sub = "Late";

      if (!grouped[round]) {
        grouped[round] = { Early: [], Mid: [], Late: [] };
      }
      grouped[round][sub].push(p);
    });

    // Debug logging
    console.log('playersByRound:', Object.keys(grouped));
    console.log('selectedRound:', selectedRound);
    console.log('Round 1 players:', grouped[1]);

    return grouped;
  }, [baseFiltered, selectedRound]);

  // Compute pick ranges with average scores for Pick Value view
  const pickRanges = useMemo(() => {
    const ranges = [];

    // Check if this year has AMP scores (2026 has scores, 2027+ don't yet)
    const hasScores = yearFilter === 2026;

    Object.entries(playersByRound).forEach(([round, subgroups]) => {
      const roundNum = parseInt(round);

      // Round access: logged-out see round 1; logged-in free see rounds 1-2; paid see all.
      if (!(isPaid === 1 || window.location.hostname === 'localhost')) {
        if (isLoggedIn ? roundNum > 2 : roundNum !== 1) return;
      }

      ['Early', 'Mid', 'Late'].forEach((sub) => {
        const players = subgroups[sub] || [];
        if (players.length === 0) return;

        // Calculate average composite score only if year has scores
        let avgScore = null;
        let tier = null;

        if (hasScores) {
          avgScore = players.length > 0
            ? (players.reduce((sum, p) => sum + parseFloat(calculateCompositeAmpScore(p)), 0) / players.length).toFixed(2)
            : 0;
          tier = getAmpTier(avgScore);
        }

        // Determine pick range (1-4, 5-8, 9-12)
        let startPick, endPick;
        if (sub === 'Early') { startPick = 1; endPick = 4; }
        else if (sub === 'Mid') { startPick = 5; endPick = 8; }
        else { startPick = 9; endPick = 12; }

        ranges.push({
          round: roundNum,
          subgroup: sub,
          label: getPickRangeLabel(roundNum, startPick, endPick),
          players,
          avgScore,
          tier,
          year: yearFilter,
          hasScores,
        });
      });
    });

    // Sort by round then subgroup
    ranges.sort((a, b) => {
      if (a.round !== b.round) return a.round - b.round;
      const subOrder = { Early: 0, Mid: 1, Late: 2 };
      return subOrder[a.subgroup] - subOrder[b.subgroup];
    });

    return ranges;
  }, [playersByRound, yearFilter, isPaid]);

  // Loading state while checking auth
  if (!authChecked) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
      }}>
        <div style={{
          width: "48px",
          height: "48px",
          border: "4px solid rgba(255, 215, 0, 0.3)",
          borderTopColor: "#FFD700",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
    <style>{`
      .amp-nav-banner {
        width: 100%;
        background: #0f1b2d;
        border-bottom: 1px solid rgba(255,255,255,0.08);
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 16px;
        min-height: 48px;
        position: relative;
        box-sizing: border-box;
      }
      .amp-nav-logo {
        height: 36px;
        width: 36px;
        border-radius: 50%;
        object-fit: cover;
        border: 1px solid rgba(255, 215, 0, 0.4);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        display: block;
      }
      .amp-nav-brand {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 1.25rem;
        font-weight: 900;
        letter-spacing: -0.3px;
        background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45));
        text-decoration: none;
        pointer-events: none;
        white-space: nowrap;
      }
      @media (max-width: 600px) {
        .amp-nav-logo {
          height: 30px;
          width: 30px;
        }
        .amp-nav-brand {
          font-size: 1.05rem;
        }
      }
      .amp-nav-hamburger {
        background: transparent;
        border: 1px solid rgba(255, 215, 0, 0.3);
        border-radius: 6px;
        padding: 8px 10px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 4px;
        transition: all 0.2s ease;
      }
      .amp-nav-hamburger:hover {
        background: rgba(255, 215, 0, 0.08);
        border-color: rgba(255, 215, 0, 0.55);
      }
      .amp-nav-hamburger span {
        display: block;
        width: 20px;
        height: 2px;
        background: rgba(255, 215, 0, 0.85);
        border-radius: 2px;
        transition: all 0.25s ease;
      }
      .amp-nav-hamburger.open span:nth-child(1) {
        transform: translateY(6px) rotate(45deg);
      }
      .amp-nav-hamburger.open span:nth-child(2) {
        opacity: 0;
      }
      .amp-nav-hamburger.open span:nth-child(3) {
        transform: translateY(-6px) rotate(-45deg);
      }
      .amp-nav-menu {
        position: absolute;
        top: 100%;
        right: 12px;
        margin-top: 6px;
        background: #0f1b2d;
        border: 1px solid rgba(255, 215, 0, 0.3);
        border-radius: 10px;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
        display: flex;
        flex-direction: column;
        min-width: 220px;
        z-index: 200;
        overflow: hidden;
      }
      .amp-nav-menu a {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        color: rgba(255, 255, 255, 0.75);
        text-decoration: none;
        font-size: 0.85rem;
        font-weight: 600;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        transition: all 0.2s ease;
        letter-spacing: 0.02em;
      }
      .amp-nav-menu a:last-child {
        border-bottom: none;
      }
      .amp-nav-menu a:hover {
        color: #FFD700;
        background: rgba(255, 215, 0, 0.08);
      }
      .amp-nav-menu .icon {
        color: #FFD700;
        font-size: 1rem;
        width: 16px;
        text-align: center;
      }
      .amp-upgrade-bar {
        width: 100%;
        background: linear-gradient(90deg, #1a0a00 0%, #2d1500 50%, #1a0a00 100%);
        border-bottom: 1px solid rgba(255, 165, 0, 0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 9px 16px;
        box-sizing: border-box;
        text-align: center;
        flex-wrap: wrap;
      }
      .amp-upgrade-bar-text {
        font-size: 0.82rem;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.75);
        letter-spacing: 0.01em;
        white-space: nowrap;
      }
      .amp-upgrade-bar-link {
        display: inline-block;
        background: linear-gradient(135deg, #f97316, #ea580c);
        color: #fff;
        font-size: 0.78rem;
        font-weight: 800;
        padding: 4px 14px;
        border-radius: 20px;
        text-decoration: none;
        letter-spacing: 0.03em;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(249, 115, 22, 0.4);
        transition: all 0.2s ease;
      }
      .amp-upgrade-bar-link:hover {
        background: linear-gradient(135deg, #fb923c, #f97316);
        box-shadow: 0 4px 12px rgba(249, 115, 22, 0.6);
        transform: translateY(-1px);
      }
      @media (max-width: 480px) {
        .amp-upgrade-bar-text { font-size: 0.75rem; }
      }
    `}</style>
    {!presentationMode && (
    <div className="amp-nav-banner">
      <a href="https://fantasyamp.com">
        <img
          src="/assets/amp_logo.png"
          alt="Fantasy Amp"
          className="amp-nav-logo"
        />
      </a>
      <span className="amp-nav-brand">Fantasy Amp</span>
      <button
        type="button"
        className={`amp-nav-hamburger${navMenuOpen ? ' open' : ''}`}
        onClick={() => setNavMenuOpen(prev => !prev)}
        aria-label={navMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={navMenuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      {navMenuOpen && (
        <div className="amp-nav-menu" onClick={() => setNavMenuOpen(false)}>
          <a href="https://fantasyamp.com/trade">
            <span className="icon">⇄</span><span>Trade Tracker</span>
          </a>
          <a href="https://fantasyamp.com/story">
            <span className="icon">✦</span><span>Our Story</span>
          </a>
          <a href="https://fantasyamp.com/rookies_2025">
            <span className="icon">◆</span><span>Historic Profiles</span>
          </a>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsDarkMode(prev => !prev);
              logUserAction('toggle_dark_mode', { enabled: !isDarkMode });
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              padding: "12px 16px",
              background: "none",
              border: "none",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              color: "#fff",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            <span className="icon">{isDarkMode ? '☀️' : '🌙'}</span>
            <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      )}
    </div>
    )}
    {!presentationMode && isLoggedIn && isPaid !== 1 && (
      <div className="amp-upgrade-bar">
        <span className="amp-upgrade-bar-text">🔒 Unlock the Tier Builder, synced mock drafts &amp; rookie pick valuation.</span>
        <a href="https://fantasyamp.com/products" className="amp-upgrade-bar-link" target="_blank" rel="noopener noreferrer">
          Try AMPLive Free →
        </a>
      </div>
    )}
    {/* Brand corner badge: shows whenever the board is in a "recording clean" state
        (scores hidden, reveal mode, or presentation mode) so captures carry the brand once */}
    {(hideAmpScores || revealMode || presentationMode) && (
      <div style={{
        position: "fixed",
        bottom: "16px",
        right: "16px",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        background: "rgba(26,26,46,0.75)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        border: "1px solid rgba(102,126,234,0.35)",
        borderRadius: "20px",
        opacity: 0.85,
        userSelect: "none",
        pointerEvents: "none",
      }}>
        <img
          src="/assets/amp_logo.png"
          alt=""
          style={{ width: "16px", height: "16px", borderRadius: "50%" }}
        />
        <span style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "0.5px",
          background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}>
          fantasyamp.com
        </span>
      </div>
    )}
    {/* Reusable upgrade / login prompt modal (replaces alert() paywalls) */}
    {upgradePrompt && (
      <div
        onClick={() => setUpgradePrompt(null)}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 10000, backdropFilter: "blur(4px)",
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            border: "2px solid rgba(255,215,0,0.4)",
            borderRadius: "16px", padding: "40px 36px",
            maxWidth: "420px", width: "90%", textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          }}
        >
          <div style={{ fontSize: "2rem", marginBottom: "12px" }}>🔒</div>
          <div style={{ color: "#FFD700", fontSize: "1.4rem", fontWeight: "800", marginBottom: "10px" }}>
            {upgradePrompt.title}
          </div>
          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.95rem", marginBottom: "24px", lineHeight: "1.6" }}>
            {upgradePrompt.message}
          </div>
          <a
            href={upgradePrompt.requiresLogin ? COGNITO_LOGIN_URL : "https://fantasyamp.com/products"}
            style={{
              display: "block", padding: "14px 32px", borderRadius: "10px",
              background: upgradePrompt.requiresLogin
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #FFD700, #FFA500)",
              color: upgradePrompt.requiresLogin ? "#fff" : "#1a1a2e",
              fontWeight: "800", fontSize: "1rem",
              textDecoration: "none", marginBottom: "12px",
            }}
          >
            {upgradePrompt.requiresLogin ? "Log In / Sign Up Free" : (upgradePrompt.ctaLabel || "Upgrade to AMPLive for $10/mo →")}
          </a>
          {upgradePrompt.requiresLogin && (
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", marginBottom: "8px" }}>
              Free to sign up. By signing up you'll also receive the free Fantasy Amp newsletter. Unsubscribe anytime.
            </div>
          )}
          <button
            onClick={() => setUpgradePrompt(null)}
            style={{
              background: "transparent", border: "none",
              color: "rgba(255,255,255,0.35)", fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    )}
    <div
      style={{
        padding: "32px 24px",
        fontFamily: "Space Grotesk",
        background: isDarkMode
          ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
          : "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
        minHeight: "100vh",
        color: isDarkMode ? "#ffffff" : "#000",
        boxShadow: "inset 0 0 60px rgba(0,0,0,0.3)",
      }}
    >

      {/* Pick Range Modal */}
      {selectedPickRange && (
        <PickRangeModal
          pickRange={selectedPickRange}
          onClose={() => setSelectedPickRange(null)}
        />
      )}

      {/* Comparison Modal */}
      {showComparison && (
        <ComparisonModal
          players={selectedPlayers}
          onClose={() => setShowComparison(false)}
          onRemovePlayer={removeFromComparison}
          isPaid={isPaid}
        />
      )}

      {/* Floating Compare Button */}
      {selectedPlayers.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            zIndex: 999,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            alignItems: "flex-end",
          }}
        >
          {/* Compare Button */}
          <button
            onClick={() => setShowComparison(true)}
            style={{
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              border: "none",
              color: "#000",
              padding: "16px 32px",
              fontSize: "1.2rem",
              fontWeight: "800",
              borderRadius: "50px",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(255, 215, 0, 0.6)",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05) translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(255, 215, 0, 0.8)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1) translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(255, 215, 0, 0.6)";
            }}
          >
            <span>⚖️</span>
            <span>Compare ({selectedPlayers.length})</span>
          </button>

          {/* Clear All Button */}
          <button
            onClick={clearAllSelections}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              color: "white",
              padding: "10px 20px",
              fontSize: "0.9rem",
              fontWeight: "600",
              borderRadius: "25px",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#FF4444";
              e.currentTarget.style.borderColor = "#FF4444";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
            }}
          >
            Clear All
          </button>
        </div>
      )}


{/* Clean Top Bar: Tabs + Dark Mode */}
{!presentationMode && (
<div
  style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    marginBottom: "28px",
    flexWrap: "wrap",
    padding: "0 20px",
  }}
>
  {[
    { key: 'mock-draft', label: 'Mock Draft', desc: 'Simulate Drafts' },
    { key: 'rankings', label: 'Rankings', desc: 'Player Board' },
    // { key: 'pick-value', label: 'Pick Value', desc: 'Startup Equivalents' },
    { key: 'tier-builder', label: 'Tier Builder', desc: 'Build Your Board' },
  ].map(tab => {
    const active = viewMode === tab.key;
    const locked = window.location.hostname !== 'localhost' && (
      tab.key !== 'rankings' && tab.key !== 'mock-draft' && tab.key !== 'tier-builder' && !isLoggedIn
    );
    return (
      <button
        key={tab.key}
        onClick={() => {
          if (locked) {
            if (tab.key === 'tier-builder') {
              openUpgradePrompt('tier_builder', 'Tier Builder is an AMPLive feature', 'Build your own tiered draft board with full drag-and-drop control. It autosaves as you go.');
            } else {
              openUpgradePrompt('login_required_tab', 'Create a free account to continue', 'Log in to access this feature.', true);
            }
            return;
          }
          if (tab.key === 'tier-builder') {
            if (isPaid !== 1) {
              // Free / logged-out users land on the official AMP board, read-only
              setTierViewSource('amp');
              setTierBuilderData(null); // effect reloads from the amp source
            } else if (!tierBuilderData) {
              initializeTierBuilder();
            }
          }
          setViewMode(tab.key);
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "10px 24px",
          minWidth: "110px",
          borderRadius: "12px",
          border: active ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
          background: active
            ? "linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,165,0,0.1))"
            : "rgba(255,255,255,0.05)",
          cursor: locked ? "not-allowed" : "pointer",
          transition: "all .3s ease",
          boxShadow: active ? "0 4px 16px rgba(255,215,0,0.25)" : "none",
          transform: active ? "translateY(-1px)" : "none",
          opacity: locked ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = "rgba(255,255,255,0.1)";
            e.currentTarget.style.borderColor = "rgba(255,215,0,0.4)";
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
          }
        }}
      >
        <span style={{
          fontSize: "0.9rem",
          fontWeight: "700",
          color: active ? "#FFD700" : "#fff",
          letterSpacing: "0.3px",
        }}>
          {locked && "🔒 "}{tab.label}
        </span>
        <span style={{
          fontSize: "0.6rem",
          color: active ? "rgba(255,215,0,0.7)" : "rgba(255,255,255,0.35)",
          marginTop: "2px",
          fontWeight: "500",
        }}>
          {locked ? (tab.key === 'tier-builder' ? "AMPLive Required" : "Login Required") : tab.desc}
        </span>
      </button>
    );
  })}

</div>
)}


      {/* Rankings login prompt for non-logged-in users */}
      {/* Rankings Header — only show in Rankings mode */}
      {viewMode === 'rankings' && (
        <>
          {/* What Are AMP Scores? — expandable explainer */}
          <div style={{ maxWidth: "780px", margin: "20px auto 0 auto", padding: "0 20px" }}>
            <button
              onClick={() => setWhatAreAmpScoresOpen(prev => !prev)}
              style={{
                width: "100%",
                padding: "12px 20px",
                fontSize: "0.95rem",
                fontWeight: "700",
                borderRadius: "10px",
                border: "1px solid rgba(255, 215, 0, 0.35)",
                background: "linear-gradient(135deg, rgba(255,215,0,0.08), rgba(255,165,0,0.04))",
                color: "#FFD700",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                letterSpacing: "0.3px",
              }}
            >
              <span>⚡ What are AMP Scores?</span>
              <span style={{
                display: "inline-block",
                fontSize: "0.85rem",
                opacity: 0.7,
                transform: whatAreAmpScoresOpen ? "rotate(90deg)" : "rotate(0)",
                transition: "transform 0.2s ease",
              }}>▶</span>
            </button>
            {whatAreAmpScoresOpen && (
              <div style={{
                marginTop: "10px",
                padding: "20px",
                background: "rgba(0, 0, 0, 0.25)",
                border: "1px solid rgba(255, 215, 0, 0.2)",
                borderRadius: "12px",
                display: "grid",
                gridTemplateColumns: isMobileView ? "1fr" : "repeat(3, 1fr)",
                gap: "14px",
                textAlign: "left",
              }}>
                {/* A — Adjusted Competition */}
                <div style={{
                  padding: "16px",
                  background: "linear-gradient(135deg, rgba(102, 126, 234, 0.18), rgba(102, 126, 234, 0.06))",
                  border: "2px solid rgba(102, 126, 234, 0.45)",
                  borderRadius: "10px",
                }}>
                  <div style={{
                    fontSize: "2.2rem",
                    fontWeight: 900,
                    color: "#667eea",
                    lineHeight: 1,
                    marginBottom: "8px",
                    textShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
                  }}>A</div>
                  <div style={{
                    fontSize: "0.85rem",
                    fontWeight: "800",
                    color: "#fff",
                    marginBottom: "6px",
                    letterSpacing: "0.3px",
                  }}>Adjusted Competition</div>
                  <div style={{
                    fontSize: "0.78rem",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.55,
                  }}>
                    Contextualizes whether a player produced at a big-name program or a smaller one with less competition. Formerly called Analytics, this captures the strength of schedule beneath the stat line.
                  </div>
                </div>

                {/* M — Momentum */}
                <div style={{
                  padding: "16px",
                  background: "linear-gradient(135deg, rgba(255, 165, 0, 0.18), rgba(255, 165, 0, 0.06))",
                  border: "2px solid rgba(255, 165, 0, 0.5)",
                  borderRadius: "10px",
                }}>
                  <div style={{
                    fontSize: "2.2rem",
                    fontWeight: 900,
                    color: "#FFA500",
                    lineHeight: 1,
                    marginBottom: "8px",
                    textShadow: "0 2px 8px rgba(255, 165, 0, 0.4)",
                  }}>M</div>
                  <div style={{
                    fontSize: "0.85rem",
                    fontWeight: "800",
                    color: "#fff",
                    marginBottom: "6px",
                    letterSpacing: "0.3px",
                  }}>Momentum</div>
                  <div style={{
                    fontSize: "0.78rem",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.55,
                  }}>
                    Perceived draft capital. A player can have the best profile in the world, but their initial dynasty value hinges on where they are drafted in the NFL Draft.
                  </div>
                </div>

                {/* P — Production */}
                <div style={{
                  padding: "16px",
                  background: "linear-gradient(135deg, rgba(236, 72, 153, 0.18), rgba(236, 72, 153, 0.06))",
                  border: "2px solid rgba(236, 72, 153, 0.5)",
                  borderRadius: "10px",
                }}>
                  <div style={{
                    fontSize: "2.2rem",
                    fontWeight: 900,
                    color: "#ec4899",
                    lineHeight: 1,
                    marginBottom: "8px",
                    textShadow: "0 2px 8px rgba(236, 72, 153, 0.4)",
                  }}>P</div>
                  <div style={{
                    fontSize: "0.85rem",
                    fontWeight: "800",
                    color: "#fff",
                    marginBottom: "6px",
                    letterSpacing: "0.3px",
                  }}>Production</div>
                  <div style={{
                    fontSize: "0.78rem",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.55,
                  }}>
                    A combination of position-level analytics and production stats. Think yards and TDs, plus yards per route run and yards through contact. This is an AMP differentiator that identifies the best performers at their position group to predict success at the next level.
                  </div>
                </div>
              </div>
            )}
            {whatAreAmpScoresOpen && (
              <div style={{
                marginTop: "10px",
                padding: "12px 16px",
                background: "rgba(255, 215, 0, 0.06)",
                border: "1px dashed rgba(255, 215, 0, 0.3)",
                borderRadius: "10px",
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.65)",
                lineHeight: 1.55,
                textAlign: "left",
              }}>
                <strong style={{ color: "#FFD700", fontWeight: 700 }}>Note on the 2026 NFL Draft class:</strong>{" "}
                Scores for the 2026 class were generated under an older methodology where{" "}
                <strong style={{ color: "#fff" }}>A</strong> stood for <em>Analytics</em>,{" "}
                <strong style={{ color: "#fff" }}>M</strong> for <em>Momentum (actual NFL Draft capital, post-draft)</em>, and{" "}
                <strong style={{ color: "#fff" }}>P</strong> for <em>Production (stat-specific)</em>. The 2027 and 2028 classes use the updated framework described above.
              </div>
            )}
          </div>

          {/* Last Updated */}
          <div style={{ textAlign: "center", marginTop: "15px", marginBottom: "25px", fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)", fontWeight: "500" }}>
            Last Updated: {formattedLastUpdated}
          </div>

          {/* Title */}
          <h1 style={{ textAlign: "center", marginBottom: "40px", fontSize: "3rem", fontWeight: 900, letterSpacing: "2px", textShadow: "3px 3px 12px rgba(0,0,0,0.4), 0 0 40px rgba(255,215,0,0.3)", color: "#FFFFFF" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobileView ? "12px" : "18px", marginBottom: "8px", flexWrap: "wrap" }}>
              <img
                src="/assets/amp_logo.png"
                alt="Fantasy Amp"
                style={{
                  width: isMobileView ? "64px" : "88px",
                  height: isMobileView ? "64px" : "88px",
                  borderRadius: "50%",
                  border: "2px solid rgba(255, 215, 0, 0.5)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(28, 36, 78, 1)",
                  background: "#1C244E",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
              AMPLive ⚡
            </span>
            <span style={{ display: "block", fontSize: "1.8rem", fontWeight: 700, background: "linear-gradient(90deg, #FFD700, #FFA500, #FFD700)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Dynamic Fantasy Football Rankings</span>
          </h1>

          {/* Search Bar */}
          <div style={{ maxWidth: "800px", margin: "0 auto 40px auto", position: "relative" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={{ position: "absolute", left: "20px", fontSize: "1.5rem", pointerEvents: "none", zIndex: 3 }}>🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => { setTimeout(() => setShowDropdown(false), 200); }}
                placeholder="Search players by name..."
                style={{ width: "100%", padding: "18px 60px 18px 60px", fontSize: "1.1rem", fontWeight: "500", borderRadius: showDropdown && searchResults.length > 0 ? "16px 16px 0 0" : "16px", border: "3px solid rgba(255, 255, 255, 0.3)", backgroundColor: "rgba(255, 255, 255, 0.95)", color: "#000", outline: "none", transition: "all 0.3s ease", boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)", fontFamily: "Space Grotesk" }}
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(""); setShowDropdown(false); }}
                  style={{ position: "absolute", right: "15px", background: "#fee2e2", border: "none", color: "#991b1b", width: "32px", height: "32px", borderRadius: "50%", cursor: "pointer", fontSize: "1.2rem", fontWeight: "bold", transition: "all 0.3s ease", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#991b1b"; e.currentTarget.style.color = "white"; e.currentTarget.style.transform = "rotate(90deg)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#991b1b"; e.currentTarget.style.transform = "rotate(0deg)"; }}
                  title="Clear search"
                >×</button>
              )}
            </div>
            {showDropdown && searchQuery && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", borderRadius: "0 0 16px 16px", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.3)", maxHeight: "400px", overflowY: "auto", zIndex: 999, border: "3px solid #FFD700", borderTop: "2px solid rgba(255, 215, 0, 0.3)" }}>
                {searchResults.length > 0 ? searchResults.map((player) => (
                  <div key={player.id} onClick={() => handlePlayerSelect(player)} style={{ padding: "16px 20px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", transition: "all 0.2s ease", display: "flex", alignItems: "center", gap: "12px" }} onMouseEnter={(e) => { e.currentTarget.style.background = "linear-gradient(90deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.1))"; e.currentTarget.style.paddingLeft = "24px"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.paddingLeft = "20px"; }}>
                    {player.image && (<img src={player.image} alt={player.first_name} style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover", border: "2px solid #667eea" }} />)}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "700", fontSize: "1rem", color: "#1a202c" }}>{player.first_name} {player.last_name}</div>
                      <div style={{ fontSize: "0.85rem", color: "#4a5568" }}>{player?.metrics?.position} | {player?.nfl_team || player?.metrics?.college_team} | {player?.nfl_draft_year || "—"} | {player?.draft_pick_capital || "—"}</div>
                    </div>
                    <div style={{ fontSize: "1.2rem", color: "#FFD700" }}>→</div>
                  </div>
                )) : (
                  <div style={{ padding: "20px", textAlign: "center", color: "#4a5568", fontStyle: "italic" }}>No players found matching "{searchQuery}"</div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Position Filter - Only show in Rankings mode */}
      {viewMode === 'rankings' && (
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          {positionOptions.map((pos) => (
            <button
              key={pos}
              onClick={() => {
                setFilter(pos);
                logUserAction('filter_position', { position: pos });
              }}
              style={{
                padding: "12px 24px",
                fontSize: "1.15rem",
                fontWeight: "bold",
                borderRadius: 12,
                border: filter === pos ? "3px solid #FFD700" : "2px solid rgba(255,255,255,0.3)",
                backgroundColor: filter === pos ? "#FFD700" : "rgba(255,255,255,0.15)",
                color: filter === pos ? "#000" : "#fff",
                cursor: "pointer",
                transition: "all .3s ease-in-out",
                boxShadow: filter === pos ? "0 6px 20px rgba(255,215,0,0.4)" : "0 4px 12px rgba(0,0,0,0.2)",
                transform: filter === pos ? "scale(1.05)" : "scale(1)",
              }}
              onMouseEnter={(e) => {
                if (filter !== pos) {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.25)";
                  e.currentTarget.style.transform = "scale(1.03)";
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== pos) {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Year Filter - Only show in Rankings mode */}
      {viewMode === 'rankings' && (() => {
        const prospectsLocked = false; // rankings are free for all draft classes
        const activeCategory = rankingMode === 'cff' ? 'cff' : yearFilter === 1 ? 'dynasty' : yearFilter === 2026 ? 'rookie' : 'prospect';
        const categoryBtn = (key, label, active, locked, onClick) => (
          <button
            key={key}
            onClick={locked ? undefined : onClick}
            title={locked ? "Upgrade to AMPLive" : undefined}
            style={{
              padding: "12px 28px",
              fontSize: "1.1rem",
              fontWeight: "800",
              borderRadius: 12,
              border: active ? "3px solid #FFD700" : locked ? "2px solid rgba(255,255,255,0.1)" : "2px solid rgba(255,255,255,0.3)",
              backgroundColor: active ? "#FFD700" : "rgba(255,255,255,0.12)",
              color: locked ? "rgba(255,255,255,0.3)" : active ? "#000" : "#fff",
              cursor: locked ? "not-allowed" : "pointer",
              transition: "all .3s ease-in-out",
              boxShadow: active ? "0 6px 20px rgba(255,215,0,0.4)" : "0 4px 12px rgba(0,0,0,0.2)",
              transform: active ? "scale(1.05)" : "scale(1)",
              opacity: locked ? 0.5 : 1,
            }}
            onMouseEnter={(e) => { if (!locked && !active) { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.22)"; e.currentTarget.style.transform = "scale(1.03)"; } }}
            onMouseLeave={(e) => { if (!locked && !active) { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.transform = "scale(1)"; } }}
          >
            {locked ? '🔒 ' : ''}{label}
          </button>
        );
        return (
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 12, marginBottom: (activeCategory === 'prospect' && !prospectsLocked) || activeCategory === 'cff' ? 12 : 0 }}>
              {categoryBtn('dynasty', 'Dynasty Rankings', activeCategory === 'dynasty', false, () => { setRankingMode('standard'); setYearFilter(1); setTeamFilter("All"); setP4Filter(false); setMobileVisibleRounds(4); logUserAction('filter_year', { year: 1 }); })}
              {categoryBtn('rookie', '2026 Rookie Draft', activeCategory === 'rookie', false, () => { setRankingMode('standard'); setYearFilter(2026); setTeamFilter("All"); setP4Filter(false); setMobileVisibleRounds(4); logUserAction('filter_year', { year: 2026 }); })}
              {categoryBtn('prospect', 'Prospect Rankings', activeCategory === 'prospect', prospectsLocked, () => { setRankingMode('standard'); setYearFilter(2027); setTeamFilter("All"); setP4Filter(false); setMobileVisibleRounds(4); logUserAction('filter_year', { year: 2027 }); })}
              {categoryBtn('cff', 'CFF Rankings', activeCategory === 'cff', false, () => { setRankingMode('cff'); setTeamFilter("All"); setP4Filter(false); setMobileVisibleRounds(4); logUserAction('filter_year', { mode: 'cff' }); })}
            </div>
            {/* Sub-year buttons for prospects */}
            {activeCategory === 'prospect' && !prospectsLocked && (
              <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 10 }}>
                {[2027, 2028, 2029].map(yr => (
                  <button
                    key={yr}
                    onClick={() => { setYearFilter(yr); setTeamFilter("All"); setP4Filter(false); setMobileVisibleRounds(4); logUserAction('filter_year', { year: yr }); }}
                    style={{
                      padding: "8px 20px",
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      borderRadius: 10,
                      border: yearFilter === yr ? "2px solid #FFD700" : "2px solid rgba(255,255,255,0.25)",
                      backgroundColor: yearFilter === yr ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.08)",
                      color: yearFilter === yr ? "#FFD700" : "rgba(255,255,255,0.75)",
                      cursor: "pointer",
                      transition: "all .2s ease",
                    }}
                  >
                    {yr}
                  </button>
                ))}
              </div>
            )}
            {activeCategory === 'prospect' && !prospectsLocked && (yearFilter === 2028 || yearFilter === 2029) && (
              <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", marginTop: 10, marginBottom: 0, fontStyle: "italic" }}>
                *These rankings are very fluid but here's how things look right now!
              </p>
            )}
            {activeCategory === 'cff' && (
              <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", marginTop: 10, marginBottom: 0, fontStyle: "italic" }}>
                2026 college fantasy production — P4 schools, standard scoring, all prospect classes
              </p>
            )}
          </div>
        );
      })()}

      {/* Team Filter Dropdown */}
      {viewMode === 'rankings' && (() => {
        const isProspect = yearFilter === 2027 || yearFilter === 2028 || yearFilter === 2029;
        const teamOptions = ["All", ...Array.from(new Set(
          formatAdjustedPlayers
            .filter(p => rankingMode === 'cff' ? [2027, 2028, 2029].includes(p.nfl_draft_year) : (p.nfl_draft_year === yearFilter || yearFilter === 1))
            .map(p => (rankingMode === 'cff' || isProspect) ? (p.metrics?.college_team || p.college_team || '') : (p.nfl_team || ''))
            .filter(Boolean)
            .filter(t => rankingMode === 'cff' ? P4_SCHOOLS.has(t) : (!p4Filter || P4_SCHOOLS.has(t)))
        )).sort()];
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              style={{
                padding: "10px 18px",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: 10,
                border: teamFilter !== "All" ? "2px solid #FFD700" : "2px solid rgba(255,255,255,0.3)",
                background: teamFilter !== "All" ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.1)",
                color: teamFilter !== "All" ? "#FFD700" : "#fff",
                cursor: "pointer",
                outline: "none",
                minWidth: 200,
              }}
            >
              {teamOptions.map(t => (
                <option key={t} value={t} style={{ background: "#1a1a2e", color: "#fff" }}>{t === "All" ? (isProspect ? "All Colleges" : "All NFL Teams") : t}</option>
              ))}
            </select>
            {yearFilter !== 1 && rankingMode !== 'cff' && (
              <button
                onClick={() => { setP4Filter(f => { if (!f) setTeamFilter("All"); return !f; }); }}
                style={{
                  padding: "10px 18px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  borderRadius: 10,
                  border: p4Filter ? "3px solid #FFD700" : "2px solid rgba(255,255,255,0.3)",
                  backgroundColor: p4Filter ? "#FFD700" : "rgba(255,255,255,0.15)",
                  color: p4Filter ? "#000" : "#fff",
                  cursor: "pointer",
                  transition: "all .3s ease-in-out",
                  boxShadow: p4Filter ? "0 6px 20px rgba(255,215,0,0.4)" : "0 4px 12px rgba(0,0,0,0.2)",
                }}
                onMouseEnter={(e) => { if (!p4Filter) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.25)"; }}
                onMouseLeave={(e) => { if (!p4Filter) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)"; }}
              >
                P4
              </button>
            )}
          </div>
        );
      })()}

      {/* Scoring Format Toggle */}
      {viewMode === 'rankings' && rankingMode !== 'cff' && (
      <div style={{ textAlign: "center", marginBottom: 24, display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", borderRadius: 10, overflow: "hidden", border: "2px solid rgba(255,255,255,0.3)" }}>
          {['SF', '1QB'].map(fmt => (
            <button
              key={fmt}
              onClick={() => {
                setScoringFormat(fmt);
                logUserAction('filter_scoring_format', { format: fmt });
              }}
              style={{
                padding: "10px 24px",
                fontSize: "1rem",
                fontWeight: "bold",
                border: "none",
                backgroundColor: scoringFormat === fmt ? "#FFD700" : "rgba(255,255,255,0.1)",
                color: scoringFormat === fmt ? "#000" : "#fff",
                cursor: "pointer",
                transition: "all .3s ease-in-out",
              }}
              onMouseEnter={(e) => {
                if (scoringFormat !== fmt) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)";
              }}
              onMouseLeave={(e) => {
                if (scoringFormat !== fmt) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
              }}
            >
              {fmt === 'SF' ? 'SuperFlex' : '1QB'}
            </button>
          ))}
        </div>
        <button
          onClick={() => {
            setTePremium(prev => !prev);
            logUserAction('toggle_te_premium', { enabled: !tePremium });
          }}
          style={{
            padding: "10px 24px",
            fontSize: "1rem",
            fontWeight: "bold",
            borderRadius: 10,
            border: tePremium ? "2px solid #f59e0b" : "2px solid rgba(255,255,255,0.3)",
            backgroundColor: tePremium ? "#f59e0b" : "rgba(255,255,255,0.1)",
            color: tePremium ? "#000" : "#fff",
            cursor: "pointer",
            transition: "all .3s ease-in-out",
          }}
          onMouseEnter={(e) => {
            if (!tePremium) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)";
          }}
          onMouseLeave={(e) => {
            if (!tePremium) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
          }}
        >
          TE Premium
        </button>
      </div>
      )}

      {/* Round Filter + Quick Hits + Player Grid - Only show in Rankings mode */}
      {viewMode === 'rankings' && (
      <>
      {/* Round Filter */}
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
          }}
        >
          {/* All Rounds Button */}
          {(() => {
            const roundsUnlocked = isPaid === 1 || window.location.hostname === 'localhost';
            return (
          <button
            onClick={() => {
              if (roundsUnlocked) {
                setSelectedRound(null);
                logUserAction('filter_round', { round: 'all' });
              } else {
                if (!isLoggedIn) {
                  openUpgradePrompt('all_rounds', 'Create a free account', 'Sign in to see round 2, then upgrade to AMPLive for all rounds.', true);
                } else {
                  openUpgradePrompt('all_rounds', 'Unlock all rounds with AMPLive', 'Free accounts see rounds 1 and 2. Upgrade to AMPLive for all rounds across every draft class. $10/mo, cancel anytime.');
                }
              }
            }}
            style={{
              padding: "10px 20px",
              fontSize: "1.05rem",
              fontWeight: "bold",
              borderRadius: 12,
              border: selectedRound === null ? "3px solid #FFD700" : "2px solid rgba(255,255,255,0.3)",
              backgroundColor: roundsUnlocked ? (selectedRound === null ? "#FFD700" : "rgba(255,255,255,0.15)") : "rgba(100,100,100,0.3)",
              color: roundsUnlocked ? (selectedRound === null ? "#000" : "#fff") : "rgba(255,255,255,0.5)",
              cursor: roundsUnlocked ? "pointer" : "not-allowed",
              transition: "all .3s ease-in-out",
              boxShadow: selectedRound === null ? "0 6px 20px rgba(255,215,0,0.4)" : "0 4px 12px rgba(0,0,0,0.2)",
              transform: selectedRound === null ? "scale(1.05)" : "scale(1)",
              opacity: roundsUnlocked ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (roundsUnlocked && selectedRound !== null) {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.25)";
                e.currentTarget.style.transform = "scale(1.03)";
              }
            }}
            onMouseLeave={(e) => {
              if (roundsUnlocked && selectedRound !== null) {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            title={!roundsUnlocked ? "🔒 Login to unlock" : ""}
          >
            {!roundsUnlocked && "🔒 "}All Rounds
          </button>
            );
          })()}

          {/* Round Range Selection - full range for paid; capped selector otherwise */}
          {(isPaid === 1 || window.location.hostname === 'localhost') ? (
            // Paid users: Custom range with start/end dropdowns
            <>
              <select
                value={selectedRound && selectedRound.includes("-") ? selectedRound.split("-")[0] : "1"}
                onChange={(e) => {
                  const start = e.target.value;
                  const end = selectedRound && selectedRound.includes("-") ? selectedRound.split("-")[1] : "4";
                  const newRange = `${start}-${end}`;
                  setSelectedRound(newRange);
                  logUserAction('filter_round', { round: newRange });
                }}
                style={{
                  padding: "10px 15px",
                  fontSize: "1.05rem",
                  fontWeight: "bold",
                  borderRadius: 12,
                  border: "2px solid rgba(255,255,255,0.3)",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  cursor: "pointer",
                  transition: "all .3s ease-in-out",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  outline: "none",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                <option value="1">Round 1</option>
                <option value="2">Round 2</option>
                <option value="3">Round 3</option>
                <option value="4">Round 4</option>
                <option value="5">Round 5</option>
                <option value="6">Round 6</option>
                <option value="7">Round 7</option>
                <option value="8">Round 8</option>
                <option value="9">Round 9</option>
                <option value="10">Round 10</option>
                <option value="11">Round 11</option>
                <option value="12">Round 12</option>
              </select>
              <span style={{ color: "#fff", fontWeight: "bold", fontSize: "1.2rem" }}>to</span>
              <select
                value={selectedRound && selectedRound.includes("-") ? selectedRound.split("-")[1] : "4"}
                onChange={(e) => {
                  const start = selectedRound && selectedRound.includes("-") ? selectedRound.split("-")[0] : "1";
                  const end = e.target.value;
                  const newRange = `${start}-${end}`;
                  setSelectedRound(newRange);
                  logUserAction('filter_round', { round: newRange });
                }}
                style={{
                  padding: "10px 15px",
                  fontSize: "1.05rem",
                  fontWeight: "bold",
                  borderRadius: 12,
                  border: "2px solid rgba(255,255,255,0.3)",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  cursor: "pointer",
                  transition: "all .3s ease-in-out",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  outline: "none",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                <option value="1">Round 1</option>
                <option value="2">Round 2</option>
                <option value="3">Round 3</option>
                <option value="4">Round 4</option>
                <option value="5">Round 5</option>
                <option value="6">Round 6</option>
                <option value="7">Round 7</option>
                <option value="8">Round 8</option>
                <option value="9">Round 9</option>
                <option value="10">Round 10</option>
                <option value="11">Round 11</option>
                <option value="12">Round 12</option>
              </select>
            </>
          ) : (
            // Logged-out: Round 1 only. Logged-in free: Rounds 1 and 2.
            <select
              value={selectedRound || "1"}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedRound(value);
              }}
              style={{
                padding: "10px 20px",
                fontSize: "1.05rem",
                fontWeight: "bold",
                borderRadius: 12,
                border: selectedRound ? "3px solid #FFD700" : "2px solid rgba(255,255,255,0.3)",
                backgroundColor: selectedRound ? "#FFD700" : "rgba(255,255,255,0.15)",
                color: selectedRound ? "#000" : "#fff",
                cursor: "pointer",
                transition: "all .3s ease-in-out",
                boxShadow: selectedRound ? "0 6px 20px rgba(255,215,0,0.4)" : "0 4px 12px rgba(0,0,0,0.2)",
                outline: "none",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              <option value="1">Round 1</option>
              {isLoggedIn && <option value="2">Round 2</option>}
            </select>
          )}
        </div>
      </div>

      {/* Quick Hits - Collapsible (commented out) */}
      {false && <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto 30px auto",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          border: "3px solid rgba(255,215,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Dropdown Header */}
        <button
          onClick={() => {
            const newState = !showQuickHits;
            setShowQuickHits(newState);
            logUserAction('quick_hits_toggle', { state: newState ? 'open' : 'closed' });
          }}
          style={{
            width: "100%",
            padding: "20px 40px",
            background: showQuickHits
              ? "linear-gradient(135deg, #667eea 0%, #5568d3 100%)"
              : "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
            border: "none",
            borderBottom: showQuickHits ? "2px solid rgba(255,215,0,0.3)" : "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            if (!showQuickHits) {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)";
            }
          }}
          onMouseLeave={(e) => {
            if (!showQuickHits) {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)";
            }
          }}
        >
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "800",
              margin: 0,
              color: showQuickHits ? "#fff" : "#667eea",
              transition: "color 0.3s ease",
            }}
          >
            Quick Hits
          </h2>
          <div
            style={{
              fontSize: "1.5rem",
              color: showQuickHits ? "#FFD700" : "#667eea",
              transform: showQuickHits ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.3s ease",
            }}
          >
            ▼
          </div>
        </button>

        {/* Collapsible Content */}
        {showQuickHits && (
          <div style={{ padding: "30px 40px", position: "relative" }}>
                {quickHitsLoading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#4a5568",
                      fontSize: "1rem",
                    }}
                  >
                    <div style={{ fontSize: "3rem", marginBottom: "16px" }}>⏳</div>
                    <p style={{ margin: 0, fontStyle: "italic" }}>
                      Loading latest updates...
                    </p>
                  </div>
                ) : quickHits.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#4a5568",
                      fontSize: "1rem",
                    }}
                  >
                    <div style={{ fontSize: "3rem", marginBottom: "16px" }}>📰</div>
                    <p style={{ margin: 0, fontStyle: "italic" }}>
                      No quick hits available yet
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {quickHits.map((hit, index) => (
                      <div
                        key={index}
                        style={{
                          background: "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                          border: "1px solid rgba(102, 126, 234, 0.2)",
                          borderRadius: "12px",
                          padding: "16px 20px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)";
                          e.currentTarget.style.transform = "translateX(4px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)";
                          e.currentTarget.style.transform = "translateX(0)";
                        }}
                      >
                        <div style={{ marginBottom: "8px" }}>
                          <span
                            style={{
                              fontSize: "0.8rem",
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            {new Date(hit.timestamp).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                        <p
                          style={{
                            fontSize: "1rem",
                            color: "#1a202c",
                            lineHeight: "1.6",
                            margin: 0,
                          }}
                        >
                          {hit.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
          </div>
        )}
      </div>}

      {/* How to Use AMPLive - Collapsible */}
      {false && <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto 30px auto",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          border: "3px solid rgba(255,215,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Dropdown Header */}
        <button
          onClick={() => {
            const newState = !showHowTo;
            setShowHowTo(newState);
            logUserAction('how_to_toggle', { state: newState ? 'open' : 'closed' });
          }}
          style={{
            width: "100%",
            padding: "20px 40px",
            background: showHowTo
              ? "linear-gradient(135deg, #667eea 0%, #5568d3 100%)"
              : "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
            border: "none",
            borderBottom: showHowTo ? "2px solid rgba(255,215,0,0.3)" : "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            if (!showHowTo) {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)";
            }
          }}
          onMouseLeave={(e) => {
            if (!showHowTo) {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)";
            }
          }}
        >
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "800",
              margin: 0,
              color: showHowTo ? "#fff" : "#667eea",
              transition: "color 0.3s ease",
            }}
          >
            How to Use AMPLive
          </h2>
          <div
            style={{
              fontSize: "1.5rem",
              color: showHowTo ? "#FFD700" : "#667eea",
              transform: showHowTo ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.3s ease",
            }}
          >
            ▼
          </div>
        </button>

        {/* Collapsible Content */}
        {showHowTo && (
          <div style={{ padding: "30px 40px" }}>
            <p style={{ fontSize: "1.05rem", color: "#1a202c", lineHeight: "1.8", margin: "0", fontStyle: "italic" }}>
              This tool saves you time and keeps you on the cutting edge of dynasty. You are the decision maker on prospects but with the reduction in information overload that this tool provides you can make better & more informed decisions on your team's future.
            </p>
            <div style={{ marginTop: "20px", textAlign: "right", color: "#667eea", fontSize: "0.95rem", lineHeight: "1.6" }}>
              <div style={{ fontWeight: "700", fontSize: "1.05rem", marginBottom: "4px" }}>Stay Amped!</div>
              <div style={{ fontWeight: "600" }}>John</div>
              <div style={{ fontSize: "0.85rem", color: "#4a5568" }}>Creator of Fantasy Amp</div>
              <a href="mailto:support@fantasyamp.com" style={{ color: "#667eea", textDecoration: "none", fontSize: "0.9rem" }}>support@fantasyamp.com</a>
            </div>
          </div>
        )}
      </div>}

      {/* AMP Score Legend - Collapsible (TEMPORARILY HIDDEN — flip `false` to `true` to re-enable) */}
      {false && (
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto 40px auto",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          border: "3px solid rgba(255,215,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Dropdown Header */}
        <button
          onClick={() => {
            const newState = !showLegend;
            setShowLegend(newState);
            logUserAction('legend_toggle', { state: newState ? 'open' : 'closed' });
          }}
          style={{
            width: "100%",
            padding: "24px 40px",
            background: showLegend
              ? "linear-gradient(135deg, #667eea 0%, #5568d3 100%)"
              : "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
            border: "none",
            borderBottom: showLegend ? "2px solid rgba(255,215,0,0.3)" : "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            if (!showLegend) {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)";
            }
          }}
          onMouseLeave={(e) => {
            if (!showLegend) {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)";
            }
          }}
        >
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: "800",
              margin: 0,
              color: showLegend ? "#fff" : "#667eea",
              transition: "color 0.3s ease",
            }}
          >
            Learn More About AMP Scores
          </h2>
          <div
            style={{
              fontSize: "2rem",
              color: showLegend ? "#FFD700" : "#667eea",
              transform: showLegend ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.3s ease",
            }}
          >
            ▼
          </div>
        </button>

        {/* Collapsible Content */}
        {showLegend && (
          <div style={{ padding: "32px 20px" }}>
            {/* AMP Component Scores Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
          {/* Analytics Card */}
          <div
            style={{
              background: "rgba(102, 126, 234, 0.08)",
              border: "2px solid rgba(102, 126, 234, 0.3)",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>🏹</div>
            <h3
              style={{
                fontSize: "1.2rem",
                fontWeight: "700",
                marginBottom: "6px",
                color: "#667eea",
              }}
            >
              A — Adjusted Competition
            </h3>
            <p style={{ fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.5", margin: 0 }}>
              Advanced metrics: efficiency, technical performance
            </p>
          </div>

          {/* Momentum Card */}
          <div
            style={{
              background: "rgba(255, 165, 0, 0.08)",
              border: "2px solid rgba(255, 165, 0, 0.3)",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>🔥</div>
            <h3
              style={{
                fontSize: "1.2rem",
                fontWeight: "700",
                marginBottom: "6px",
                color: "#FFA500",
              }}
            >
              M — Momentum
            </h3>
            <p style={{ fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.5", margin: 0 }}>
              Rising stock, draft buzz, projected usage
            </p>
          </div>

          {/* Production Card */}
          <div
            style={{
              background: "rgba(34, 197, 94, 0.08)",
              border: "2px solid rgba(34, 197, 94, 0.3)",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>📊</div>
            <h3
              style={{
                fontSize: "1.2rem",
                fontWeight: "700",
                marginBottom: "6px",
                color: "#22c55e",
              }}
            >
              P — Production
            </h3>
            <p style={{ fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.5", margin: 0 }}>
              Elite college stats, proven dominance
            </p>
          </div>
        </div>

        {/* Total Score Section */}
        <div
          style={{
            background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)",
            border: "2px solid #667eea",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "32px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <div style={{ fontSize: "2rem" }}>⚡</div>
            <h3
              style={{
                fontSize: "1.3rem",
                fontWeight: "700",
                margin: 0,
                color: "#667eea",
              }}
            >
              AMP Overall Score
            </h3>
          </div>
          <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.6", marginBottom: "12px" }}>
            Weighted composite score combining all three components:
          </p>
          <div style={{
            fontSize: "0.9rem",
            color: "#1a202c",
            lineHeight: "1.8",
            paddingLeft: "20px"
          }}>
            <div>• <strong>Momentum:</strong> 55% weight</div>
            <div>• <strong>Production:</strong> 35% weight</div>
            <div>• <strong>Adjusted Competition:</strong> 10% weight</div>
          </div>
          <p style={{ fontSize: "0.85rem", color: "#4a5568", marginTop: "12px", fontStyle: "italic", margin: 0 }}>
            Scores range from 0-100. Elite prospects typically score 85+.
          </p>
        </div>

        {/* Rank Change Indicators */}
        <div style={{ marginTop: "24px" }}>
          <h3
            style={{
              fontSize: "1.3rem",
              fontWeight: "700",
              marginBottom: "16px",
              color: "#667eea",
              textAlign: "center",
            }}
          >
            📊 Rank Change Indicators
          </h3>
          <p
            style={{
              fontSize: "0.9rem",
              color: "#4a5568",
              lineHeight: "1.6",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            Track weekly movement in player rankings
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "12px",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            {[
              { emoji: "🚀", label: "Huge Rise", change: "+20+" },
              { emoji: "📈", label: "Strong Rise", change: "+10 to +19" },
              { emoji: "⬆️", label: "Rising", change: "+5 to +9" },
              { emoji: "⚓", label: "Steady", change: "-4 to +4" },
              { emoji: "⬇️", label: "Falling", change: "-5 to -9" },
              { emoji: "📉", label: "Strong Fall", change: "-10 to -19" },
              { emoji: "💀", label: "Huge Fall", change: "-20+" },
            ].map((item) => (
              <div
                key={item.emoji}
                style={{
                  background: "rgba(102, 126, 234, 0.05)",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                  borderRadius: "10px",
                  padding: "12px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "1.5rem", marginBottom: "6px" }}>
                  {item.emoji}
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    color: "#1a202c",
                    marginBottom: "2px",
                  }}
                >
                  {item.label}
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#4a5568",
                  }}
                >
                  {item.change}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Tips */}
        <div
          style={{
            marginTop: "28px",
            textAlign: "center",
            padding: "20px",
            background: "rgba(255, 215, 0, 0.08)",
            borderRadius: "10px",
            border: "1px solid rgba(255, 215, 0, 0.3)",
          }}
        >
          <p
            style={{
              fontSize: "0.95rem",
              color: "#1a202c",
              lineHeight: "1.6",
              margin: 0,
              fontWeight: "600",
            }}
          >
            💡 Click player names for full profiles • Rankings update weekly
          </p>
        </div>
          </div>
        )}
      </div>
      )}

      {/* Quick Legend */}
      {viewMode === 'rankings' && (
        <div style={{ maxWidth: "1400px", margin: "0 auto 16px auto", padding: "0 20px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "stretch", flexWrap: "wrap" }}>
          <button
            onClick={() => setShowQuickLegend(p => !p)}
            style={{
              flex: 1,
              minWidth: "220px",
              padding: "10px 18px",
              background: showQuickLegend ? "rgba(255,215,0,0.1)" : "rgba(255,255,255,0.06)",
              border: showQuickLegend ? "1px solid rgba(255,215,0,0.4)" : "1px solid rgba(255,255,255,0.12)",
              borderRadius: showQuickLegend ? "12px 12px 0 0" : "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.2s ease",
            }}
          >
            <span style={{ fontSize: "0.85rem", fontWeight: "700", color: showQuickLegend ? "#FFD700" : "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "1rem" }}>💡</span> How to use these rankings
            </span>
            <span style={{ color: showQuickLegend ? "#FFD700" : "rgba(255,255,255,0.4)", fontSize: "0.75rem", transform: showQuickLegend ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>▼</span>
          </button>
          <div
            onClick={() => {
              setHideAmpScores(prev => !prev);
              logUserAction('toggle_hide_amp_scores', { enabled: !hideAmpScores });
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              background: hideAmpScores ? "rgba(102,126,234,0.15)" : "rgba(255,255,255,0.06)",
              border: hideAmpScores ? "1px solid #667eea" : "1px solid rgba(255,255,255,0.12)",
              borderRadius: "12px",
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
              transition: "all 0.2s ease",
            }}
          >
            <span style={{ fontSize: "0.85rem", fontWeight: "700", color: hideAmpScores ? "#a5b4fc" : "rgba(255,255,255,0.6)" }}>
              {hideAmpScores ? "Show AMP Scores" : "Hide AMP Scores"}
            </span>
            <div style={{
              width: "36px",
              height: "20px",
              borderRadius: "10px",
              background: hideAmpScores ? "rgba(102,126,234,0.5)" : "rgba(255,255,255,0.15)",
              border: hideAmpScores ? "1px solid #667eea" : "1px solid rgba(255,255,255,0.3)",
              position: "relative",
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}>
              <div style={{
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                background: hideAmpScores ? "#667eea" : "rgba(255,255,255,0.5)",
                position: "absolute",
                top: "2px",
                left: hideAmpScores ? "19px" : "2px",
                transition: "all 0.2s ease",
              }} />
            </div>
          </div>
          </div>
          {showQuickLegend && (
            <div style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,215,0,0.4)",
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}>
              {[
                { icon: "🔗", text: "Player names link to their individual profile pages on FantasyAmp.com" },
                { icon: "📈", text: "Emojis next to a player (🔥⬆️⬇️) indicate a recent ranking change" },
                { icon: "⚖️", text: "Compare players by clicking their positional rank badge (e.g. WR1) to select, then click another" },
                { icon: "⚡", text: "AMP Scores apply to rookies and prospects only; see the dropdown at the top of the page for details" },
                { icon: "🕐", text: "These rankings update constantly; check the Last Updated date at the top of the page to see how fresh they are" },
              ].map(({ icon, text }) => (
                <div key={text} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: "1rem", flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <span style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ROUND GRID OR TRANSFER PORTAL GRID */}
<div style={{ marginTop: 30, padding: 16, borderRadius: 10 }}>
  {/* TRANSFER PORTAL SECTION - COMMENTED OUT
  {yearFilter === "Transfer Portal" ? (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 20,
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        {baseFiltered
          .sort((a, b) => {
            const ratingA = a.amp_star_rating || 0;
            const ratingB = b.amp_star_rating || 0;
            return ratingB - ratingA;
          })
          .map((player) => {
          const isSelected = selectedPlayers.find((p) => p.id === player.id);
          const isHighlighted = highlightedPlayer === player.id;
          return (
            <div
              key={player.id}
              id={`player-${player.id}`}
              style={{
                position: "relative",
                background: isSelected
                  ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                  : isHighlighted
                  ? "linear-gradient(135deg, #667eea 0%, #5568d3 100%)"
                  : (player?.nfl_team_image || player?.metrics?.team_logo)
                  ? `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%), url(${player.nfl_team_image || player.metrics.team_logo})`
                  : "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
                backgroundSize: "cover, cover",
                backgroundPosition: "center, center",
                backgroundRepeat: "no-repeat, no-repeat",
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: isSelected
                  ? "0 12px 40px rgba(255, 215, 0, 0.6)"
                  : isHighlighted
                  ? "0 12px 40px rgba(102, 126, 234, 0.6)"
                  : "0 8px 24px rgba(0,0,0,0.35)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                transform: isSelected ? "scale(1.05)" : isHighlighted ? "scale(1.03)" : "scale(1)",
                border: isSelected ? "3px solid #FFD700" : "2px solid rgba(102, 126, 234, 0.3)",
              }}
              onClick={() => togglePlayerSelection(player)}
              onMouseEnter={(e) => {
                if (!isSelected && !isHighlighted) {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow = "0 12px 32px rgba(102, 126, 234, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected && !isHighlighted) {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.35)";
                }
              }}
            >
              <div style={{ padding: "16px 12px" }}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  {player.image && (
                    <img
                      src={player.image}
                      alt={player.first_name}
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      }}
                    />
                  )}
                  {(player?.nfl_team_image || player?.metrics?.team_logo) && (
                    <img
                      src={player.nfl_team_image || player.metrics.team_logo}
                      alt="team"
                      style={{
                        width: 35,
                        height: 35,
                        objectFit: "contain",
                      }}
                    />
                  )}
                </div>

                <h3 style={{ fontSize: "0.95rem", fontWeight: "800", textAlign: "center", margin: "0 0 4px 0", color: "#1a202c", lineHeight: 1.2 }}>
                  {player.first_name}
                </h3>
                <h3 style={{ fontSize: "0.95rem", fontWeight: "800", textAlign: "center", margin: "0 0 12px 0", color: "#1a202c", lineHeight: 1.2 }}>
                  {player.last_name}
                </h3>

                <div style={{ fontSize: "0.8rem", color: "#667eea", textAlign: "center", marginBottom: 8, fontWeight: "700" }}>
                  {player?.metrics?.position || "—"}
                </div>

                <div style={{ fontSize: "0.75rem", color: "#4a5568", textAlign: "center", marginBottom: 8, fontWeight: "600" }}>
                  {player?.height_str || "—"} • {player?.metrics?.weight ? `${player.metrics.weight} lbs` : "—"}
                </div>

                <div style={{ fontSize: "0.75rem", color: "#4a5568", textAlign: "center", fontWeight: "600" }}>
                  {player?.nfl_draft_year || "—"}
                </div>

                {player?.amp_star_rating && (
                  <div
                    style={{
                      marginTop: 12,
                      fontSize: "1.2rem",
                      textAlign: "center",
                      fontWeight: "700",
                      display: "flex",
                      justifyContent: "center",
                      gap: "3px",
                    }}
                  >
                    {(() => {
                      const rating = player.amp_star_rating;
                      const fullStars = Math.floor(rating);
                      const hasHalf = (rating % 1) >= 0.5;
                      const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

                      const filledStarStyle = {
                        background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        filter: "drop-shadow(0 2px 4px rgba(102, 126, 234, 0.4))",
                        textShadow: "0 1px 2px rgba(102, 126, 234, 0.3)",
                      };

                      const emptyStarStyle = {
                        color: "#e5e7eb",
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                      };

                      return (
                        <>
                          {[...Array(fullStars)].map((_, i) => (
                            <span key={`full-${i}`} style={filledStarStyle}>★</span>
                          ))}

                          {hasHalf && (
                            <span style={{ position: "relative", display: "inline-block" }}>
                              <span style={emptyStarStyle}>★</span>
                              <span
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  width: "50%",
                                  overflow: "hidden",
                                  ...filledStarStyle,
                                }}
                              >
                                ★
                              </span>
                            </span>
                          )}

                          {[...Array(emptyStars)].map((_, i) => (
                            <span key={`empty-${i}`} style={emptyStarStyle}>★</span>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : ( */}
  {/* CFF Flat Display */}
  {rankingMode === 'cff' && (
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobileView ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
      gap: isMobileView ? 8 : 20,
      maxWidth: "1400px",
      margin: "0 auto",
      padding: isMobileView ? "0 6px" : "0 20px",
    }}>
      {baseFiltered.map((player) => {
        const isSelected = selectedPlayers.find((p) => p.id === player.id);
        const isHighlighted = highlightedPlayer === player.id;
        const posColor = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' }[player.metrics?.position] || '#667eea';
        const classLabel = { 2027: "'27", 2028: "'28", 2029: "'29" }[player.nfl_draft_year] || '';
        return (
          <div
            key={player.id}
            id={`player-${player.id}`}
            style={{
              background: isHighlighted ? "#fffbeb" : "linear-gradient(180deg, rgba(102,126,234,0.1) 0%, #fff 38%)",
              border: isHighlighted ? "3px solid #FFA500" : (isSelected ? "3px solid #FFD700" : "2px solid rgba(255,215,0,0.45)"),
              borderRadius: isMobileView ? 10 : 16,
              padding: isMobileView ? 8 : 16,
              textAlign: "center",
              boxShadow: isHighlighted ? "0 0 30px rgba(255,165,0,0.6)" : "0 2px 8px rgba(255,215,0,0.08), 0 8px 24px rgba(0,0,0,0.12)",
              transition: "all .3s ease",
              cursor: "pointer",
              position: "relative",
            }}
            onMouseEnter={(e) => { if (!isSelected && !isHighlighted) { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(16,185,129,0.25), 0 12px 32px rgba(0,0,0,0.15)"; e.currentTarget.style.border = "2px solid #10b981"; } }}
            onMouseLeave={(e) => { if (!isSelected && !isHighlighted) { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(255,215,0,0.08), 0 8px 24px rgba(0,0,0,0.12)"; e.currentTarget.style.border = "2px solid rgba(255,215,0,0.45)"; } }}
            onClick={() => togglePlayerSelection(player)}
          >
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              fontSize: isMobileView ? 9 : 12,
              fontWeight: 800,
              textAlign: 'center',
              padding: isMobileView ? '3px 4px' : '5px 8px',
              margin: isMobileView ? '-8px -8px 6px -8px' : '-16px -16px 10px -16px',
              borderRadius: isMobileView ? '8px 8px 0 0' : '14px 14px 0 0',
              letterSpacing: '0.8px',
            }}>
              CFF #{player.cff_rank} · {classLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: isMobileView ? 4 : 8, marginBottom: isMobileView ? 4 : 10 }}>
              {player.image && (
                <img src={player.image} alt={player.name} style={{ width: isMobileView ? 32 : 60, height: isMobileView ? 32 : 60, borderRadius: "50%", objectFit: "cover", boxShadow: "0 0 8px rgba(0,0,0,0.2)", flexShrink: 0 }} onError={(e) => (e.currentTarget.style.display = "none")} />
              )}
              {player.team_image && (
                <img src={player.team_image} alt="" style={{ width: isMobileView ? 24 : 40, height: isMobileView ? 24 : 40, objectFit: "contain", flexShrink: 0 }} onError={(e) => (e.currentTarget.style.display = "none")} />
              )}
            </div>
            <a href={`https://fantasyamp.com/player/${player.first_name.toLowerCase()}_${player.last_name.toLowerCase()}`} style={{ textDecoration: "none", color: "inherit", cursor: "pointer", display: "block", marginBottom: 6 }}>
              <div style={{ fontSize: isMobileView ? "0.75rem" : "1rem", fontWeight: "800", color: "#1a202c", lineHeight: isMobileView ? "1.1" : "1.4" }}>{player.first_name}</div>
              <div style={{ fontSize: isMobileView ? "0.75rem" : "1rem", fontWeight: "800", color: "#1a202c", lineHeight: isMobileView ? "1.1" : "1.4" }}>{player.last_name}</div>
            </a>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
              <span style={{ fontSize: isMobileView ? "0.65rem" : "0.8rem", fontWeight: "800", color: "#fff", background: posColor, padding: isMobileView ? "2px 6px" : "3px 8px", borderRadius: "6px" }}>
                {player.metrics?.position}
              </span>
              {posRankMap[player.id] && (
                <span style={{ fontSize: isMobileView ? "0.6rem" : "0.72rem", color: "#667eea", fontWeight: "600" }}>
                  {player.metrics?.position}{posRankMap[player.id]}
                </span>
              )}
            </div>
            <div style={{ fontSize: isMobileView ? "0.65rem" : "0.8rem", color: "#4169e1", fontWeight: 600, textAlign: "center", marginTop: 2 }}>
              {player.metrics?.college_team || player.college_team}
            </div>
            {player.amp_production_score && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: isMobileView ? 4 : 6 }}>
                <span style={{ fontSize: isMobileView ? "0.6rem" : "0.72rem", fontWeight: "700", color: "#fff", background: "#10b981", padding: "2px 7px", borderRadius: "4px" }}>
                  P {player.amp_production_score}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  )}

  {/* Normal Round-Based Display */}
  {rankingMode !== 'cff' && (
    Object.entries(playersByRound)
    .filter(([round]) => Number(round) <= 12)  // Allow up to 12 rounds for paid users
    .filter(([round]) => {
      // Round access: logged-out see round 1; logged-in free see rounds 1-2; paid see all.
      return (isPaid === 1 || window.location.hostname === 'localhost') ? true : (isLoggedIn ? Number(round) <= 2 : Number(round) === 1);
    })
    .map(([round, subgroups]) => {
      // Handle both single round and round range filtering
      if (selectedRound) {
        if (selectedRound.includes("-")) {
          // Range filtering (e.g., "1-4")
          const [start, end] = selectedRound.split("-").map(Number);
          const roundNum = Number(round);
          if (roundNum < start || roundNum > end) return null;
        } else {
          // Single round filtering
          if (String(selectedRound) !== String(round)) return null;
        }
      }

      // Mobile: limit visible rounds when no specific round is selected
      if (isMobileView && !selectedRound && Number(round) > mobileVisibleRounds) return null;

          const totalPlayers = Object.values(subgroups).flat().length;
          if (totalPlayers === 0) return null;

return (
  <div key={round} style={{ marginBottom: 30 }}>
              {/* Early / Mid / Late groups */}
              {Object.entries(subgroups).map(([label, group]) => {
                if (group.length === 0) return null;
                return (
                  <div key={label} style={{ marginBottom: 40 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobileView ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
                        gap: isMobileView ? 8 : 20,
                        maxWidth: "1400px",
                        margin: "0 auto",
                        padding: isMobileView ? "0 6px" : "0 20px",
                      }}
                    >
                      {group.map((player) => {
                        const isSelected = selectedPlayers.find((p) => p.id === player.id);
                        const isHighlighted = highlightedPlayer === player.id;
                        const posColor = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' }[player.metrics?.position] || '#667eea';
                        return (
                        <div
                          key={player.id}
                          id={`player-${player.id}`}
                          style={{
                            background: isHighlighted ? "#fffbeb" : (player.drafted ? "#ffe5e5" : "linear-gradient(180deg, rgba(102,126,234,0.1) 0%, #fff 38%)"),
                            border: isHighlighted ? "3px solid #FFA500" : (isSelected ? "3px solid #FFD700" : "2px solid rgba(255,215,0,0.45)"),
                            borderRadius: isMobileView ? 10 : 16,
                            padding: isMobileView ? 8 : 16,
                            textAlign: "center",
                            boxShadow: isHighlighted
                              ? "0 0 30px rgba(255,165,0,0.6)"
                              : isSelected
                                ? "0 12px 32px rgba(255,215,0,0.5)"
                                : "0 2px 8px rgba(255,215,0,0.08), 0 8px 24px rgba(0,0,0,0.12)",
                            transition: "all .3s ease",
                            cursor: "pointer",
                            position: "relative",
                            transform: isHighlighted ? "scale(1.05)" : "scale(1)",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected && !isHighlighted) {
                              e.currentTarget.style.transform = "translateY(-6px)";
                              e.currentTarget.style.boxShadow = "0 8px 28px rgba(184,134,11,0.35), 0 12px 32px rgba(0,0,0,0.15)";
                              e.currentTarget.style.border = "2px solid #B8860B";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected && !isHighlighted) {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(255,215,0,0.08), 0 8px 24px rgba(0,0,0,0.12)";
                              e.currentTarget.style.border = "2px solid rgba(255,215,0,0.45)";
                            } else if (isHighlighted) {
                              e.currentTarget.style.transform = "scale(1.05)";
                            }
                          }}
                        >
                          {player.nfl_draft_year === 2026 && (
                            <div style={{
                              background: (() => {
                                const r = Number(player.nfl_draft_round);
                                if (r === 1) return 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)';
                                if (r === 2) return 'linear-gradient(135deg, #D8D8D8 0%, #A8A8A8 100%)';
                                return getDraftRoundColor(player.nfl_draft_round);
                              })(),
                              color: [1, 2].includes(Number(player.nfl_draft_round)) ? '#4B0082' : '#fff',
                              fontSize: isMobileView ? 9 : 12,
                              fontWeight: 800,
                              textAlign: 'center',
                              padding: isMobileView ? '3px 4px' : '5px 8px',
                              margin: isMobileView ? '-8px -8px 6px -8px' : '-16px -16px 10px -16px',
                              borderRadius: isMobileView ? '8px 8px 0 0' : '14px 14px 0 0',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              textShadow: [1, 2].includes(Number(player.nfl_draft_round)) ? 'none' : '0 1px 2px rgba(0,0,0,0.4)',
                              boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.15)',
                            }}>
                              {!player.nfl_draft_round || player.nfl_draft_round === 0 || player.nfl_draft_round === 'UDFA'
                                ? 'Undrafted'
                                : `Rd ${player.nfl_draft_round} | Pick ${player.nfl_draft_selection}`}
                            </div>
                          )}
                          {/* Side-by-side images */}
                          {!isBasicMode && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: isMobileView ? 4 : 8,
                              marginBottom: isMobileView ? 4 : 10,
                            }}
                          >
                            {player.image && (
                              <img
                                src={player.image}
                                alt={player.name}
                                style={{
                                  width: isMobileView ? 32 : 60,
                                  height: isMobileView ? 32 : 60,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  boxShadow: "0 0 8px rgba(0,0,0,0.2)",
                                  flexShrink: 0,
                                }}
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            )}

                            {(player.nfl_team_image || player.team_image) && (
                              <img
                                src={player.nfl_team_image || player.team_image}
                                alt={`${player.nfl_team || player.team} logo`}
                                style={{
                                  width: isMobileView ? 22 : 40,
                                  height: isMobileView ? 22 : 40,
                                  objectFit: "contain",
                                }}
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            )}
                          </div>
                          )}

                          {/* Player Name - Two Lines */}
                          <a
                            href={`https://fantasyamp.com/player/${player.first_name.toLowerCase()}_${player.last_name.toLowerCase()}`}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              cursor: "pointer",
                              display: "block",
                              marginBottom: 8,
                            }}
                          >
                            <div
                              style={{
                                fontSize: isMobileView ? "0.75rem" : "1rem",
                                fontWeight: "800",
                                color: "#1a202c",
                                textAlign: "center",
                                lineHeight: isMobileView ? "1.1" : "1.4",
                              }}
                            >
                              {player.first_name}
                            </div>
                            <div
                              style={{
                                fontSize: isMobileView ? "0.75rem" : "1rem",
                                fontWeight: "800",
                                color: "#1a202c",
                                textAlign: "center",
                                lineHeight: isMobileView ? "1.1" : "1.4",
                              }}
                            >
                              {player.last_name}
                            </div>
                          </a>

                          {isBasicMode && (
                            <div
                              style={{
                                textAlign: "center",
                                fontSize: isMobileView ? "0.65rem" : "0.85rem",
                                color: "#667eea",
                                fontWeight: "700",
                              }}
                            >
                              {player.draft_pick_capital} {player?.positionRank ?? ""}
                            </div>
                          )}

                          {!isBasicMode && (<>
                          {/* Draft Capital + Momentum - Primary Info */}
                          {player?.draft_pick_capital && (
                            <div
                              style={{
                                textAlign: "center",
                                marginBottom: isMobileView ? 3 : 6,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: isMobileView ? "4px" : "8px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: isMobileView ? "0.65rem" : "0.85rem",
                                  fontWeight: "700",
                                  color: "#fff",
                                  background: "#667eea",
                                  padding: isMobileView ? "2px 6px" : "4px 10px",
                                  borderRadius: "6px",
                                }}
                              >
                                {player.draft_pick_capital}
                              </span>
                              {getRankChangeEmoji(player.rank, player.prior_rank) && (
                                <span style={{ fontSize: isMobileView ? "0.8rem" : "1.1rem" }}>
                                  {getRankChangeEmoji(player.rank, player.prior_rank)}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Team - NFL team if available, otherwise college */}
                          {(player?.nfl_team || player?.metrics?.college_team) && (
                            <div
                              style={{
                                fontSize: isMobileView ? "0.65rem" : "0.8rem",
                                color: "#4169e1",
                                fontWeight: 600,
                                textAlign: "center",
                                marginBottom: isMobileView ? 2 : 4,
                              }}
                            >
                              {player.nfl_team || player.metrics.college_team}
                            </div>
                          )}

                          {/* Physical Stats - Tertiary Info (hidden on mobile) */}
                          {!isMobileView && (player?.height_str || player?.metrics?.weight) && (
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: "#666",
                                textAlign: "center",
                                marginBottom: 6,
                              }}
                            >
                              {player?.height_str && <span>{player.height_str}</span>}
                              {player?.metrics?.weight && (
                                <span>
                                  {player.height_str ? " • " : ""}
                                  {player.metrics.weight} lbs
                                </span>
                              )}
                            </div>
                          )}

                          {!isMobileView && player?.draft_date_age != null && <div
                            style={{
                              fontSize: "0.75rem",
                              color: posColor,
                              fontWeight: "bold",
                              fontStyle: "italic",
                              textAlign: "center",
                              marginBottom: 6,
                            }}
                          >
                            🎂 {Number(player.draft_date_age).toFixed(1)}
                          </div>}

                          {/* Position Rank + Compare */}
                          <div style={{
                            fontSize: isMobileView ? ".7rem" : ".8rem",
                            color: "#333",
                            lineHeight: "1.4",
                          }}>
                            <div style={{ marginBottom: isMobileView ? 4 : 10, display: "flex", justifyContent: "center" }}>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePlayerSelection(player);
                                }}
                                style={{
                                  fontSize: isMobileView ? "0.7rem" : "0.85rem",
                                  fontWeight: "700",
                                  color: isSelected ? "#FFD700" : posColor,
                                  textAlign: "center",
                                  marginBottom: isMobileView ? 2 : 6,
                                  cursor: "pointer",
                                  padding: isMobileView ? "2px 6px" : "4px 8px",
                                  borderRadius: "6px",
                                  background: isSelected ? "rgba(255, 215, 0, 0.1)" : `${posColor}0d`,
                                  border: isSelected ? "2px solid #FFD700" : `2px solid ${posColor}40`,
                                  transition: "all 0.2s ease",
                                  display: "inline-block",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = "rgba(102, 126, 234, 0.1)";
                                    e.currentTarget.style.borderColor = "#667eea";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = "rgba(102, 126, 234, 0.05)";
                                    e.currentTarget.style.borderColor = "rgba(102, 126, 234, 0.2)";
                                  }
                                }}
                              >
                                {player?.positionRank ?? "—"} {isSelected ? "✓" : ""}
                              </div>
                            </div> {/* Close marginBottom wrapper */}

                            {/* AMP Scores: free for logged-in users. Logged-out users see them blurred + locked. */}
                            {!hideAmpScores && (player?.amp_analytics_score != null || player?.amp_momentum_score != null || player?.amp_production_score != null) && (
                              (isLoggedIn || window.location.hostname === 'localhost') ? (
                              <div
                                style={{
                                  marginTop: 8,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "6px",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                {/* Analytics */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    title="Adjusted Competition Score"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.65rem",
                                      fontWeight: "700",
                                      color: "#fff",
                                      boxShadow: "0 2px 4px rgba(102, 126, 234, 0.3)",
                                    }}
                                  >
                                    A
                                  </div>
                                  <div
                                    style={{
                                      background:
                                        player?.amp_analytics_score >= 80
                                          ? "#d1fae5"
                                          : player?.amp_analytics_score >= 60
                                          ? "#fef9c3"
                                          : "#fee2e2",
                                      color:
                                        player?.amp_analytics_score >= 80
                                          ? "#065f46"
                                          : player?.amp_analytics_score >= 60
                                          ? "#92400e"
                                          : "#991b1b",
                                      padding: "2px 8px",
                                      borderRadius: "8px",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      minWidth: "28px",
                                      textAlign: "center",
                                      border: "1.5px solid #667eea",
                                    }}
                                  >
                                    {player?.amp_analytics_score}
                                  </div>
                                </div>

                                {/* Momentum */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    title="Momentum Score"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.65rem",
                                      fontWeight: "700",
                                      color: "#fff",
                                      boxShadow: "0 2px 4px rgba(102, 126, 234, 0.3)",
                                    }}
                                  >
                                    M
                                  </div>
                                  <div
                                    style={{
                                      background:
                                        player?.amp_momentum_score >= 80
                                          ? "#d1fae5"
                                          : player?.amp_momentum_score >= 60
                                          ? "#fef9c3"
                                          : "#fee2e2",
                                      color:
                                        player?.amp_momentum_score >= 80
                                          ? "#065f46"
                                          : player?.amp_momentum_score >= 60
                                          ? "#92400e"
                                          : "#991b1b",
                                      padding: "2px 8px",
                                      borderRadius: "8px",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      minWidth: "28px",
                                      textAlign: "center",
                                      border: "1.5px solid #667eea",
                                    }}
                                  >
                                    {player?.amp_momentum_score}
                                  </div>
                                </div>

                                {/* Production */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    title="Production Score"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.65rem",
                                      fontWeight: "700",
                                      color: "#fff",
                                      boxShadow: "0 2px 4px rgba(102, 126, 234, 0.3)",
                                    }}
                                  >
                                    P
                                  </div>
                                  <div
                                    style={{
                                      background:
                                        player?.amp_production_score >= 80
                                          ? "#d1fae5"
                                          : player?.amp_production_score >= 60
                                          ? "#fef9c3"
                                          : "#fee2e2",
                                      color:
                                        player?.amp_production_score >= 80
                                          ? "#065f46"
                                          : player?.amp_production_score >= 60
                                          ? "#92400e"
                                          : "#991b1b",
                                      padding: "2px 8px",
                                      borderRadius: "8px",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      minWidth: "28px",
                                      textAlign: "center",
                                      border: "1.5px solid #667eea",
                                    }}
                                  >
                                    {player?.amp_production_score}
                                  </div>
                                </div>

                                {/* Composite AMP Score (summary) */}
                                {(() => {
                                  const composite = calculateCompositeAmpScore(player);
                                  return (
                                    <div style={{ width: "100%", marginTop: "5px", paddingTop: "7px", borderTop: "1px solid rgba(102,126,234,0.22)", display: "flex", justifyContent: "center" }}>
                                      <div
                                        title="Composite AMP Score (55% Momentum, 35% Production, 10% Adjusted Competition)"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "7px",
                                          background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
                                          borderRadius: "9px",
                                          padding: "3px 12px",
                                          boxShadow: "0 2px 6px rgba(102,126,234,0.35)",
                                        }}
                                      >
                                        <span style={{ fontSize: "0.52rem", fontWeight: 800, color: "rgba(255,255,255,0.85)", letterSpacing: "0.8px" }}>AMP</span>
                                        <span style={{ fontSize: "0.98rem", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{Math.round(parseFloat(composite))}</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                              ) : (
                              <div
                                onClick={() => openUpgradePrompt('amp_scores', 'AMP Scores are free with an account', 'Create a free account to see Adjusted Competition, Momentum, and Production for every player across all four draft classes.', true)}
                                title="Create a free account to see AMP Scores"
                                style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: "6px", alignItems: "center", width: "100%", cursor: "pointer" }}
                              >
                                {['A', 'M', 'P'].map((letter) => (
                                  <div key={letter} style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", width: "100%" }}>
                                    <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: "700", color: "#fff", boxShadow: "0 2px 4px rgba(102, 126, 234, 0.3)" }}>{letter}</div>
                                    <div style={{ background: "#e5e7eb", color: "#374151", padding: "2px 8px", borderRadius: "8px", fontSize: "0.7rem", fontWeight: 600, minWidth: "28px", textAlign: "center", border: "1.5px solid #667eea", filter: "blur(4px)", userSelect: "none" }}>88</div>
                                  </div>
                                ))}
                                <div style={{ width: "100%", marginTop: "5px", paddingTop: "7px", borderTop: "1px solid rgba(102,126,234,0.22)", display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "7px", background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)", borderRadius: "9px", padding: "3px 11px", filter: "blur(4px)", userSelect: "none" }}>
                                    <span style={{ fontSize: "0.52rem", fontWeight: 800, color: "rgba(255,255,255,0.85)", letterSpacing: "0.8px" }}>AMP</span>
                                    <span style={{ fontSize: "0.98rem", fontWeight: 900, color: "#fff", lineHeight: 1 }}>88</span>
                                  </div>
                                  <div style={{ fontSize: "0.6rem", fontWeight: 700, color: "#FFD700", letterSpacing: "0.3px" }}>🔒 Unlock</div>
                                </div>
                              </div>
                              )
                            )}

                            {!isMobileView && player?.consensus_tier && (
                              <div
                                style={{
                                  marginTop: 2,
                                  fontSize: ".75rem",
                                  color: "#000",
                                }}
                              >
                                Consensus: {player.consensus_tier}
                              </div>
                            )}

                            {/* AMP Star Rating (Disabled) */}
                            {false && player?.amp_star_rating && (
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: "1.2rem",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: "3px",
                                }}
                              >
                                {(() => {
                                  const rating = player.amp_star_rating;
                                  const fullStars = Math.floor(rating);
                                  const hasHalf = (rating % 1) >= 0.5;
                                  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

                                  // 3D star styles with AMP purple gradient
                                  const filledStarStyle = {
                                    background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                    filter: "drop-shadow(0 2px 4px rgba(102, 126, 234, 0.4))",
                                    textShadow: "0 1px 2px rgba(102, 126, 234, 0.3)",
                                  };

                                  const emptyStarStyle = {
                                    color: "#e5e7eb",
                                    filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                                  };

                                  return (
                                    <>
                                      {/* Full stars */}
                                      {[...Array(fullStars)].map((_, i) => (
                                        <span key={`full-${i}`} style={filledStarStyle}>★</span>
                                      ))}

                                      {/* Half star - using clipped full star */}
                                      {hasHalf && (
                                        <span style={{ position: "relative", display: "inline-block" }}>
                                          <span style={emptyStarStyle}>★</span>
                                          <span
                                            style={{
                                              position: "absolute",
                                              left: 0,
                                              top: 0,
                                              width: "50%",
                                              overflow: "hidden",
                                              ...filledStarStyle,
                                            }}
                                          >
                                            ★
                                          </span>
                                        </span>
                                      )}

                                      {/* Empty stars */}
                                      {[...Array(emptyStars)].map((_, i) => (
                                        <span key={`empty-${i}`} style={emptyStarStyle}>★</span>
                                      ))}
                                    </>
                                  );
                                })()}
                              </div>
                            )}

                            {/* Landing Spot Audio Spin - only renders if a recording exists for this player */}
                            {player?.nfl_draft_year === 2026 &&
                              isPaid === 1 &&
                              audioAvailablePlayers &&
                              audioAvailablePlayers.has(`${player.first_name}_${player.last_name}`) && (
                                <div
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const pName = `${player.first_name}_${player.last_name}`;
                                    if (playingAudioId === player.id) {
                                      audioRef.current?.pause();
                                      setPlayingAudioId(null);
                                    } else {
                                      try {
                                        const res = await fetch(`${API_BASE}/get-audio-url?player_name=${encodeURIComponent(pName)}`);
                                        const data = await res.json();
                                        if (data.url) {
                                          if (audioRef.current) audioRef.current.pause();
                                          audioRef.current = new Audio(data.url);
                                          audioRef.current.onended = () => setPlayingAudioId(null);
                                          audioRef.current.onerror = () => {
                                            console.warn('Audio not available for', pName);
                                            setPlayingAudioId(null);
                                          };
                                          audioRef.current.play().catch(() => {
                                            setPlayingAudioId(null);
                                          });
                                          setPlayingAudioId(player.id);
                                        }
                                      } catch (err) {
                                        console.error('Audio error:', err);
                                      }
                                    }
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    padding: isMobileView ? '2px 6px' : '4px 8px',
                                    marginTop: isMobileView ? 4 : 10,
                                    background: playingAudioId === player.id
                                      ? 'linear-gradient(135deg, #10b981, #059669)'
                                      : 'linear-gradient(135deg, #667eea 0%, #5568d3 100%)',
                                    border: 'none',
                                    borderRadius: 6, cursor: 'pointer',
                                    color: '#fff', fontSize: isMobileView ? 8 : 10, fontWeight: 700,
                                    letterSpacing: '0.5px',
                                    boxShadow: playingAudioId === player.id
                                      ? '0 2px 8px rgba(16, 185, 129, 0.4)'
                                      : '0 2px 8px rgba(102, 126, 234, 0.4)',
                                    transition: 'all 0.2s',
                                  }}
                                  title="Click for a Landing Spot audio analysis!"
                                >
                                  {'\uD83D\uDD0A'} Landing Spot Spin {'\uD83D\uDEEC'}
                                </div>
                              )}
                          </div> {/* Close Metrics block wrapper */}
                          </>)}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
  )}

  {/* Mobile: Show Next Round button */}
  {isMobileView && !selectedRound && (() => {
    // Cap the revealable rounds to what this user is allowed to see:
    // logged-out -> round 1; logged-in free -> rounds 1-2; paid -> all.
    const isPaidView = isPaid === 1 || window.location.hostname === 'localhost';
    const maxAllowedRound = isPaidView ? 12 : (isLoggedIn ? 2 : 1);
    const totalRounds = Object.keys(playersByRound).filter(r => Number(r) <= maxAllowedRound).length;
    if (mobileVisibleRounds >= totalRounds) return null;
    return (
      <div style={{ textAlign: "center", padding: "16px 0 24px", }}>
        <button
          onClick={() => setMobileVisibleRounds(prev => prev + 1)}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            padding: "12px 28px",
            fontSize: "0.9rem",
            fontWeight: "700",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
          }}
        >
          Show Round {mobileVisibleRounds + 1} ▼
        </button>
        <div style={{ fontSize: "0.7rem", color: "#999", marginTop: 6 }}>
          Showing rounds 1–{mobileVisibleRounds} of {totalRounds}
        </div>
      </div>
    );
  })()}

  {isLoggedIn && (
    <div style={{ textAlign: "center", padding: "16px 0 24px" }}>
      <button
        onClick={handleDownloadExcel}
        style={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 12,
          padding: "12px 28px",
          fontSize: "0.9rem",
          fontWeight: "700",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
        }}
      >
        Download as Excel
      </button>
    </div>
  )}
</div>
      </>
      )}

      {/* Mock Draft View */}
      {viewMode === 'mock-draft' && !isLoggedIn && process.env.NODE_ENV !== 'development' && (
        <div style={{ maxWidth: "520px", margin: "60px auto", padding: "0 20px", textAlign: "center" }}>
          {/* Hero image with overlay */}
          <div style={{ position: "relative", marginBottom: "32px", borderRadius: "16px", overflow: "hidden" }}>
            <img
              src="https://i.imgur.com/NIiC7gJ.jpeg"
              alt="Fantasy Amp Mock Draft"
              style={{ width: "100%", borderRadius: "16px", display: "block", objectFit: "cover" }}
            />
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top, rgba(10,6,18,0.85) 0%, transparent 50%)",
              borderRadius: "16px",
            }} />
            <div style={{
              position: "absolute", bottom: "20px", left: 0, right: 0,
              padding: "0 20px",
            }}>
              <div style={{
                display: "inline-block",
                background: "rgba(147,51,234,0.7)",
                border: "1px solid rgba(192,132,252,0.5)",
                borderRadius: "50px",
                padding: "4px 14px",
                fontSize: "0.7rem",
                fontWeight: "800",
                color: "#C084FC",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                backdropFilter: "blur(8px)",
              }}>
                ⚡ AMP Mock Draft
              </div>
            </div>
          </div>

          {/* Headline */}
          <h2 style={{
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontSize: "2rem",
            fontWeight: "900",
            margin: "0 0 12px",
            lineHeight: 1.2,
          }}>
            Mock Draft with Fantasy Amp
          </h2>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: "1.7", marginBottom: "32px", margin: "0 0 32px" }}>
            Practice your 2026, 2027, 2028 &amp; 2029 rookie drafts with live AMP rankings.<br />
            Free to sign up.<br />
            <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.4)" }}>By signing up you'll also receive the free Fantasy Amp newsletter. Unsubscribe anytime.</span>
          </p>

          {/* Feature pills */}
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", flexWrap: "wrap", marginBottom: "32px" }}>
            {["Sleeper Synced", "AMP Rankings", "2026 & 2027 Classes"].map(f => (
              <div key={f} style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "50px",
                padding: "5px 14px",
                fontSize: "0.75rem",
                fontWeight: "600",
                color: "rgba(255,255,255,0.6)",
              }}>{f}</div>
            ))}
          </div>

          {/* CTA */}
          <a
            href={COGNITO_LOGIN_URL}
            style={{
              display: "inline-block", padding: "16px 52px", borderRadius: "50px",
              background: "linear-gradient(135deg, #9333EA, #7C3AED)",
              color: "#fff", fontWeight: "800", fontSize: "1.1rem",
              textDecoration: "none",
              boxShadow: "0 8px 32px rgba(147,51,234,0.5), 0 0 0 1px rgba(192,132,252,0.2)",
              marginBottom: "16px",
              letterSpacing: "0.02em",
            }}
          >
            ⚡ Login / Create Free Account
          </a>
          <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.8rem" }}>
            Already have AMPLive? <a href={COGNITO_LOGIN_URL} style={{ color: "#FFD700", textDecoration: "none" }}>Sign in →</a>
          </div>
        </div>
      )}

      {viewMode === 'mock-draft' && (isLoggedIn || process.env.NODE_ENV === 'development') && (
        <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
          {/* Sub-mode toggle */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 8,
            marginBottom: 16, flexWrap: 'wrap',
          }}>
            {[
              { key: 'live', label: 'Live Mock', requiresPaid: false },
              { key: 'projections', label: 'Projections & Values', requiresPaid: true },
            ].map(opt => {
              const active = mockDraftSubMode === opt.key;
              const locked = opt.requiresPaid && isPaid !== 1;
              return (
                <button
                  key={opt.key}
                  onClick={() => {
                    if (locked) {
                      openUpgradePrompt('projections_values', 'Projections & Values is an AMPLive feature', 'See projected picks and live value deltas for every player in your mock draft.');
                      return;
                    }
                    setMockDraftSubMode(opt.key);
                  }}
                  style={{
                    padding: '8px 18px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    borderRadius: 10,
                    border: active ? '2px solid #FFD700' : '1px solid rgba(255,255,255,0.18)',
                    background: active ? 'rgba(255,215,0,0.18)' : 'rgba(255,255,255,0.05)',
                    color: locked ? 'rgba(255,255,255,0.4)' : active ? '#FFD700' : 'rgba(255,255,255,0.7)',
                    cursor: locked ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {opt.label}{locked ? ' 🔒' : ''}
                </button>
              );
            })}
          </div>

          {mockDraftSubMode === 'projections' && isPaid === 1 && (
            <ProjectionsView
              formatAdjustedPlayers={formatAdjustedPlayers}
              mockDraftYear={mockDraftYear}
              setMockDraftYear={setMockDraftYear}
              isPaid={isPaid}
              isLoggedIn={isLoggedIn}
              isMobileView={isMobileView}
            />
          )}

          {mockDraftSubMode === 'live' && (<>
          <div style={{ textAlign: "center", marginBottom: "40px", position: "relative" }}>
            <div style={{
              position: "absolute", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              width: "320px", height: "80px",
              background: "radial-gradient(ellipse, rgba(147,51,234,0.35) 0%, transparent 70%)",
              filter: "blur(20px)",
              pointerEvents: "none",
            }} />
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isMobileView ? "14px" : "20px",
              flexWrap: "wrap",
              position: "relative",
              zIndex: 1,
            }}>
              <img
                src="/assets/amp_logo.png"
                alt="Fantasy Amp"
                style={{
                  width: isMobileView ? "64px" : "84px",
                  height: isMobileView ? "64px" : "84px",
                  borderRadius: "50%",
                  border: "2px solid rgba(255, 215, 0, 0.5)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(28, 36, 78, 1)",
                  background: "#1C244E",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
              <div style={{ textAlign: isMobileView ? "center" : "left" }}>
                <h2 style={{
                  fontSize: isMobileView ? "2rem" : "2.8rem",
                  fontWeight: "900",
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "none",
                  marginBottom: "6px",
                  marginTop: 0,
                  lineHeight: 1.1,
                }}>
                  {isCffDraft ? 'CFF Mock Draft' : 'Mock Draft Board'}
                </h2>
                <p style={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.95rem",
                  margin: 0,
                  fontWeight: "500",
                }}>
                  {isCffDraft ? '2026 college fantasy rankings · P4 prospects · 2027-2029 classes' : 'Simulate your draft with live AMP rankings'}
                </p>
              </div>
            </div>
          </div>
          {sleeperLoading && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div
                style={{
                  fontSize: "1.3rem",
                  color: "#FFD700",
                  fontWeight: "600",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              >
                Loading...
              </div>
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
            </div>
          )}

          {sleeperError && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                marginBottom: "20px",
                background: "rgba(239, 68, 68, 0.2)",
                border: "1px solid rgba(239, 68, 68, 0.5)",
                borderRadius: "12px",
                color: "#fca5a5",
                fontSize: "1rem",
              }}
            >
              {sleeperError}
            </div>
          )}

          {/* Mode Choice Screen */}
          {!sleeperLoading && !sleeperLeagues && !sleeperLeagueInfo && draftMode === null && (
            <div style={{ maxWidth: "560px", margin: "0 auto", textAlign: "center" }}>
              {(!isLoggedIn && process.env.NODE_ENV !== 'development') ? (
                <div style={{
                  background: "rgba(255,255,255,0.06)", borderRadius: "16px",
                  padding: "36px 28px", border: "2px solid rgba(255,215,0,0.3)",
                }}>
                  <div style={{ fontSize: "1.8rem", marginBottom: "12px" }}>🔒</div>
                  <div style={{ color: "#FFD700", fontSize: "1.3rem", fontWeight: "800", marginBottom: "8px" }}>
                    Log in to start a mock draft
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem", marginBottom: "24px" }}>
                    Free to sign up.<br />
                    <span style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)" }}>By signing up you'll also receive the free Fantasy Amp newsletter. Unsubscribe anytime.</span>
                  </div>
                  <a
                    href={COGNITO_LOGIN_URL}
                    style={{
                      display: "inline-block", padding: "14px 40px", borderRadius: "50px",
                      background: "linear-gradient(135deg, #10b981, #059669)",
                      color: "#fff", fontWeight: "800", fontSize: "1rem",
                      textDecoration: "none", boxShadow: "0 6px 20px rgba(16,185,129,0.4)",
                    }}
                  >
                    Login / Sign Up Free
                  </a>
                </div>
              ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Sleeper */}
                <button
                  onClick={() => setDraftMode('sleeper')}
                  style={{
                    padding: "0",
                    background: "linear-gradient(135deg, rgba(255,215,0,0.14) 0%, rgba(255,180,0,0.06) 100%)",
                    border: "2px solid rgba(255,215,0,0.7)",
                    borderRadius: "16px",
                    color: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.25s ease",
                    boxShadow: "0 4px 24px rgba(255,215,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08)",
                    overflow: "hidden",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,215,0,0.26) 0%, rgba(255,180,0,0.14) 100%)";
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(255,215,0,0.32), inset 0 1px 0 rgba(255,255,255,0.1)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,215,0,0.14) 0%, rgba(255,180,0,0.06) 100%)";
                    e.currentTarget.style.boxShadow = "0 4px 24px rgba(255,215,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "20px 22px" }}>
                    <img
                      src="/assets/sleeper.jpg"
                      alt="Sleeper"
                      style={{
                        width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
                        objectFit: "cover", boxShadow: "0 4px 12px rgba(255,215,0,0.4)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "1.05rem", fontWeight: "800", color: "#FFD700", marginBottom: "3px", letterSpacing: "0.3px" }}>I use Sleeper</div>
                      <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.4 }}>Connect your league to auto-populate draft picks and roster</div>
                    </div>
                    <div style={{ color: "rgba(255,215,0,0.6)", fontSize: "1.2rem", flexShrink: 0 }}>›</div>
                  </div>
                </button>

                {/* Rookie Mock */}
                <button
                  onClick={() => { setDraftMode('guest'); setGuestDraftSubMode('regular'); }}
                  style={{
                    padding: "0",
                    background: "#10b981",
                    border: "2px solid #059669",
                    borderRadius: "16px",
                    color: "#000",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.25s ease",
                    boxShadow: "0 4px 24px rgba(16,185,129,0.3)",
                    overflow: "hidden",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "#059669";
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(16,185,129,0.4)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "#10b981";
                    e.currentTarget.style.boxShadow = "0 4px 24px rgba(16,185,129,0.3)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "20px 22px" }}>
                    <img
                      src="/assets/jeremiah_smith.jpg"
                      alt="Rookie Mock Draft"
                      style={{
                        width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
                        objectFit: "cover", boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "1.05rem", fontWeight: "800", color: "#000", marginBottom: "3px", letterSpacing: "0.3px" }}>Rookie Mock Draft</div>
                      <div style={{ fontSize: "0.85rem", color: "#000", lineHeight: 1.4 }}>Mock the 2026, 2027, 2028, or 2029 rookie class</div>
                    </div>
                    <div style={{ color: "#000", fontSize: "1.2rem", flexShrink: 0 }}>›</div>
                  </div>
                </button>

                {/* CFF Mock — AMPLive only */}
                {(() => {
                  const cffLocked = isPaid !== 1 && window.location.hostname !== 'localhost';
                  return (
                    <button
                      onClick={() => {
                        if (cffLocked) {
                          openUpgradePrompt('cff_mock', 'CFF Mock Draft is an AMPLive feature', 'Upgrade to AMPLive to mock-draft 2027-2029 prospects by 2026 college fantasy production rankings.');
                          return;
                        }
                        setDraftMode('guest'); setGuestDraftSubMode('cff'); setIsCffDraft(true); setGuestNumRounds(5);
                      }}
                      style={{
                        padding: "0",
                        background: cffLocked ? "rgba(255,255,255,0.04)" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        border: cffLocked ? "2px solid rgba(255,255,255,0.12)" : "2px solid #059669",
                        borderRadius: "16px",
                        color: "#fff",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.25s ease",
                        boxShadow: cffLocked ? "none" : "0 4px 24px rgba(16,185,129,0.3)",
                        overflow: "hidden",
                        opacity: cffLocked ? 0.55 : 1,
                      }}
                      onMouseEnter={e => { if (!cffLocked) { e.currentTarget.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(16,185,129,0.45)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
                      onMouseLeave={e => { if (!cffLocked) { e.currentTarget.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(16,185,129,0.3)"; e.currentTarget.style.transform = "translateY(0)"; } }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "20px 22px" }}>
                        <div style={{ width: 48, height: 48, borderRadius: "12px", flexShrink: 0, background: "rgba(0,0,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem" }}>
                          {cffLocked ? '🔒' : '🎓'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "1.05rem", fontWeight: "800", color: cffLocked ? "rgba(255,255,255,0.4)" : "#fff", marginBottom: "3px", letterSpacing: "0.3px" }}>CFF Mock Draft</div>
                          <div style={{ fontSize: "0.85rem", color: cffLocked ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.75)", lineHeight: 1.4 }}>{cffLocked ? 'AMPLive feature — upgrade to unlock' : 'Draft 2027-2029 prospects by 2026 college fantasy rank'}</div>
                        </div>
                        <div style={{ color: cffLocked ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)", fontSize: "1.2rem", flexShrink: 0 }}>›</div>
                      </div>
                    </button>
                  );
                })()}

                {/* Dynasty Startup */}
                <button
                  onClick={() => { setDraftMode('guest'); setGuestDraftSubMode('dynasty'); setGuestNumRounds(12); setMockDraftYear(1); }}
                  style={{
                    padding: "0",
                    background: "#9333ea",
                    border: "2px solid #7e22ce",
                    borderRadius: "16px",
                    color: "#000",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.25s ease",
                    boxShadow: "0 4px 24px rgba(147,51,234,0.3)",
                    overflow: "hidden",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "#7e22ce";
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(147,51,234,0.45)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "#9333ea";
                    e.currentTarget.style.boxShadow = "0 4px 24px rgba(147,51,234,0.3)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "20px 22px" }}>
                    <img
                      src="/assets/josh_allen.jpg"
                      alt="Dynasty Startup Mock"
                      style={{
                        width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
                        objectFit: "cover", boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "1.05rem", fontWeight: "800", color: "#000", marginBottom: "3px", letterSpacing: "0.3px" }}>Dynasty Startup Mock</div>
                      <div style={{ fontSize: "0.85rem", color: "#000", lineHeight: 1.4 }}>Full dynasty player pool by ADP</div>
                    </div>
                    <div style={{ color: "#000", fontSize: "1.2rem", flexShrink: 0 }}>›</div>
                  </div>
                </button>
              </div>
              )}
            </div>
          )}


          {/* Guest Draft Setup — regular rookie draft */}
          {!sleeperLoading && draftMode === 'guest' && !sleeperDraftBoard && guestDraftSubMode === 'regular' && (
            <div style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center" }}>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: "16px", padding: "40px 30px", border: "1px solid rgba(255,255,255,0.15)" }}>
                <h3 style={{ color: "#FFD700", fontSize: "1.3rem", fontWeight: "800", marginBottom: "8px", marginTop: 0 }}>Configure Your Draft</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "24px", marginTop: 0 }}>Which class are you drafting?</p>
                <div style={{ display: "flex", gap: "10px", marginBottom: "28px", flexWrap: "wrap", justifyContent: "center" }}>
                  {[2026, 2027, 2028, 2029].map(yr => {
                    const locked = false; // all mock classes are free to draft; the synced-mock meter is the paid gate
                    return (
                      <button
                        key={yr}
                        onClick={() => { if (!locked) setMockDraftYear(yr); }}
                        title={locked ? "Upgrade to AMPLive to unlock" : undefined}
                        style={{
                          flex: 1,
                          padding: "14px",
                          borderRadius: "10px",
                          border: mockDraftYear === yr ? "2px solid #FFD700" : locked ? "2px solid rgba(255,255,255,0.08)" : "2px solid rgba(255,255,255,0.2)",
                          background: mockDraftYear === yr ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.04)",
                          color: locked ? "rgba(255,255,255,0.25)" : mockDraftYear === yr ? "#FFD700" : "rgba(255,255,255,0.7)",
                          fontWeight: "800",
                          fontSize: "1rem",
                          cursor: locked ? "not-allowed" : "pointer",
                        }}
                      >
                        {locked ? "🔒 " : ""}{yr}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", textAlign: "left" }}>
                  {[
                    { label: "Number of Teams", value: guestNumTeams, setter: setGuestNumTeams, options: [6,8,10,12,14,16] },
                    { label: "Number of Rounds", value: guestNumRounds, setter: setGuestNumRounds, options: [1,2,3,4,5] },
                    { label: "Your Draft Position", value: guestDraftPosition, setter: setGuestDraftPosition, options: Array.from({ length: guestNumTeams }, (_, i) => i + 1) },
                  ].map(({ label, value, setter, options }) => (
                    <div key={label}>
                      <label style={{ display: "block", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", fontWeight: "600", marginBottom: "8px" }}>{label}</label>
                      <select
                        value={value}
                        onChange={e => setter(Number(e.target.value))}
                        style={{ width: "100%", padding: "12px 16px", fontSize: "1rem", borderRadius: "10px", border: "2px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", outline: "none", cursor: "pointer" }}
                      >
                        {options.map(o => <option key={o} value={o} style={{ background: "#1a1a2e" }}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  <div>
                    <label style={{ display: "block", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", fontWeight: "600", marginBottom: "8px" }}>Draft Type</label>
                    <div style={{ display: "flex", gap: "10px" }}>
                      {['snake', 'linear'].map(type => (
                        <button
                          key={type}
                          onClick={() => setGuestDraftType(type)}
                          style={{ flex: 1, padding: "12px", borderRadius: "10px", border: `2px solid ${guestDraftType === type ? '#FFD700' : 'rgba(255,255,255,0.2)'}`, background: guestDraftType === type ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.06)", color: guestDraftType === type ? "#FFD700" : "rgba(255,255,255,0.7)", fontWeight: "700", fontSize: "0.95rem", cursor: "pointer", textTransform: "capitalize" }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "12px", marginTop: "28px" }}>
                  <button
                    onClick={() => setDraftMode(null)}
                    style={{ flex: 1, padding: "14px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", fontWeight: "600", fontSize: "1rem", cursor: "pointer" }}
                  >
                    Back
                  </button>
                  <button
                    onClick={startGuestDraft}
                    style={{ flex: 2, padding: "14px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg, #FFD700, #f59e0b)", color: "#000", fontWeight: "800", fontSize: "1rem", cursor: "pointer" }}
                  >
                    Set Up Draft Board
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Guest Draft Setup — dynasty startup mock */}
          {!sleeperLoading && draftMode === 'guest' && !sleeperDraftBoard && guestDraftSubMode === 'dynasty' && (
            <div style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center" }}>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: "16px", padding: "40px 30px", border: "1px solid rgba(147,51,234,0.3)" }}>
                <h3 style={{ color: "#C084FC", fontSize: "1.3rem", fontWeight: "800", marginBottom: "4px", marginTop: 0 }}>Dynasty Startup Mock</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "28px", marginTop: 0 }}>Full player pool ranked by ADP</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", textAlign: "left" }}>
                  {[
                    { label: "Number of Teams", value: guestNumTeams, setter: setGuestNumTeams, options: [6,8,10,12,14,16] },
                    { label: "Number of Rounds", value: guestNumRounds, setter: setGuestNumRounds, options: Array.from({ length: 12 }, (_, i) => i + 1) },
                    { label: "Your Draft Position", value: guestDraftPosition, setter: setGuestDraftPosition, options: Array.from({ length: guestNumTeams }, (_, i) => i + 1) },
                  ].map(({ label, value, setter, options }) => (
                    <div key={label}>
                      <label style={{ display: "block", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", fontWeight: "600", marginBottom: "8px" }}>{label}</label>
                      <select
                        value={value}
                        onChange={e => setter(Number(e.target.value))}
                        style={{ width: "100%", padding: "12px 16px", fontSize: "1rem", borderRadius: "10px", border: "2px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", outline: "none", cursor: "pointer" }}
                      >
                        {options.map(o => <option key={o} value={o} style={{ background: "#1a1a2e" }}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  <div>
                    <label style={{ display: "block", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", fontWeight: "600", marginBottom: "8px" }}>Draft Type</label>
                    <div style={{ display: "flex", gap: "10px" }}>
                      {['snake', 'linear'].map(type => (
                        <button
                          key={type}
                          onClick={() => { setGuestDraftType(type); }}
                          style={{ flex: 1, padding: "12px", borderRadius: "10px", border: `2px solid ${guestDraftType === type ? '#C084FC' : 'rgba(255,255,255,0.2)'}`, background: guestDraftType === type ? "rgba(147,51,234,0.2)" : "rgba(255,255,255,0.06)", color: guestDraftType === type ? "#C084FC" : "rgba(255,255,255,0.7)", fontWeight: "700", fontSize: "0.95rem", cursor: "pointer", textTransform: "capitalize" }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(
                    <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", userSelect: "none" }}>
                      <div
                        onClick={() => setMockDraftReverseRound3(prev => !prev)}
                        style={{
                          width: "36px", height: "20px", borderRadius: "10px", flexShrink: 0,
                          background: mockDraftReverseRound3 ? "rgba(192,132,252,0.4)" : "rgba(255,255,255,0.15)",
                          border: mockDraftReverseRound3 ? "1px solid #C084FC" : "1px solid rgba(255,255,255,0.3)",
                          position: "relative", transition: "all 0.2s ease",
                        }}
                      >
                        <div style={{
                          width: "14px", height: "14px", borderRadius: "50%",
                          background: mockDraftReverseRound3 ? "#C084FC" : "rgba(255,255,255,0.5)",
                          position: "absolute", top: "2px",
                          left: mockDraftReverseRound3 ? "19px" : "2px",
                          transition: "all 0.2s ease",
                        }} />
                      </div>
                      <span style={{ fontSize: "0.9rem", fontWeight: "600", color: mockDraftReverseRound3 ? "#C084FC" : "rgba(255,255,255,0.6)", transition: "color 0.2s ease" }}>
                        3rd Round Reversal
                      </span>
                    </label>
                  )}
                </div>
                <div style={{ display: "flex", gap: "12px", marginTop: "28px" }}>
                  <button
                    onClick={() => setDraftMode(null)}
                    style={{ flex: 1, padding: "14px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", fontWeight: "600", fontSize: "1rem", cursor: "pointer" }}
                  >
                    Back
                  </button>
                  <button
                    onClick={startGuestDraft}
                    style={{ flex: 2, padding: "14px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg, #9333ea, #C084FC)", color: "#fff", fontWeight: "800", fontSize: "1rem", cursor: "pointer" }}
                  >
                    Start Dynasty Mock
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Guest Draft Setup — CFF mock */}
          {!sleeperLoading && draftMode === 'guest' && !sleeperDraftBoard && guestDraftSubMode === 'cff' && (
            <div style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center" }}>
              <div style={{ background: "rgba(16,185,129,0.08)", borderRadius: "16px", padding: "40px 30px", border: "1px solid rgba(16,185,129,0.3)" }}>
                <h3 style={{ color: "#10b981", fontSize: "1.3rem", fontWeight: "800", marginBottom: "4px", marginTop: 0 }}>CFF Mock Draft</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "28px", marginTop: 0 }}>2027-2029 prospects ranked by 2026 college fantasy production · P4 only</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", textAlign: "left" }}>
                  {[
                    { label: "Number of Teams", value: guestNumTeams, setter: setGuestNumTeams, options: [6, 8, 10, 12, 14, 16] },
                    { label: "Number of Rounds", value: guestNumRounds, setter: setGuestNumRounds, options: [3, 5, 7, 10, 15, 20] },
                    { label: "Your Draft Position", value: guestDraftPosition, setter: setGuestDraftPosition, options: Array.from({ length: guestNumTeams }, (_, i) => i + 1) },
                  ].map(({ label, value, setter, options }) => (
                    <div key={label}>
                      <label style={{ display: "block", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", fontWeight: "600", marginBottom: "8px" }}>{label}</label>
                      <select
                        value={value}
                        onChange={e => setter(Number(e.target.value))}
                        style={{ width: "100%", padding: "12px 16px", fontSize: "1rem", borderRadius: "10px", border: "2px solid rgba(16,185,129,0.3)", background: "rgba(255,255,255,0.08)", color: "#fff", outline: "none", cursor: "pointer" }}
                      >
                        {options.map(o => <option key={o} value={o} style={{ background: "#1a1a2e" }}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  <div>
                    <label style={{ display: "block", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", fontWeight: "600", marginBottom: "8px" }}>Draft Type</label>
                    <div style={{ display: "flex", gap: "10px" }}>
                      {['snake', 'linear'].map(type => (
                        <button
                          key={type}
                          onClick={() => setGuestDraftType(type)}
                          style={{ flex: 1, padding: "12px", borderRadius: "10px", border: `2px solid ${guestDraftType === type ? '#10b981' : 'rgba(255,255,255,0.2)'}`, background: guestDraftType === type ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.06)", color: guestDraftType === type ? "#10b981" : "rgba(255,255,255,0.7)", fontWeight: "700", fontSize: "0.95rem", cursor: "pointer", textTransform: "capitalize" }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "12px", marginTop: "28px" }}>
                  <button
                    onClick={() => { setDraftMode(null); setGuestDraftSubMode(null); setIsCffDraft(false); }}
                    style={{ flex: 1, padding: "14px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", fontWeight: "600", fontSize: "1rem", cursor: "pointer" }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      if (isPaid !== 1 && window.location.hostname !== 'localhost') {
                        openUpgradePrompt('cff_mock', 'CFF Mock Draft is an AMPLive feature', 'Upgrade to AMPLive to mock-draft 2027-2029 prospects by 2026 college fantasy production rankings.');
                        return;
                      }
                      startGuestDraft();
                    }}
                    style={{ flex: 2, padding: "14px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", fontWeight: "800", fontSize: "1rem", cursor: "pointer" }}
                  >
                    Start CFF Draft
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stage A: Username Input */}
          {!sleeperLoading && !sleeperLeagues && !sleeperLeagueInfo && draftMode === 'sleeper' && (
            <div
              style={{
                maxWidth: "500px",
                margin: "0 auto",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: "16px",
                  padding: "40px 30px",
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
              >
                <label
                  style={{
                    display: "block",
                    color: "#fff",
                    fontSize: "1.1rem",
                    fontWeight: "600",
                    marginBottom: "12px",
                  }}
                >
                  Sleeper Username
                </label>
                <input
                  type="text"
                  value={sleeperUsername}
                  onChange={(e) => setSleeperUsername(e.target.value)}
                  placeholder="Enter your Sleeper username"
                  style={{
                    width: "100%",
                    padding: "14px 18px",
                    fontSize: "1.1rem",
                    borderRadius: "10px",
                    border: "2px solid rgba(255,255,255,0.2)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    outline: "none",
                    marginBottom: "20px",
                    boxSizing: "border-box",
                    transition: "border-color 0.3s ease",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#FFD700"; }}
                  onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.2)"; }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sleeperUsername.trim()) {
                      setSleeperLoading(true);
                      setSleeperError(null);
                      fetch('https://api.sleeper.app/v1/user/' + encodeURIComponent(sleeperUsername.trim()))
                        .then(res => {
                          if (!res.ok) throw new Error('not found');
                          return res.json();
                        })
                        .then(userData => {
                          if (!userData || !userData.user_id) throw new Error('not found');
                          setSleeperUserId(userData.user_id);
                          // Fetch both 2025 and 2026 leagues — dynasty leagues roll to a new ID each season
                          return Promise.all([
                            fetch('https://api.sleeper.app/v1/user/' + userData.user_id + '/leagues/nfl/2025').then(r => r.json()).catch(() => []),
                            fetch('https://api.sleeper.app/v1/user/' + userData.user_id + '/leagues/nfl/2026').then(r => r.json()).catch(() => []),
                          ]).then(([leagues2025, leagues2026]) => {
                            const all2025 = Array.isArray(leagues2025) ? leagues2025 : [];
                            const all2026 = Array.isArray(leagues2026) ? leagues2026 : [];
                            // Prefer 2026 leagues; include 2025 leagues only if no 2026 version exists
                            const seen2026Names = new Set(all2026.map(l => l.name));
                            const unique2025 = all2025.filter(l => !seen2026Names.has(l.name));
                            const combined = [...all2026, ...unique2025];
                            if (combined.length === 0) {
                              setSleeperError('No NFL leagues found for this username.');
                            } else {
                              setSleeperLeagues(combined);
                            }
                          });
                        })
                        .catch(() => setSleeperError('Username not found. Please check and try again.'))
                        .finally(() => setSleeperLoading(false));
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (!sleeperUsername.trim()) return;
                    setSleeperLoading(true);
                    setSleeperError(null);
                    fetch('https://api.sleeper.app/v1/user/' + encodeURIComponent(sleeperUsername.trim()))
                      .then(res => {
                        if (!res.ok) throw new Error('not found');
                        return res.json();
                      })
                      .then(userData => {
                        if (!userData || !userData.user_id) throw new Error('not found');
                        setSleeperUserId(userData.user_id);
                        // Fetch both 2025 and 2026 leagues — dynasty leagues roll to a new ID each season
                        return Promise.all([
                          fetch('https://api.sleeper.app/v1/user/' + userData.user_id + '/leagues/nfl/2025').then(r => r.json()).catch(() => []),
                          fetch('https://api.sleeper.app/v1/user/' + userData.user_id + '/leagues/nfl/2026').then(r => r.json()).catch(() => []),
                        ]).then(([leagues2025, leagues2026]) => {
                          const all2025 = Array.isArray(leagues2025) ? leagues2025 : [];
                          const all2026 = Array.isArray(leagues2026) ? leagues2026 : [];
                          // Prefer 2026 leagues; include 2025 leagues only if no 2026 version exists
                          const seen2026Names = new Set(all2026.map(l => l.name));
                          const unique2025 = all2025.filter(l => !seen2026Names.has(l.name));
                          const combined = [...all2026, ...unique2025];
                          if (combined.length === 0) {
                            setSleeperError('No NFL leagues found for this username.');
                          } else {
                            setSleeperLeagues(combined);
                          }
                        });
                      })
                      .catch(() => setSleeperError('Username not found. Please check and try again.'))
                      .finally(() => setSleeperLoading(false));
                  }}
                  style={{
                    padding: "14px 40px",
                    fontSize: "1.1rem",
                    fontWeight: "700",
                    borderRadius: "50px",
                    border: "none",
                    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                    color: "#000",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 6px 20px rgba(255,215,0,0.4)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 8px 28px rgba(255,215,0,0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.4)";
                  }}
                >
                  Find Leagues
                </button>
              </div>
            </div>
          )}

          {/* Stage B: League Selection */}
          {!sleeperLoading && sleeperLeagues && !sleeperLeagueInfo && (
            <div style={{ maxWidth: "700px", margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: "#FFD700",
                    marginBottom: "8px",
                  }}
                >
                  Select a League
                </h3>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1rem" }}>
                  {sleeperLeagues.length} league{sleeperLeagues.length !== 1 ? 's' : ''} found for <span style={{ color: "#fff", fontWeight: "600" }}>{sleeperUsername}</span>
                </p>
                <button
                  onClick={() => {
                    setSleeperLeagues(null);
                    setSleeperUserId(null);
                    setSleeperError(null);
                  }}
                  style={{
                    marginTop: "8px",
                    padding: "6px 16px",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "transparent",
                    color: "rgba(255,255,255,0.7)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#FFD700";
                    e.currentTarget.style.color = "#FFD700";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                  }}
                >
                  Change Username
                </button>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "12px",
                }}
              >
                {sleeperLeagues.map((league) => (
                  <button
                    key={league.league_id}
                    onClick={() => {
                      setSleeperLeagueId(league.league_id);
                      setSleeperLoading(true);
                      setSleeperError(null);
                      fetch(API_BASE + '/sleeper/league-info?league_id=' + encodeURIComponent(league.league_id))
                        .then(res => res.json())
                        .then(data => {
                          if (data.error) {
                            setSleeperError(data.error);
                          } else {
                            setSleeperLeagueInfo(data);
                          }
                        })
                        .catch(() => setSleeperError('Failed to connect to league. Please try again.'))
                        .finally(() => setSleeperLoading(false));
                    }}
                    style={{
                      padding: "20px 16px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      borderRadius: "12px",
                      border: "2px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.08)",
                      color: "#fff",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#FFD700";
                      e.currentTarget.style.background = "rgba(255,215,0,0.15)";
                      e.currentTarget.style.transform = "scale(1.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <div style={{ marginBottom: "6px" }}>{league.name}</div>
                    <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", fontWeight: "400" }}>
                      {league.total_rosters} teams &middot; {league.season}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stage C: Team Selection */}
          {!sleeperLoading && sleeperLeagueInfo && !sleeperSelectedOwner && (
            <div style={{ maxWidth: "700px", margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: "#FFD700",
                    marginBottom: "8px",
                  }}
                >
                  {sleeperLeagueInfo.league_name}
                </h3>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1rem" }}>
                  Select your team
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "8px" }}>
                  <button
                    onClick={() => {
                      setSleeperLeagueInfo(null);
                      setSleeperLeagueId('');
                      setSleeperError(null);
                    }}
                    style={{
                      padding: "6px 16px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "transparent",
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#FFD700";
                      e.currentTarget.style.color = "#FFD700";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                      e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                    }}
                  >
                    Change League
                  </button>
                  <button
                    onClick={() => {
                      setSleeperLeagueInfo(null);
                      setSleeperLeagueId('');
                      setSleeperLeagues(null);
                      setSleeperUserId(null);
                      setSleeperError(null);
                    }}
                    style={{
                      padding: "6px 16px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "transparent",
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#FFD700";
                      e.currentTarget.style.color = "#FFD700";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                      e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                    }}
                  >
                    Change Username
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "12px",
                }}
              >
                {sleeperLeagueInfo.teams && sleeperLeagueInfo.teams.map((team) => (
                  <button
                    key={team.owner_id}
                    onClick={() => {
                      if (!isLoggedIn && window.location.hostname !== 'localhost') {
                        openUpgradePrompt('synced_mock_login', 'Create a free account to sync your league', 'Log in to pull your real Sleeper league into a mock draft. 10 synced mocks free with an account.', true);
                        return;
                      }
                      if (isPaid !== 1 && window.location.hostname !== 'localhost' && syncedMockCount >= FREE_SYNCED_MOCKS) {
                        openUpgradePrompt('synced_mock_limit', `You've used your ${FREE_SYNCED_MOCKS} free synced mocks`, 'Upgrade to AMPLive for unlimited Sleeper-synced mock drafts with your real league. $10/mo, cancel anytime.');
                        return;
                      }
                      setSleeperSelectedOwner(team.owner_id);
                      setSleeperLoading(true);
                      setSleeperError(null);
                      fetch(API_BASE + '/sleeper/draft-board?league_id=' + encodeURIComponent(sleeperLeagueId.trim()) + '&owner_id=' + encodeURIComponent(team.owner_id))
                        .then(res => res.json())
                        .then(data => {
                          if (data.error) {
                            setSleeperError(data.error);
                            setSleeperSelectedOwner(null);
                          } else {
                            setSleeperDraftBoard(data);
                            setSleeperBaseDraftBoard(data);
                            // Non-blocking fetch of traded picks for year switching
                            fetch('https://api.sleeper.app/v1/league/' + encodeURIComponent(sleeperLeagueId.trim()) + '/traded_picks')
                              .then(res => res.json())
                              .then(tp => { if (Array.isArray(tp)) setSleeperTradedPicks(tp); })
                              .catch(() => {});
                            // Non-blocking fetch of team needs
                            fetch(API_BASE + '/sleeper/team-needs?league_id=' + encodeURIComponent(sleeperLeagueId.trim()))
                              .then(res => res.json())
                              .then(needsData => {
                                if (needsData.teams) setSleeperTeamNeeds(needsData);
                              })
                              .catch(() => {}); // Non-blocking — ignore errors
                            // Non-blocking fetch of player values from RDS
                            fetch(API_BASE + '/player-values')
                              .then(res => res.json())
                              .then(data => {
                                if (!data.error) {
                                  Object.values(data).forEach(v => { if (v.amp_rank != null) v.amp_rank = Number(v.amp_rank); });
                                  setPlayerValues(data);
                                }
                              })
                              .catch(() => {});
                            // Non-blocking fetch of rosters + player names for roster modal
                            fetch('https://api.sleeper.app/v1/league/' + encodeURIComponent(sleeperLeagueId.trim()) + '/rosters')
                              .then(res => res.json())
                              .then(rostersData => {
                                const rosterMap = {};
                                const allPlayerIds = new Set();
                                rostersData.forEach(r => {
                                  const players = r.players || [];
                                  // Key by both owner_id and roster_id so lookups work regardless of ID format
                                  if (r.owner_id) rosterMap[r.owner_id] = players;
                                  if (r.roster_id != null) rosterMap[String(r.roster_id)] = players;
                                  players.forEach(id => allPlayerIds.add(id));
                                });
                                setSleeperRosters(rosterMap);
                                return fetch('https://api.sleeper.app/v1/players/nfl').then(res => res.json()).then(allPlayers => {
                                  const subset = {};
                                  allPlayerIds.forEach(id => {
                                    if (allPlayers[id]) {
                                      subset[id] = {
                                        name: (allPlayers[id].first_name || '') + ' ' + (allPlayers[id].last_name || ''),
                                        position: allPlayers[id].position || '',
                                        team: allPlayers[id].team || '',
                                        searchRank: allPlayers[id].search_rank || 9999,
                                      };
                                    }
                                  });
                                  setSleeperPlayerNames(subset);
                                });
                              })
                              .catch(() => {}); // Non-blocking — ignore errors
                          }
                        })
                        .catch(() => {
                          setSleeperError('Failed to load draft board. Please try again.');
                          setSleeperSelectedOwner(null);
                        })
                        .finally(() => setSleeperLoading(false));
                    }}
                    style={{
                      padding: "16px 12px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      borderRadius: "12px",
                      border: "2px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.08)",
                      color: "#fff",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#FFD700";
                      e.currentTarget.style.background = "rgba(255,215,0,0.15)";
                      e.currentTarget.style.transform = "scale(1.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    {team.display_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Standalone Team Ratings — shown when draft order hasn't been set or no draft board */}
          {!sleeperLoading && sleeperSelectedOwner && sleeperLeagueInfo?.teams && sleeperRosters && sleeperPlayerNames && playerValues && (!sleeperDraftBoard || sleeperDraftBoard.draft_order_set === false) && (
            <div style={{ marginTop: "24px" }}>
              <h3 style={{ textAlign: "center", fontSize: "1.1rem", fontWeight: "700", color: "#FFD700", marginBottom: "16px" }}>
                Team Ratings
              </h3>
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${sleeperLeagueInfo.teams.length}, minmax(110px, 1fr))`,
                    gap: "6px",
                    minWidth: `${sleeperLeagueInfo.teams.length * 116}px`,
                  }}
                >
                  {(() => {
                    const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };
                    const posScoresAll = {};
                    const totalScoresAll = {};
                    sleeperLeagueInfo.teams.forEach(tm => {
                      const oid = tm.owner_id;
                      const pids = sleeperRosters[oid] || [];
                      const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
                      Object.entries(topNC).forEach(([pos, topN]) => {
                        const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
                        if (rks.length > 0) {
                          if (!posScoresAll[pos]) posScoresAll[pos] = {};
                          posScoresAll[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
                        }
                      });
                      const topRanks = pls.map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, 12);
                      if (topRanks.length > 0) totalScoresAll[oid] = topRanks.reduce((s, r) => s + r, 0) / topRanks.length;
                    });
                    // Normalize per-position
                    const posStarsAll = {};
                    Object.entries(posScoresAll).forEach(([pos, scores]) => {
                      const vals = Object.values(scores);
                      const mn = Math.min(...vals); const mx = Math.max(...vals); const rng = mx - mn;
                      posStarsAll[pos] = {};
                      Object.entries(scores).forEach(([oid, sc]) => {
                        posStarsAll[pos][oid] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
                      });
                    });
                    // Normalize total
                    const totalVals = Object.values(totalScoresAll);
                    const totalMn = Math.min(...totalVals); const totalMx = Math.max(...totalVals); const totalRng = totalMx - totalMn;
                    const totalStarsAll = {};
                    Object.entries(totalScoresAll).forEach(([oid, sc]) => {
                      totalStarsAll[oid] = totalRng === 0 ? 3 : Math.round(((sc - totalMn) / totalRng) * 10) / 2;
                    });
                    const posOrder = ['QB', 'RB', 'WR', 'TE'];
                    const posClrs = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
                    const renderStars = (st, color) => [1,2,3,4,5].map(j => {
                      if (j <= Math.floor(st)) return <span key={j} style={{ color }}>★</span>;
                      if (j - 0.5 <= st) return <span key={j} style={{ position: 'relative', display: 'inline-block' }}><span style={{ color: 'rgba(255,255,255,0.15)' }}>★</span><span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: '50%', color }}>★</span></span>;
                      return <span key={j} style={{ color: 'rgba(255,255,255,0.15)' }}>★</span>;
                    });
                    const starColor = (st) => st >= 4 ? '#FFD700' : st >= 3 ? '#10b981' : st >= 2 ? '#f59e0b' : '#ef4444';

                    return sleeperLeagueInfo.teams
                      .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
                      .map((tm, i) => {
                        const oid = tm.owner_id;
                        const isUser = oid === sleeperSelectedOwner;
                        return (
                          <div key={`standalone-${i}`} style={{
                            padding: "8px 6px",
                            background: "rgba(255,255,255,0.04)",
                            border: isUser ? "2px solid rgba(255,215,0,0.5)" : "2px solid rgba(0,0,0,0.8)",
                            borderRadius: "8px",
                            textAlign: "center",
                            fontSize: "0.65rem",
                            outline: "2px solid rgba(0,0,0,0.7)",
                            outlineOffset: "-1px",
                          }}>
                            <div style={{
                              fontWeight: "700",
                              fontSize: "0.7rem",
                              color: isUser ? "#FFD700" : "rgba(255,255,255,0.7)",
                              marginBottom: "6px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textShadow: isUser ? "0 0 6px rgba(255,215,0,0.4)" : "none",
                            }}>
                              {tm.display_name}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1px", lineHeight: "1.6" }}>
                              {posOrder.map(pos => {
                                const st = posStarsAll[pos]?.[oid];
                                if (st == null) return null;
                                return (
                                  <div key={pos} style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '1px 5px' }}>
                                    <span style={{ color: posClrs[pos], fontWeight: '700', fontSize: '0.6rem', width: '18px', textShadow: '0 0 3px #000, 0 0 3px #000' }}>{pos}</span>
                                    <span style={{ fontSize: '0.55rem' }}>{renderStars(st, starColor(st))}</span>
                                  </div>
                                );
                              })}
                              {totalStarsAll[oid] != null && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.10)', borderRadius: '4px', padding: '1px 5px', marginTop: '2px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                  <span style={{ color: '#c0c0c0', fontWeight: '700', fontSize: '0.55rem', width: '18px', textShadow: '0 0 3px #000, 0 0 3px #000' }}>OVR</span>
                                  <span style={{ fontSize: '0.55rem' }}>{renderStars(totalStarsAll[oid], starColor(totalStarsAll[oid]))}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Stage D: Draft Board Grid */}
          {!sleeperLoading && sleeperDraftBoard && (
            <div>
              {/* Mobile landscape prompt */}
              {!mobileLandscapeDismissed && (
                <div
                  className="mobile-landscape-prompt"
                  style={{
                    display: "none",
                    textAlign: "center",
                    padding: "16px 20px",
                    marginBottom: "16px",
                    background: "rgba(59,130,246,0.15)",
                    border: "1px solid rgba(59,130,246,0.4)",
                    borderRadius: "12px",
                    position: "relative",
                  }}
                >
                  <style>{`@media (max-width: 768px) and (orientation: portrait) { .mobile-landscape-prompt { display: block !important; } }`}</style>
                  <button
                    onClick={() => setMobileLandscapeDismissed(true)}
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "10px",
                      background: "none",
                      border: "none",
                      color: "rgba(255,255,255,0.5)",
                      fontSize: "1.1rem",
                      cursor: "pointer",
                      padding: "2px 6px",
                      lineHeight: "1",
                    }}
                  >
                    ✕
                  </button>
                  <div style={{ fontSize: "1.5rem", marginBottom: "6px" }}>📱↔️</div>
                  <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem", fontWeight: "600" }}>
                    Rotate your phone to landscape for the best mock draft experience!
                  </div>
                </div>
              )}
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.95rem", marginBottom: "12px" }}>
                  <span style={{
                    display: "inline-block",
                    background: "rgba(255,215,0,0.15)",
                    border: "1px solid rgba(255,215,0,0.4)",
                    color: "#FFD700",
                    fontWeight: "700",
                    padding: "2px 10px",
                    borderRadius: "6px",
                    marginRight: "8px",
                    fontSize: "0.95rem",
                  }}>{sleeperDraftBoard.season}</span>
                  {sleeperDraftBoard.draft_type}
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
                  {/* Change Team dropdown */}
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => { setShowChangeTeamDropdown(prev => !prev); setShowChangeLeagueDropdown(false); }}
                      style={{
                        padding: "8px 20px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        borderRadius: "8px",
                        border: showChangeTeamDropdown ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.3)",
                        background: "transparent",
                        color: showChangeTeamDropdown ? "#FFD700" : "rgba(255,255,255,0.7)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#FFD700";
                        e.currentTarget.style.color = "#FFD700";
                      }}
                      onMouseLeave={(e) => {
                        if (!showChangeTeamDropdown) {
                          e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                          e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                        }
                      }}
                    >
                      Change Team ▾
                    </button>
                    {showChangeTeamDropdown && sleeperLeagueInfo?.teams && (
                      <div style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#1a1a2e",
                        border: "1px solid rgba(255,215,0,0.3)",
                        borderRadius: "10px",
                        padding: "6px",
                        zIndex: 100,
                        minWidth: "200px",
                        maxHeight: "280px",
                        overflowY: "auto",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                      }}>
                        {[...sleeperLeagueInfo.teams].sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '')).map(tm => {
                          const isSelected = tm.owner_id === sleeperSelectedOwner;
                          return (
                            <button
                              key={tm.owner_id}
                              onClick={() => {
                                resetMockDraft();
                                setShowChangeTeamDropdown(false);
                                setSleeperSelectedOwner(tm.owner_id);
                                setSleeperLoading(true);
                                // Re-fetch draft board with new owner to get correct is_my_pick from server
                                fetch(API_BASE + '/sleeper/draft-board?league_id=' + encodeURIComponent(sleeperLeagueId.trim()) + '&owner_id=' + encodeURIComponent(tm.owner_id))
                                  .then(res => res.json())
                                  .then(data => {
                                    if (data.error) {
                                      setSleeperError(data.error);
                                    } else {
                                      setSleeperDraftBoard(data);
                                      setSleeperBaseDraftBoard(data);
                                      // Re-fetch traded picks for year switching
                                      fetch('https://api.sleeper.app/v1/league/' + encodeURIComponent(sleeperLeagueId.trim()) + '/traded_picks')
                                        .then(res => res.json())
                                        .then(tp => { if (Array.isArray(tp)) setSleeperTradedPicks(tp); })
                                        .catch(() => {});
                                    }
                                  })
                                  .catch(() => setSleeperError('Failed to load draft board.'))
                                  .finally(() => setSleeperLoading(false));
                              }}
                              style={{
                                display: "block",
                                width: "100%",
                                padding: "8px 12px",
                                fontSize: "0.85rem",
                                fontWeight: isSelected ? "700" : "500",
                                borderRadius: "6px",
                                border: "none",
                                background: isSelected ? "rgba(255,215,0,0.15)" : "transparent",
                                color: isSelected ? "#FFD700" : "rgba(255,255,255,0.7)",
                                cursor: "pointer",
                                textAlign: "left",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                            >
                              {tm.display_name}{isSelected ? ' ✓' : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Change League dropdown */}
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => { setShowChangeLeagueDropdown(prev => !prev); setShowChangeTeamDropdown(false); }}
                      style={{
                        padding: "8px 20px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        borderRadius: "8px",
                        border: showChangeLeagueDropdown ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.3)",
                        background: "transparent",
                        color: showChangeLeagueDropdown ? "#FFD700" : "rgba(255,255,255,0.7)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#FFD700";
                        e.currentTarget.style.color = "#FFD700";
                      }}
                      onMouseLeave={(e) => {
                        if (!showChangeLeagueDropdown) {
                          e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                          e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                        }
                      }}
                    >
                      Change League ▾
                    </button>
                    {showChangeLeagueDropdown && sleeperLeagues && (
                      <div style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#1a1a2e",
                        border: "1px solid rgba(255,215,0,0.3)",
                        borderRadius: "10px",
                        padding: "6px",
                        zIndex: 100,
                        minWidth: "240px",
                        maxHeight: "280px",
                        overflowY: "auto",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                      }}>
                        {sleeperLeagues.map(league => {
                          const isSelected = league.league_id === sleeperLeagueId;
                          return (
                            <button
                              key={league.league_id}
                              onClick={() => {
                                if (isSelected) { setShowChangeLeagueDropdown(false); return; }
                                resetMockDraft();
                                setSleeperLeagueId(league.league_id);
                                setSleeperSelectedOwner(null);
                                setSleeperDraftBoard(null);
                                setSleeperBaseDraftBoard(null);
                                setSleeperTradedPicks(null);
                                setSleeperTeamNeeds(null);
                                setSleeperRosters(null);
                                setSleeperPlayerNames(null);
                                setRosterModalOwnerId(null);
                                setSleeperError(null);
                                setShowChangeLeagueDropdown(false);
                                // Fetch league info for the new league
                                setSleeperLoading(true);
                                fetch('https://api.sleeper.app/v1/league/' + encodeURIComponent(league.league_id))
                                  .then(res => res.json())
                                  .then(leagueData => {
                                    // Fetch users for this league
                                    return fetch('https://api.sleeper.app/v1/league/' + encodeURIComponent(league.league_id) + '/users')
                                      .then(res => res.json())
                                      .then(users => {
                                        const rosters_url = 'https://api.sleeper.app/v1/league/' + encodeURIComponent(league.league_id) + '/rosters';
                                        return fetch(rosters_url).then(res => res.json()).then(rosters => {
                                          const teams = rosters.map(r => {
                                            const user = users.find(u => u.user_id === r.owner_id) || {};
                                            return {
                                              roster_id: r.roster_id,
                                              owner_id: r.owner_id || String(r.roster_id),
                                              display_name: user.display_name || `Team ${r.roster_id}`,
                                            };
                                          });
                                          setSleeperLeagueInfo({
                                            league_name: leagueData.name,
                                            total_rosters: leagueData.total_rosters,
                                            teams,
                                          });
                                        });
                                      });
                                  })
                                  .catch(() => setSleeperError('Failed to load league.'))
                                  .finally(() => setSleeperLoading(false));
                              }}
                              style={{
                                display: "block",
                                width: "100%",
                                padding: "8px 12px",
                                fontSize: "0.85rem",
                                fontWeight: isSelected ? "700" : "500",
                                borderRadius: "6px",
                                border: "none",
                                background: isSelected ? "rgba(255,215,0,0.15)" : "transparent",
                                color: isSelected ? "#FFD700" : "rgba(255,255,255,0.7)",
                                cursor: "pointer",
                                textAlign: "left",
                                transition: "all 0.15s ease",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                            >
                              {league.name}{isSelected ? ' ✓' : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      resetMockDraft();
                      setSleeperLeagueInfo(null);
                      setSleeperLeagueId('');
                      setSleeperLeagues(null);
                      setSleeperUserId(null);
                      setSleeperSelectedOwner(null);
                      setSleeperDraftBoard(null);
                      setSleeperBaseDraftBoard(null);
                      setSleeperTradedPicks(null);
                      setSleeperTeamNeeds(null);
                      setSleeperRosters(null);
                      setSleeperPlayerNames(null);
                      setRosterModalOwnerId(null);
                      setSleeperError(null);
                    }}
                    style={{
                      padding: "8px 20px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "transparent",
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#FFD700";
                      e.currentTarget.style.color = "#FFD700";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                      e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                    }}
                  >
                    Change Username
                  </button>
                </div>
              </div>

              {/* Mock Draft Control Bar */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                marginBottom: "20px",
                flexWrap: "wrap",
              }}>
                {(mockDraftActive || mockDraftComplete) && (
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "rgba(255,215,0,0.12)",
                    border: "1px solid rgba(255,215,0,0.4)",
                    color: "#FFD700",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    padding: "6px 14px",
                    borderRadius: "8px",
                    letterSpacing: "0.5px",
                  }}>
                    {mockDraftYear === 1 ? 'Dynasty Startup' : mockDraftYear} {scoringFormat === 'SF' ? 'SF' : '1QB'}{tePremium ? ' TEP' : ''}
                  </span>
                )}
                {!mockDraftActive && !mockDraftComplete && (
                  <>
                    {mockDraftYear === 1 && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        background: "rgba(147,51,234,0.15)", border: "1px solid rgba(192,132,252,0.4)",
                        color: "#C084FC", fontWeight: "700", fontSize: "0.85rem",
                        padding: "6px 14px", borderRadius: "8px",
                      }}>
                        Dynasty Startup
                      </span>
                    )}
                    {mockDraftYear > 2026 && (
                      <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>
                        Future year draft order is unknown — using this year as a proxy
                      </span>
                    )}
                    {mockDraftYear !== 1 && <div style={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                      {[2026, 2027, 2028, 2029].map(yr => {
                        const yearLocked = false; // all mock classes free; synced-mock meter is the paid gate
                        return (
                        <button
                          key={yr}
                          onClick={() => {
                            if (yearLocked) return;
                            setMockDraftYear(yr);
                            setUseSleeperAdp(false);
                            setReassigningPickIndex(null);
                            if (sleeperBaseDraftBoard && sleeperLeagueInfo?.teams) {
                              // Build roster_id -> { owner_id, display_name } map
                              const rosterMap = {};
                              sleeperLeagueInfo.teams.forEach(t => {
                                rosterMap[String(t.roster_id)] = { owner_id: t.owner_id, display_name: t.display_name };
                              });
                              // Remap picks using traded_picks for the selected year
                              const tradedForYear = (sleeperTradedPicks || []).filter(tp => String(tp.season) === String(yr));
                              const remappedPicks = sleeperBaseDraftBoard.picks.map(pick => {
                                let currentOwnerId = pick.original_owner_id;
                                let currentName = pick.original_owner_name;
                                let wasTrded = false;
                                for (const tp of tradedForYear) {
                                  if (tp.round === pick.round) {
                                    // Match: the traded pick's roster_id is the original slot owner
                                    const origSlotOwner = rosterMap[String(tp.roster_id)]?.owner_id;
                                    if (origSlotOwner === pick.original_owner_id) {
                                      const newOwner = rosterMap[String(tp.owner_id)];
                                      if (newOwner) {
                                        currentOwnerId = newOwner.owner_id;
                                        currentName = newOwner.display_name;
                                        wasTrded = true;
                                      }
                                    }
                                  }
                                }
                                return {
                                  ...pick,
                                  current_owner_id: currentOwnerId,
                                  current_owner_name: currentName,
                                  is_my_pick: currentOwnerId === sleeperSelectedOwner,
                                  was_traded: wasTrded,
                                };
                              });
                              setSleeperDraftBoard({ ...sleeperBaseDraftBoard, season: String(yr), picks: remappedPicks });
                            }
                          }}
                          style={{
                            padding: "6px 14px",
                            fontSize: "0.85rem",
                            fontWeight: mockDraftYear === yr ? "700" : "500",
                            borderRadius: "8px",
                            border: mockDraftYear === yr ? "1px solid #FFD700" : yearLocked ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(255,255,255,0.2)",
                            background: mockDraftYear === yr ? "rgba(255,215,0,0.2)" : "transparent",
                            color: yearLocked ? "rgba(255,255,255,0.3)" : mockDraftYear === yr ? "#FFD700" : "rgba(255,255,255,0.6)",
                            cursor: yearLocked ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease",
                            opacity: yearLocked ? 0.6 : 1,
                          }}
                          title={yearLocked ? "Upgrade to AMPLive to unlock" : undefined}
                        >
                          {yr}{yearLocked ? ' 🔒' : ''}
                        </button>
                        );
                      })}
                    </div>}
                    {(
                      <label style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        color: mockDraftReverseRound3 ? "#FFD700" : "rgba(255,255,255,0.6)",
                        fontWeight: "600",
                        transition: "color 0.2s ease",
                      }}>
                        <div
                          onClick={() => setMockDraftReverseRound3(prev => !prev)}
                          style={{
                            width: "36px",
                            height: "20px",
                            borderRadius: "10px",
                            background: mockDraftReverseRound3 ? "rgba(255,215,0,0.4)" : "rgba(255,255,255,0.15)",
                            border: mockDraftReverseRound3 ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.3)",
                            position: "relative",
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                          }}
                        >
                          <div style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: mockDraftReverseRound3 ? "#FFD700" : "rgba(255,255,255,0.5)",
                            position: "absolute",
                            top: "2px",
                            left: mockDraftReverseRound3 ? "19px" : "2px",
                            transition: "all 0.2s ease",
                          }} />
                        </div>
                        3rd Round Reversal
                      </label>
                    )}
                    <label
                      title={isPaid !== 1 ? "Upgrade to AMPLive to unlock" : "Make every pick yourself — built for mock draft videos"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: isPaid === 1 ? "pointer" : "not-allowed",
                        fontSize: "0.85rem",
                        color: isPaid !== 1 ? "rgba(255,255,255,0.3)" : creatorMockMode ? "#FFD700" : "rgba(255,255,255,0.6)",
                        fontWeight: "600",
                        transition: "color 0.2s ease",
                      }}
                    >
                      <div
                        onClick={() => {
                          if (isPaid !== 1) {
                            openUpgradePrompt('creator_mock_mode', 'Slow mocks are an AMPLive feature', 'Make every pick yourself, built for recording mock draft videos.');
                            return;
                          }
                          setCreatorMockMode(prev => !prev);
                        }}
                        style={{
                          width: "36px",
                          height: "20px",
                          borderRadius: "10px",
                          background: creatorMockMode ? "rgba(255,215,0,0.4)" : "rgba(255,255,255,0.15)",
                          border: creatorMockMode ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.3)",
                          position: "relative",
                          transition: "all 0.2s ease",
                          flexShrink: 0,
                          opacity: isPaid !== 1 ? 0.5 : 1,
                        }}
                      >
                        <div style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          background: creatorMockMode ? "#FFD700" : "rgba(255,255,255,0.5)",
                          position: "absolute",
                          top: "2px",
                          left: creatorMockMode ? "19px" : "2px",
                          transition: "all 0.2s ease",
                        }} />
                      </div>
                      Creator Mode{isPaid !== 1 ? ' 🔒' : ''}
                    </label>
                    {/* League Format Toggles */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", width: "100%" }}>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                        <div style={{ display: "inline-flex", borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.2)" }}>
                          {['SF', '1QB'].map(fmt => (
                            <button
                              key={fmt}
                              onClick={() => setScoringFormat(fmt)}
                              style={{
                                padding: "8px 16px",
                                fontSize: "0.8rem",
                                fontWeight: "700",
                                border: "none",
                                backgroundColor: scoringFormat === fmt ? "#FFD700" : "rgba(255,255,255,0.06)",
                                color: scoringFormat === fmt ? "#000" : "rgba(255,255,255,0.5)",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                            >
                              {fmt === 'SF' ? 'SuperFlex' : '1QB'}
                            </button>
                          ))}
                        </div>
                        <label style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          color: tePremium ? "#FFD700" : "rgba(255,255,255,0.5)",
                          fontWeight: "600",
                          transition: "color 0.2s ease",
                        }}>
                          <div
                            onClick={() => setTePremium(prev => !prev)}
                            style={{
                              width: "36px",
                              height: "20px",
                              borderRadius: "10px",
                              background: tePremium ? "rgba(255,215,0,0.4)" : "rgba(255,255,255,0.15)",
                              border: tePremium ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.3)",
                              position: "relative",
                              transition: "all 0.2s ease",
                              flexShrink: 0,
                            }}
                          >
                            <div style={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              background: tePremium ? "#FFD700" : "rgba(255,255,255,0.5)",
                              position: "absolute",
                              top: "2px",
                              left: tePremium ? "19px" : "2px",
                              transition: "all 0.2s ease",
                            }} />
                          </div>
                          TE Premium
                        </label>
                      </div>
                      <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)", fontStyle: "italic" }}>
                        Beta: league settings will auto-populate with league selection in a future release
                      </span>
                    </div>

                    <button
                      onClick={startMockDraft}
                      style={{
                        padding: "10px 28px",
                        fontSize: "1rem",
                        fontWeight: "700",
                        borderRadius: "10px",
                        border: "none",
                        background: "linear-gradient(135deg, #FFD700, #FFA500)",
                        color: "#1a1a2e",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 15px rgba(255,215,0,0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,215,0,0.3)";
                      }}
                    >
                      Start Draft
                    </button>
                  </>
                )}
                {mockDraftActive && (
                  <>
                    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", marginRight: "4px" }}>Speed:</span>
                      {[{ label: "Fast", ms: 300 }, { label: "Normal", ms: 750 }, { label: "Slow", ms: 1500 }].map(s => (
                        <button
                          key={s.label}
                          onClick={() => setMockDraftSpeed(s.ms)}
                          style={{
                            padding: "4px 10px",
                            fontSize: "0.75rem",
                            fontWeight: mockDraftSpeed === s.ms ? "700" : "500",
                            borderRadius: "6px",
                            border: mockDraftSpeed === s.ms ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.2)",
                            background: mockDraftSpeed === s.ms ? "rgba(255,215,0,0.2)" : "transparent",
                            color: mockDraftSpeed === s.ms ? "#FFD700" : "rgba(255,255,255,0.6)",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", fontWeight: "600" }}>
                      Pick {Math.min(mockDraftCurrentPick + 2, sleeperDraftBoard.picks?.length || 0)} of {sleeperDraftBoard.picks?.length || 0}
                    </span>
                    <button
                      onClick={() => setMockDraftPaused(p => !p)}
                      style={{
                        padding: "6px 16px",
                        fontSize: "0.8rem",
                        fontWeight: "700",
                        borderRadius: "8px",
                        border: mockDraftPaused ? "1px solid #10b981" : "1px solid rgba(255,255,255,0.3)",
                        background: mockDraftPaused ? "rgba(16,185,129,0.2)" : "transparent",
                        color: mockDraftPaused ? "#10b981" : "rgba(255,255,255,0.7)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {mockDraftPaused ? "Resume" : "Pause"}
                    </button>
                  </>
                )}
              </div>

              {/* Draft mode toggles */}
              {sleeperSelectedOwner && sleeperDraftBoard?.picks && (
                <div style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: isMobileView ? "10px" : "16px",
                  maxWidth: "700px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}>
                  {/* Need vs Value toggle */}
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    color: draftValueBased ? "#FFD700" : "rgba(255,255,255,0.6)",
                    fontWeight: "600",
                    transition: "color 0.2s ease",
                  }}>
                    <div
                      onClick={() => setDraftValueBased(prev => !prev)}
                      style={{
                        width: "36px",
                        height: "20px",
                        borderRadius: "10px",
                        background: draftValueBased ? "rgba(255,215,0,0.4)" : "rgba(255,255,255,0.15)",
                        border: draftValueBased ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.3)",
                        position: "relative",
                        transition: "all 0.2s ease",
                        flexShrink: 0,
                      }}
                    >
                      <div style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: draftValueBased ? "#FFD700" : "rgba(255,255,255,0.5)",
                        position: "absolute",
                        top: "2px",
                        left: draftValueBased ? "19px" : "2px",
                        transition: "all 0.2s ease",
                      }} />
                    </div>
                    {draftValueBased ? 'Value-Based' : 'Need-Based'}
                  </label>

                  {/* Sleeper ADP toggle — only for 2026 */}
                  {mockDraftYear === 2026 && (
                    <button
                      onClick={() => setUseSleeperAdp(prev => !prev)}
                      style={{
                        background: useSleeperAdp ? "rgba(59,130,246,0.25)" : "rgba(255,255,255,0.08)",
                        border: useSleeperAdp ? "1px solid #3b82f6" : "1px solid rgba(255,255,255,0.2)",
                        color: useSleeperAdp ? "#60a5fa" : "rgba(255,255,255,0.6)",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {useSleeperAdp ? 'Sleeper ADP Active' : 'Use Sleeper ADP'}
                    </button>
                  )}
                </div>
              )}

              {/* Pre-Draft Strategy Suggestion (paid only above board) */}
              {sleeperSelectedOwner && sleeperDraftBoard?.picks && sleeperLeagueInfo?.teams && (
                <div style={{
                  marginTop: isMobileView ? "10px" : "20px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: isMobileView ? "10px" : "14px",
                  padding: isMobileView ? "10px 12px" : "18px 22px",
                  maxWidth: "700px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}>
                  <div
                    onClick={() => setDraftStrategyCollapsed(prev => !prev)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: draftStrategyCollapsed ? 0 : "12px" }}
                  >
                    <span style={{ color: "#FFD700", fontSize: "1rem", fontWeight: "800" }}>Draft Strategy</span>
                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", transition: "transform 0.2s ease", transform: draftStrategyCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▼</span>
                  </div>

                  {!draftStrategyCollapsed && (
                    <>
                      {/* Team selector */}
                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginBottom: "14px" }}>
                        {sleeperLeagueInfo.teams
                          .slice()
                          .sort((a, b) => {
                            if (a.owner_id === sleeperSelectedOwner) return -1;
                            if (b.owner_id === sleeperSelectedOwner) return 1;
                            return (a.display_name || '').localeCompare(b.display_name || '');
                          })
                          .map(tm => {
                            const isActive = (draftStrategyOwnerId || sleeperSelectedOwner) === tm.owner_id;
                            const isUser = tm.owner_id === sleeperSelectedOwner;
                            return (
                              <button
                                key={tm.owner_id}
                                onClick={() => setDraftStrategyOwnerId(tm.owner_id === sleeperSelectedOwner ? null : tm.owner_id)}
                                style={{
                                  padding: "3px 8px",
                                  fontSize: "0.7rem",
                                  fontWeight: isActive ? "700" : "500",
                                  borderRadius: "6px",
                                  border: isActive ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                                  background: isActive ? "rgba(255,215,0,0.15)" : "transparent",
                                  color: isActive ? "#FFD700" : "rgba(255,255,255,0.5)",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {isUser ? `${tm.display_name} (You)` : tm.display_name}
                              </button>
                            );
                          })}
                      </div>

                      {draftStrategy && (
                        <>
                          {/* Position strength overview */}
                          <div style={{ display: "flex", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
                            {['QB', 'RB', 'WR', 'TE'].map(pos => {
                              const st = draftStrategy.stars[pos];
                              if (st == null) return null;
                              const sc = st >= 4 ? '#FFD700' : st >= 3 ? '#10b981' : st >= 2 ? '#f59e0b' : '#ef4444';
                              return (
                                <div key={pos} style={{
                                  display: "flex", alignItems: "center", gap: "5px",
                                  background: "rgba(255,255,255,0.06)", borderRadius: "6px", padding: "4px 10px",
                                }}>
                                  <span style={{ color: draftStrategy.posClrs[pos], fontWeight: "700", fontSize: "0.8rem", textShadow: "0 0 3px #000, 0 0 3px #000" }}>{pos}</span>
                                  <span style={{ fontSize: "0.7rem" }}>
                                    {[1,2,3,4,5].map(j => (
                                      <span key={j} style={{ color: j <= Math.round(st) ? sc : 'rgba(255,255,255,0.15)' }}>★</span>
                                    ))}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Strategy lines */}
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {draftStrategy.lines.map((line, i) => (
                              <div key={i} style={{
                                fontSize: "0.85rem",
                                lineHeight: "1.5",
                                color: line.type === 'note' ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.7)',
                                fontStyle: line.type === 'note' ? 'italic' : 'normal',
                                paddingLeft: line.type === 'target' ? '12px' : '0',
                                borderLeft: line.type === 'target' ? `2px solid ${draftStrategy.posClrs[line.pos] || 'rgba(255,255,255,0.2)'}` : 'none',
                              }}>
                                {line.text}
                              </div>
                            ))}
                          </div>

                          {/* Pick overview */}
                          <div style={{ marginTop: "14px" }}>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                              {draftStrategy.pickTargets.map(pt => {
                                const isExpanded = strategyExpandedPick === pt.idx;
                                return (
                                  <button
                                    key={pt.idx}
                                    onClick={() => setStrategyExpandedPick(isExpanded ? null : pt.idx)}
                                    style={{
                                      fontSize: "0.75rem",
                                      fontWeight: "700",
                                      color: isExpanded ? "#1a1a2e" : "#FFD700",
                                      background: isExpanded ? "#FFD700" : "rgba(255,215,0,0.12)",
                                      border: "1px solid rgba(255,215,0,0.3)",
                                      borderRadius: "6px",
                                      padding: "3px 8px",
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    {pt.pickLabel}
                                  </button>
                                );
                              })}
                              <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)" }}>
                                {draftStrategy.isMyTeam ? 'Your picks' : `${draftStrategy.teamName}'s picks`}
                              </span>
                            </div>
                            {strategyExpandedPick != null && (() => {
                              const pt = draftStrategy.pickTargets.find(p => p.idx === strategyExpandedPick);
                              if (!pt || pt.candidates.length === 0) return null;
                              const posClrs = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
                              return (
                                <div style={{
                                  marginTop: "10px",
                                  background: "rgba(255,255,255,0.04)",
                                  border: "1px solid rgba(255,255,255,0.1)",
                                  borderRadius: "10px",
                                  padding: "10px 14px",
                                }}>
                                  <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", marginBottom: "8px" }}>
                                    Players projected near pick {pt.pickLabel}
                                  </div>
                                  {pt.candidates.map(player => {
                                    const pos = player.position || player.metrics?.position || '';
                                    const name = `${player.first_name} ${player.last_name}`;
                                    return (
                                      <div key={player.id} style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "5px 0",
                                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                                      }}>
                                        <span style={{
                                          fontSize: "0.65rem",
                                          fontWeight: "700",
                                          color: posClrs[pos] || '#888',
                                          background: (posClrs[pos] || '#888') + '22',
                                          borderRadius: "4px",
                                          padding: "1px 6px",
                                          minWidth: "28px",
                                          textAlign: "center",
                                          textShadow: "0 0 3px #000, 0 0 3px #000",
                                        }}>{pos}</span>
                                        <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.8)", fontWeight: "600", flex: 1 }}>{name}</span>
                                        <span style={{ fontSize: "0.65rem", color: isCffDraft ? "#10b981" : "rgba(255,255,255,0.35)" }}>{isCffDraft ? `CFF #${player.cff_rank}` : `Rank ${player.rank}`}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* User Pick Inline Section */}
              {mockDraftPaused && sleeperDraftBoard.picks && (
                <div style={{
                  marginTop: "24px",
                  background: "linear-gradient(135deg, rgba(255,215,0,0.12) 0%, rgba(255,165,0,0.06) 100%)",
                  border: "2px solid #FFD700",
                  borderRadius: "16px",
                  padding: "16px 24px",
                  textAlign: "center",
                  boxShadow: "0 0 30px rgba(255,215,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)",
                  animation: "clockPulse 2s ease-in-out infinite",
                  position: "relative",
                  overflow: "hidden",
                }}>
                  <style>{`@keyframes clockPulse { 0%, 100% { box-shadow: 0 0 30px rgba(255,215,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08); } 50% { box-shadow: 0 0 50px rgba(255,215,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08); } }`}</style>
                  <div style={{ fontSize: "0.7rem", fontWeight: "800", color: "rgba(255,215,0,0.6)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "4px" }}>
                    ⏱ Your Pick
                  </div>
                  <h3 style={{
                    color: "#FFD700",
                    fontSize: "1.3rem",
                    fontWeight: "900",
                    margin: 0,
                    textShadow: "0 0 20px rgba(255,215,0,0.5)",
                  }}>
                    On the Clock — {sleeperDraftBoard.picks[mockDraftCurrentPick]?.pick_label}
                  </h3>
                </div>
              )}

              {sleeperDraftBoard.draft_order_set === false && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px 20px",
                    marginBottom: "20px",
                    background: "rgba(245, 158, 11, 0.2)",
                    border: "1px solid rgba(245, 158, 11, 0.5)",
                    borderRadius: "12px",
                    color: "#fcd34d",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                  }}
                >
                  Draft order hasn't been set yet — pick positions may change
                </div>
              )}

              <div style={{ textAlign: "center", marginTop: isMobileView ? "12px" : "24px", marginBottom: isMobileView ? "4px" : "10px" }}>
                <h3 style={{ fontSize: isMobileView ? "1rem" : "1.3rem", fontWeight: "700", color: "#FFD700", margin: "0 0 4px" }}>
                  {sleeperLeagueInfo?.league_name}
                </h3>
                <button
                  onClick={() => setShowDraftReadMe(prev => !prev)}
                  style={{
                    background: "none",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "6px",
                    color: "rgba(255,255,255,0.4)",
                    fontSize: "0.7rem",
                    padding: "2px 10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {showDraftReadMe ? 'Hide' : 'Read me'}
                </button>
                {showDraftReadMe && (
                  <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem", marginTop: "6px", fontStyle: "italic" }}>
                    Click on player names to view profiles, click into top right of card to override and reset a draft slot. Star rating denotes relative strength of a position group from a dynasty perspective. Stay Amped!
                  </div>
                )}
              </div>

              {/* Roster Panel Toggle */}
              {sleeperSelectedOwner && sleeperRosters && sleeperPlayerNames && (
                <div style={{ textAlign: "right", marginBottom: "6px" }}>
                  <button
                    onClick={() => setShowRosterPanel(prev => !prev)}
                    style={{
                      background: showRosterPanel ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.06)",
                      border: showRosterPanel ? "1px solid rgba(255,215,0,0.4)" : "1px solid rgba(255,255,255,0.15)",
                      borderRadius: "8px",
                      color: showRosterPanel ? "#FFD700" : "rgba(255,255,255,0.5)",
                      fontSize: "0.7rem",
                      fontWeight: "700",
                      padding: "4px 12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {showRosterPanel ? "Hide Roster ◁" : "My Roster ▷"}
                  </button>
                </div>
              )}

              <div style={{ display: "flex", gap: isMobileView ? "0px" : "12px", flexDirection: isMobileView ? "column" : "row" }}>

              {/* Roster Side Panel */}
              {showRosterPanel && !isMobileView && sleeperSelectedOwner && sleeperRosters && sleeperPlayerNames && (() => {
                const ownerId = sleeperSelectedOwner;
                const playerIds = sleeperRosters[ownerId] || [];
                const posOrder = ['QB', 'RB', 'WR', 'TE'];
                const posColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
                const idealCounts = { QB: 3, RB: 5, WR: 5, TE: 2 };
                const grouped = {};
                playerIds.forEach(id => {
                  const player = sleeperPlayerNames[id];
                  if (player && posOrder.includes(player.position)) {
                    if (!grouped[player.position]) grouped[player.position] = [];
                    grouped[player.position].push({ ...player, _drafted: false });
                  }
                });
                // Add user-drafted players from mock draft
                const draftedByUser = Object.values(mockDraftResults).filter(r => r?.isUserPick);
                draftedByUser.forEach(r => {
                  const pos = r.player.metrics?.position || r.player.position;
                  if (pos && posOrder.includes(pos)) {
                    if (!grouped[pos]) grouped[pos] = [];
                    const fullName = (r.player.first_name + ' ' + r.player.last_name).trim();
                    // Avoid duplicates if somehow already on roster
                    if (!grouped[pos].some(p => p.name?.trim() === fullName)) {
                      grouped[pos].push({ name: fullName, position: pos, _drafted: true });
                    }
                  }
                });
                // Sort each group by AMP rank
                posOrder.forEach(pos => {
                  if (grouped[pos]) {
                    grouped[pos].sort((a, b) => {
                      const aRank = playerValues?.[a.name.trim()]?.amp_rank;
                      const bRank = playerValues?.[b.name.trim()]?.amp_rank;
                      if (aRank != null && bRank != null) return bRank - aRank;
                      if (aRank != null) return -1;
                      if (bRank != null) return 1;
                      return 0;
                    });
                  }
                });
                // Compute team stars for gap highlighting
                const teamStars = (() => {
                  if (!playerValues || !sleeperLeagueInfo?.teams) return {};
                  const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };
                  // Base star ratings from existing rosters (league-relative)
                  const posScores = {};
                  sleeperLeagueInfo.teams.forEach(team => {
                    const oid = team.owner_id;
                    const pids = sleeperRosters[oid] || [];
                    const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
                    Object.entries(topNC).forEach(([pos, topN]) => {
                      const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
                      if (rks.length > 0) {
                        if (!posScores[pos]) posScores[pos] = {};
                        posScores[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
                      }
                    });
                  });
                  const baseStars = {};
                  Object.entries(posScores).forEach(([pos, scores]) => {
                    const vals = Object.values(scores);
                    const mn = Math.min(...vals);
                    const mx = Math.max(...vals);
                    const rng = mx - mn;
                    const sc = scores[ownerId];
                    if (sc != null) baseStars[pos] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
                  });
                  // Add round-based star bumps from mock-drafted players
                  // 1st round = +1 star, 2nd round = +0.5 star, 3rd+ = +0.25 star
                  const draftBumps = { QB: 0, RB: 0, WR: 0, TE: 0 };
                  (sleeperDraftBoard?.picks || []).forEach((pick, idx) => {
                    const result = mockDraftResults[idx];
                    if (result && pick.current_owner_id === ownerId) {
                      const pos = result.player.position || result.player.metrics?.position || '';
                      if (pos && draftBumps[pos] !== undefined) {
                        const round = pick.round || Math.floor(idx / (sleeperDraftBoard.num_teams || 12)) + 1;
                        draftBumps[pos] += round === 1 ? 1 : round === 2 ? 0.5 : 0.25;
                      }
                    }
                  });
                  const res = {};
                  ['QB', 'RB', 'WR', 'TE'].forEach(pos => {
                    const base = baseStars[pos] ?? 3;
                    res[pos] = Math.min(5, Math.round((base + draftBumps[pos]) * 2) / 2);
                  });
                  return res;
                })();
                const teamObj = sleeperLeagueInfo?.teams?.find(t => t.owner_id === ownerId);
                return (
                  <div style={{
                    width: "240px",
                    flexShrink: 0,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "12px",
                    padding: "12px 10px",
                    overflowY: "auto",
                    maxHeight: "80vh",
                    position: "sticky",
                    top: "10px",
                  }}>
                    <h4 style={{ color: "#FFD700", fontSize: "0.85rem", fontWeight: "800", margin: "0 0 10px", textAlign: "center" }}>
                      {teamObj?.display_name || "My Roster"}
                    </h4>
                    {posOrder.map(pos => {
                      const players = grouped[pos] || [];
                      const count = players.length;
                      const ideal = idealCounts[pos];
                      const stars = teamStars[pos];
                      const isGap = stars != null && stars <= 1.5;
                      const isStrength = stars != null && stars >= 4;
                      const gapColor = isGap ? "rgba(239,68,68,0.15)" : isStrength ? "rgba(16,185,129,0.08)" : "transparent";
                      const sColor = stars != null ? (stars >= 4 ? '#FFD700' : stars >= 3 ? '#10b981' : stars >= 2 ? '#f59e0b' : '#ef4444') : null;
                      return (
                        <div key={pos} style={{
                          marginBottom: "8px",
                          background: gapColor,
                          borderRadius: "8px",
                          padding: "6px 8px",
                          border: isGap ? "1px solid rgba(239,68,68,0.3)" : isStrength ? "1px solid rgba(16,185,129,0.2)" : "1px solid transparent",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span style={{
                                color: "#fff",
                                fontWeight: "800",
                                fontSize: "0.6rem",
                                background: posColors[pos],
                                padding: "1px 6px",
                                borderRadius: "3px",
                              }}>{pos}</span>
                              <span style={{ fontSize: "0.55rem", color: "rgba(255,255,255,0.35)" }}>
                                {count}/{ideal}
                              </span>
                            </div>
                            {stars != null && (
                              <span style={{ fontSize: "0.5rem", letterSpacing: "0px" }}>
                                {[1,2,3,4,5].map(j => {
                                  if (j <= Math.floor(stars)) return <span key={j} style={{ color: sColor }}>★</span>;
                                  if (j - 0.5 <= stars) return <span key={j} style={{ position: 'relative', display: 'inline-block' }}><span style={{ color: 'rgba(255,255,255,0.15)' }}>★</span><span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: '50%', color: sColor }}>★</span></span>;
                                  return <span key={j} style={{ color: 'rgba(255,255,255,0.15)' }}>★</span>;
                                })}
                              </span>
                            )}
                          </div>
                          {isGap && (
                            <div style={{ fontSize: "0.5rem", color: "#ef4444", fontWeight: "700", marginBottom: "3px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Gap — Draft Target
                            </div>
                          )}
                          {players.map((player, j) => {
                            const pv = playerValues?.[player.name.trim()];
                            const rank = pv?.amp_rank;
                            const tierColor = rank != null ? (rank >= 80 ? '#FFD700' : rank >= 60 ? '#10b981' : rank >= 40 ? '#fff' : 'rgba(255,255,255,0.4)') : 'rgba(255,255,255,0.4)';
                            return (
                              <div key={j} style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "1px 0",
                                fontSize: "0.7rem",
                              }}>
                                <span style={{ color: player._drafted ? '#FFD700' : tierColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: player._drafted ? "130px" : "160px", fontWeight: player._drafted ? "700" : "normal" }}>
                                  {player.name.trim()}
                                  {player._drafted && <span style={{ fontSize: "0.5rem", color: "#FFD700", marginLeft: "4px", fontWeight: "800", verticalAlign: "middle" }}>NEW</span>}
                                </span>
                                {rank != null && (
                                  <span style={{ fontSize: "0.6rem", color: "rgba(255,215,0,0.6)", fontWeight: "600", flexShrink: 0, marginLeft: "4px" }}>
                                    {rank}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {count === 0 && (
                            <div style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.25)", fontStyle: "italic" }}>Empty</div>
                          )}
                        </div>
                      );
                    })}
                    {/* Newly drafted players indicator */}
                    {(() => {
                      const draftedByUser = Object.values(mockDraftResults).filter(r => r?.isUserPick);
                      if (draftedByUser.length === 0) return null;
                      return (
                        <div style={{ marginTop: "8px", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "8px" }}>
                          <div style={{ fontSize: "0.65rem", color: "#FFD700", fontWeight: "700", marginBottom: "4px" }}>
                            Drafted This Mock
                          </div>
                          {draftedByUser.map((r, i) => (
                            <div key={i} style={{ fontSize: "0.65rem", color: "#fff", padding: "1px 0", display: "flex", justifyContent: "space-between" }}>
                              <span>{r.player.first_name?.[0]}. {r.player.last_name}</span>
                              <span style={{ color: posColors[r.player.metrics?.position] || "#999", fontWeight: "700" }}>{r.player.metrics?.position}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              <div style={{ flex: 1, minWidth: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                {/* Team Name Headers */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${sleeperDraftBoard.num_teams || 12}, minmax(${isMobileView ? '62px' : '110px'}, 1fr))`,
                    gap: isMobileView ? "3px" : "6px",
                    minWidth: `${(sleeperDraftBoard.num_teams || 12) * (isMobileView ? 65 : 116)}px`,
                    marginBottom: isMobileView ? "3px" : "6px",
                  }}
                >
                  {Array.from({ length: sleeperDraftBoard.num_teams || 12 }, (_, i) => {
                    const colPicks = (sleeperDraftBoard.picks || []).filter(p => (p.slot || p.pick_in_round) === i + 1);
                    const round1Pick = colPicks.find(p => p.round === 1) || colPicks[0];
                    const colOwnerId = round1Pick?.original_owner_id;
                    const teamObj = sleeperLeagueInfo?.teams?.find(t => t.owner_id === colOwnerId);
                    const isUser = colOwnerId === sleeperSelectedOwner;
                    return (
                      <div
                        key={`header-${i}`}
                        style={{
                          textAlign: "center",
                          fontSize: isMobileView ? "0.5rem" : "0.7rem",
                          fontWeight: "700",
                          color: isUser ? "#FFD700" : "rgba(255,255,255,0.7)",
                          padding: isMobileView ? "2px 1px" : "4px 2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textShadow: isUser ? "0 0 6px rgba(255,215,0,0.4)" : "none",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {teamObj?.display_name || `Team ${i + 1}`}
                      </div>
                    );
                  })}
                </div>

                {/* Team Needs Panel - hidden on mobile */}
                <div style={{
                  maxHeight: showTeamNeeds && sleeperTeamNeeds && !isMobileView ? "400px" : "0px",
                  opacity: showTeamNeeds && sleeperTeamNeeds && !isMobileView ? 1 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.4s ease, opacity 0.3s ease",
                  minWidth: `${(sleeperDraftBoard.num_teams || 12) * (isMobileView ? 65 : 116)}px`,
                }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${sleeperDraftBoard.num_teams || 12}, minmax(${isMobileView ? '62px' : '110px'}, 1fr))`,
                      gap: isMobileView ? "3px" : "6px",
                      minWidth: `${(sleeperDraftBoard.num_teams || 12) * (isMobileView ? 65 : 116)}px`,
                      marginBottom: isMobileView ? "3px" : "6px",
                    }}
                  >
                    {Array.from({ length: sleeperDraftBoard.num_teams || 12 }, (_, i) => {
                      // Find the original team for this column using slot (fixed team position)
                      const colPicks = (sleeperDraftBoard.picks || []).filter(p => (p.slot || p.pick_in_round) === i + 1);
                      const round1Pick = colPicks.find(p => p.round === 1) || colPicks[0];
                      const colOwnerId = round1Pick?.original_owner_id;
                      const teamData = sleeperTeamNeeds?.teams?.[colOwnerId];
                      return [colOwnerId, teamData];
                    }).map(([colOwnerId, teamData], i) => {
                      const counts = teamData?.counts || {};
                      const needs = teamData?.needs || {};
                      return (
                        <div
                          key={`needs-${i}`}
                          onClick={() => {
                            if (sleeperRosters && sleeperPlayerNames && colOwnerId) {
                              setRosterModalOwnerId(colOwnerId);
                            }
                          }}
                          style={{
                            padding: "8px 6px",
                            background: "rgba(255,255,255,0.04)",
                            border: "2px solid rgba(0,0,0,0.8)",
                            borderRadius: "8px",
                            textAlign: "center",
                            fontSize: "0.65rem",
                            cursor: sleeperRosters && sleeperPlayerNames && colOwnerId ? "pointer" : "default",
                            transition: "all 0.2s ease",
                            overflow: "hidden",
                            minWidth: 0,
                            outline: "2px solid rgba(0,0,0,0.7)",
                            outlineOffset: "-1px",
                          }}
                          onMouseEnter={(e) => {
                            if (sleeperRosters && sleeperPlayerNames && colOwnerId) {
                              e.currentTarget.style.background = "rgba(255,215,0,0.1)";
                              e.currentTarget.style.borderColor = "rgba(255,215,0,0.3)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                          }}
                        >
                          {(() => {
                            if (!playerValues || !sleeperRosters || !sleeperPlayerNames || !sleeperLeagueInfo?.teams) return null;
                            const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };
                            const posScoresLocal = {};
                            sleeperLeagueInfo.teams.forEach(tm => {
                              const oid = tm.owner_id;
                              const pids = sleeperRosters[oid] || [];
                              const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
                              Object.entries(topNC).forEach(([pos, topN]) => {
                                const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
                                if (rks.length > 0) {
                                  if (!posScoresLocal[pos]) posScoresLocal[pos] = {};
                                  posScoresLocal[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
                                }
                              });
                            });
                            const colStars = {};
                            Object.entries(posScoresLocal).forEach(([pos, scores]) => {
                              const vals = Object.values(scores);
                              const mn = Math.min(...vals);
                              const mx = Math.max(...vals);
                              const rng = mx - mn;
                              const sc = scores[colOwnerId];
                              if (sc != null) colStars[pos] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
                            });
                            // Overall star: average amp_rank of top 12 players per team
                            const totalScores = {};
                            sleeperLeagueInfo.teams.forEach(tm => {
                              const oid = tm.owner_id;
                              const pids = sleeperRosters[oid] || [];
                              const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
                              const topRanks = pls.map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, 12);
                              if (topRanks.length > 0) totalScores[oid] = topRanks.reduce((s, r) => s + r, 0) / topRanks.length;
                            });
                            const totalVals = Object.values(totalScores);
                            const totalMn = Math.min(...totalVals);
                            const totalMx = Math.max(...totalVals);
                            const totalRng = totalMx - totalMn;
                            const totalStar = totalScores[colOwnerId] != null
                              ? (totalRng === 0 ? 3 : Math.round(((totalScores[colOwnerId] - totalMn) / totalRng) * 10) / 2)
                              : null;
                            const posOrder = ['QB', 'RB', 'WR', 'TE'];
                            const posClrs = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
                            return Object.keys(colStars).length > 0 ? (
                              <div style={{ marginBottom: "4px", lineHeight: "1.6", display: "flex", flexDirection: "column", alignItems: "center", gap: "1px" }}>
                                {posOrder.filter(pos => colStars[pos] != null).map(pos => {
                                  const st = colStars[pos];
                                  const sc = st >= 4 ? '#FFD700' : st >= 3 ? '#10b981' : st >= 2 ? '#f59e0b' : '#ef4444';
                                  return (
                                    <div key={pos} style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '1px 5px' }}>
                                      <span style={{ color: posClrs[pos], fontWeight: '700', fontSize: '0.6rem', width: '18px', textShadow: '0 0 3px #000, 0 0 3px #000' }}>{pos}</span>
                                      <span style={{ fontSize: '0.55rem', letterSpacing: '0px' }}>
                                        {[1,2,3,4,5].map(j => {
                                          if (j <= Math.floor(st)) return <span key={j} style={{ color: sc }}>★</span>;
                                          if (j - 0.5 <= st) return <span key={j} style={{ position: 'relative', display: 'inline-block' }}><span style={{ color: 'rgba(255,255,255,0.15)' }}>★</span><span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: '50%', color: sc }}>★</span></span>;
                                          return <span key={j} style={{ color: 'rgba(255,255,255,0.15)' }}>★</span>;
                                        })}
                                      </span>
                                    </div>
                                  );
                                })}
                                {totalStar != null && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.10)', borderRadius: '4px', padding: '1px 5px', marginTop: '2px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                    <span style={{ color: '#c0c0c0', fontWeight: '700', fontSize: '0.55rem', width: '18px', textShadow: '0 0 3px #000, 0 0 3px #000' }}>OVR</span>
                                    <span style={{ fontSize: '0.55rem', letterSpacing: '0px' }}>
                                      {[1,2,3,4,5].map(j => {
                                        const tc = totalStar >= 4 ? '#FFD700' : totalStar >= 3 ? '#10b981' : totalStar >= 2 ? '#f59e0b' : '#ef4444';
                                        if (j <= Math.floor(totalStar)) return <span key={j} style={{ color: tc }}>★</span>;
                                        if (j - 0.5 <= totalStar) return <span key={j} style={{ position: 'relative', display: 'inline-block' }}><span style={{ color: 'rgba(255,255,255,0.15)' }}>★</span><span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: '50%', color: tc }}>★</span></span>;
                                        return <span key={j} style={{ color: 'rgba(255,255,255,0.15)' }}>★</span>;
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : Object.keys(counts).length > 0 ? (
                              <div style={{ color: "rgba(255,255,255,0.4)", marginBottom: "4px", lineHeight: "1.4", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "2px 4px" }}>
                                {Object.entries(counts).map(([pos, count]) => (
                                  <span key={pos} style={{ whiteSpace: "nowrap" }}>{pos}:{count}</span>
                                ))}
                              </div>
                            ) : null;
                          })()}
                          {!teamData && (
                            <div style={{ color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>No data</div>
                          )}
                          <div style={{
                            color: "rgba(255,215,0,0.6)",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            marginTop: "4px",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                          }}>
                            View Roster
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${sleeperDraftBoard.num_teams || 12}, minmax(${isMobileView ? '62px' : '110px'}, 1fr))`,
                    gap: isMobileView ? "3px" : "6px",
                    minWidth: `${(sleeperDraftBoard.num_teams || 12) * (isMobileView ? 65 : 116)}px`,
                  }}
                >
                  {/* Draft Picks Grid */}
                  {sleeperDraftBoard.picks && sleeperDraftBoard.picks.map((pick, idx) => {
                    const result = mockDraftResults[idx];
                    const isCurrentPick = mockDraftActive && idx === mockDraftCurrentPick;
                    const isUpcoming = mockDraftActive && !mockDraftComplete && idx > mockDraftCurrentPick;
                    const resultPos = result?.player?.metrics?.position || result?.player?.position;
                    const posColors = { QB: "rgba(239,68,68,0.25)", RB: "rgba(59,130,246,0.25)", WR: "rgba(34,197,94,0.25)", TE: "rgba(234,179,8,0.25)" };
                    const posBorderColors = { QB: "rgba(239,68,68,0.6)", RB: "rgba(59,130,246,0.6)", WR: "rgba(34,197,94,0.6)", TE: "rgba(234,179,8,0.6)" };
                    return (
                      <div
                        key={idx}
                        onClick={() => { if (result && !isCurrentPick) setOverridingPickIndex(idx); }}
                        title={result && !isCurrentPick ? "Click to override" : undefined}
                        style={{
                          gridRow: pick.round,
                          gridColumn: pick.slot,
                          padding: isMobileView ? "4px 2px" : "10px 8px",
                          textAlign: "center",
                          borderRadius: isMobileView ? "6px" : "10px",
                          cursor: result && !isCurrentPick ? "pointer" : undefined,
                          border: reassigningPickIndex === idx
                            ? "2px solid #f59e0b"
                            : isCurrentPick
                            ? "2px solid #10b981"
                            : result?.isUserPick
                              ? "2px solid #FFD700"
                              : result && posBorderColors[resultPos]
                                ? "2px solid " + posBorderColors[resultPos]
                                : pick.is_my_pick
                                  ? "2px solid #FFD700"
                                  : "2px solid rgba(0,0,0,0.8)",
                          outline: "2px solid rgba(0,0,0,0.7)",
                          outlineOffset: "-1px",
                          background: result?.isUserPick
                            ? "rgba(255,215,0,0.22)"
                            : result && posColors[resultPos]
                              ? posColors[resultPos]
                              : result
                                ? "rgba(255,255,255,0.08)"
                                : pick.is_my_pick
                                  ? "rgba(255,215,0,0.18)"
                                  : "rgba(255,255,255,0.05)",
                          transition: "all 0.2s ease",
                          position: "relative",
                          opacity: isUpcoming ? 0.4 : 1,
                          overflow: reassigningPickIndex === idx ? "visible" : "hidden",
                          minWidth: 0,
                        }}
                      >
                        {result?.isUserPick && (
                          <div style={{
                            position: "absolute",
                            top: isMobileView ? "1px" : "3px",
                            left: isMobileView ? "2px" : "6px",
                            fontSize: isMobileView ? "0.35rem" : "0.55rem",
                            fontWeight: "800",
                            color: "#1a1a2e",
                            background: "#FFD700",
                            borderRadius: "3px",
                            padding: isMobileView ? "0px 2px" : "1px 4px",
                            lineHeight: "1.2",
                          }}>
                            YOU
                          </div>
                        )}
                        {result?.isOverridden && !result?.isUserPick && (
                          <div style={{
                            position: "absolute",
                            top: "3px",
                            right: "6px",
                            fontSize: "0.7rem",
                            fontWeight: "800",
                            color: "#f59e0b",
                            lineHeight: "1",
                          }} title="Overridden pick">
                            ↻
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: isMobileView ? (result ? "0.45rem" : "0.55rem") : (result ? "0.7rem" : "0.95rem"),
                            fontWeight: pick.is_my_pick ? "800" : "600",
                            color: pick.is_my_pick ? "#FFD700" : "rgba(255,255,255,0.5)",
                            marginBottom: result ? "1px" : "2px",
                          }}
                        >
                          {pick.pick_label}
                        </div>
                        {isCurrentPick && mockDraftPaused && !result && (
                          <div style={{
                            fontSize: "0.7rem",
                            fontWeight: "800",
                            color: "#10b981",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}>
                            On the Clock
                          </div>
                        )}
                        {result ? (
                          <div style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {(result.player.headshot_url || result.player.image || result.player.nfl_team_image || result.player.team_image) && (
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobileView ? "2px" : "4px", marginBottom: isMobileView ? "2px" : "4px" }}>
                                {(result.player.headshot_url || result.player.image) && (
                                  <img
                                    src={result.player.headshot_url || result.player.image}
                                    alt=""
                                    style={{
                                      width: isMobileView ? "20px" : "36px",
                                      height: isMobileView ? "20px" : "36px",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                      border: result.isUserPick ? (isMobileView ? "1px solid #FFD700" : "2px solid #FFD700") : "1px solid rgba(255,255,255,0.2)",
                                    }}
                                  />
                                )}
                                {(result.player.nfl_team_image || result.player.team_image) && (
                                  <img
                                    src={result.player.nfl_team_image || result.player.team_image}
                                    alt=""
                                    style={{
                                      width: isMobileView ? "14px" : "22px",
                                      height: isMobileView ? "14px" : "22px",
                                      objectFit: "contain",
                                      flexShrink: 0,
                                      opacity: 0.85,
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            <a
                              href={`https://fantasyamp.com/player/${(result.player.first_name + '_' + result.player.last_name).toLowerCase().replace(/[^a-z_]/g, '')}`}
                              target="_blank"
                              rel="noopener"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: isMobileView ? "0.5rem" : "0.8rem",
                                fontWeight: "700",
                                color: result.isUserPick ? "#FFD700" : "#fff",
                                lineHeight: "1.2",
                                textDecoration: "none",
                                display: "block",
                              }}
                            >
                              {isMobileView ? result.player.last_name : `${result.player.first_name?.[0]}. ${result.player.last_name}`}
                            </a>
                            <div style={{
                              fontSize: isMobileView ? "0.45rem" : "0.65rem",
                              color: "rgba(255,255,255,0.5)",
                              marginTop: "1px",
                            }}>
                              {result.player.metrics?.position || "—"}
                            </div>
                            {result.player.amp_momentum_score && mockDraftYear === 2026 && (isLoggedIn || window.location.hostname === 'localhost') && (
                              <div style={{
                                fontSize: "0.6rem",
                                fontWeight: "700",
                                color: "rgba(255,255,255,0.7)",
                                marginTop: "2px",
                              }}>
                                AMP {Math.round(parseFloat(calculateCompositeAmpScore(result.player)))}
                              </div>
                            )}
                            {mockDraftYear > 2026 && (
                              <a
                                href={`https://fantasyamp.com/player/${(result.player.first_name + '_' + result.player.last_name).toLowerCase().replace(/[^a-z_]/g, '')}`}
                                target="_blank"
                                rel="noopener"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  fontSize: "0.55rem",
                                  fontWeight: "600",
                                  color: "rgba(255,215,0,0.7)",
                                  marginTop: "2px",
                                  textDecoration: "none",
                                  display: "block",
                                }}
                              >
                                View Profile
                              </a>
                            )}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: pick.is_my_pick ? "rgba(255,215,0,0.8)" : "rgba(255,255,255,0.5)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {pick.current_owner_name}
                          </div>
                        )}
                        {!mockDraftActive && !mockDraftComplete && !result && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setReassigningPickIndex(reassigningPickIndex === idx ? null : idx); }}
                            title="Reassign pick"
                            style={{
                              position: "absolute",
                              top: "2px",
                              left: "4px",
                              fontSize: "0.6rem",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0",
                              lineHeight: "1",
                              opacity: reassigningPickIndex === idx ? 1 : 0.5,
                              transition: "opacity 0.15s ease",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => { if (reassigningPickIndex !== idx) e.currentTarget.style.opacity = '0.5'; }}
                          >
                            🔄
                          </button>
                        )}
                        {pick.was_traded && !result && (
                          <div
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "6px",
                              fontSize: "0.65rem",
                              color: "#f59e0b",
                              fontWeight: "700",
                            }}
                            title="This pick was traded"
                          >
                            T
                          </div>
                        )}
                        {reassigningPickIndex === idx && (
                          <>
                            <div
                              onClick={() => setReassigningPickIndex(null)}
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 99,
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: "#1a1a2e",
                                border: "1px solid rgba(255,255,255,0.2)",
                                borderRadius: "8px",
                                zIndex: 100,
                                maxHeight: "200px",
                                overflowY: "auto",
                                width: "140px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                              }}
                            >
                              {(sleeperLeagueInfo?.teams || [])
                                .slice()
                                .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
                                .map(team => (
                                  <div
                                    key={team.owner_id}
                                    onClick={(e) => { e.stopPropagation(); handleReassignPick(idx, team.owner_id, team.display_name); }}
                                    style={{
                                      padding: "6px 10px",
                                      fontSize: "0.75rem",
                                      color: team.owner_id === pick.current_owner_id ? "#f59e0b" : "rgba(255,255,255,0.8)",
                                      cursor: "pointer",
                                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                                      fontWeight: team.owner_id === pick.current_owner_id ? "700" : "400",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    {team.display_name}
                                  </div>
                                ))
                              }
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>{/* close flex wrapper */}
            </div>
          )}

          {/* Upgrade promo for unpaid users mid-mock */}
          {isPaid !== 1 && sleeperDraftBoard && (
            <div style={{
              textAlign: "center",
              padding: "14px 20px",
              marginTop: "16px",
              background: "rgba(255,215,0,0.08)",
              border: "1px solid rgba(255,215,0,0.25)",
              borderRadius: "10px",
            }}>
              {(() => {
                const promoNames = ['Jeremiah Smith', 'Arch Manning', 'Malachi Toney'];
                const promoPlayers = promoNames.map(name => formatAdjustedPlayers.find(p => (p.first_name + ' ' + p.last_name).trim() === name)).filter(Boolean);
                return promoPlayers.length > 0 ? (
                  <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginBottom: "10px" }}>
                    {promoPlayers.map((p, i) => (
                      <div key={i} style={{ textAlign: "center" }}>
                        {(p.headshot_url || p.image) && (
                          <img
                            src={p.headshot_url || p.image}
                            alt={(p.first_name + ' ' + p.last_name).trim()}
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid rgba(255,215,0,0.4)",
                            }}
                          />
                        )}
                        <div style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.5)", marginTop: "2px", fontWeight: "600" }}>
                          {p.first_name?.[0]}. {p.last_name}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", marginBottom: "8px" }}>
                Drafting on the free plan. Upgrade to <span style={{ color: "#FFD700", fontWeight: "700" }}>AMPLive</span> for unlimited synced mocks, full mock drafts, and rookie pick valuation.
              </div>
              <button
                onClick={() => window.open('https://fantasyamp.com/products', '_blank')}
                style={{
                  padding: "6px 18px",
                  fontSize: "0.8rem",
                  fontWeight: "700",
                  borderRadius: "8px",
                  border: "1px solid #FFD700",
                  background: "rgba(255,215,0,0.2)",
                  color: "#FFD700",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.2)"; }}
              >
                Upgrade to AMPLive for $10/mo
              </button>
            </div>
          )}

          {/* Pick selection below draft board */}
          {mockDraftPaused && sleeperDraftBoard?.picks && (
            <div style={{
              marginTop: "20px",
              background: "rgba(255,215,0,0.06)",
              border: "1px solid rgba(255,215,0,0.2)",
              borderRadius: "12px",
              padding: "16px 20px",
            }}>
              {/* Personalized Draft Plan — Paid Feature */}
              {personalizedDraftPlan && personalizedDraftPlan.picks.length > 0 && (isPaid === 1 || window.location.hostname === 'localhost') && (() => {
                const currentPick = personalizedDraftPlan.picks.find(p => p.isCurrentPick);
                const futurePicks = personalizedDraftPlan.picks.filter(p => !p.isCurrentPick);
                const posColors = personalizedDraftPlan.posColors;
                return (
                  <div style={{
                    marginBottom: "16px",
                    background: "linear-gradient(135deg, rgba(255,215,0,0.08), rgba(255,215,0,0.02))",
                    border: "1px solid rgba(255,215,0,0.3)",
                    borderRadius: "12px",
                    padding: "16px",
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                    }}>
                      <span style={{ fontSize: "1rem" }}>&#x1F3AF;</span>
                      <span style={{
                        color: "#FFD700",
                        fontSize: "0.85rem",
                        fontWeight: "800",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                      }}>
                        Your Draft Plan
                      </span>
                      <span style={{
                        flex: 1,
                        height: "1px",
                        background: "linear-gradient(to right, rgba(255,215,0,0.4), transparent)",
                      }} />
                    </div>

                    {/* Current Pick Directive */}
                    {currentPick && currentPick.recommendation && isPaid !== 1 && (
                      <div style={{
                        padding: "14px 16px",
                        borderRadius: "10px",
                        background: "rgba(255,215,0,0.05)",
                        border: "2px dashed rgba(255,215,0,0.2)",
                        textAlign: "center",
                        marginBottom: "12px",
                      }}>
                        <div style={{ fontSize: "1.1rem", marginBottom: "4px" }}>🔒</div>
                        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", marginBottom: "8px" }}>
                          2027 Rankings are an AMPLive premium feature
                        </div>
                        <a href="https://fantasyamp.com/products" style={{
                          color: "#FFD700",
                          fontSize: "0.8rem",
                          fontWeight: "700",
                          textDecoration: "none",
                        }}>
                          Upgrade to unlock for $10/mo →
                        </a>
                      </div>
                    )}
                    {currentPick && currentPick.recommendation && isPaid === 1 && (
                      <div
                        onClick={() => handleMockDraftUserPick(currentPick.recommendation.player)}
                        style={{
                          padding: "14px 16px",
                          borderRadius: "10px",
                          background: "rgba(255,215,0,0.1)",
                          border: `2px solid ${posColors[currentPick.positionTarget] || 'rgba(255,215,0,0.3)'}`,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          marginBottom: futurePicks.length > 0 ? "12px" : "0",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,215,0,0.18)";
                          e.currentTarget.style.transform = "scale(1.01)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(255,215,0,0.1)";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                          <span style={{
                            background: posColors[currentPick.positionTarget] || '#6b7280',
                            color: "#fff",
                            fontSize: "0.65rem",
                            fontWeight: "800",
                            padding: "2px 8px",
                            borderRadius: "4px",
                          }}>
                            {currentPick.positionTarget}
                          </span>
                          <span style={{ color: "#FFD700", fontSize: "1.05rem", fontWeight: "800" }}>
                            Take {(currentPick.recommendation.player.first_name + ' ' + currentPick.recommendation.player.last_name).trim()} at {currentPick.pickLabel}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "4px" }}>
                          {(currentPick.recommendation.player.headshot_url || currentPick.recommendation.player.image) && (
                            <img
                              src={currentPick.recommendation.player.headshot_url || currentPick.recommendation.player.image}
                              alt=""
                              style={{ width: isMobileView ? "28px" : "40px", height: isMobileView ? "28px" : "40px", borderRadius: "50%", objectFit: "cover", border: isMobileView ? "1px solid rgba(255,215,0,0.4)" : "2px solid rgba(255,215,0,0.4)" }}
                            />
                          )}
                          <div>
                            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                              {currentPick.planText}
                            </div>
                            {currentPick.recommendation.ampRank && (
                              <div style={{ color: "rgba(255,215,0,0.6)", fontSize: "0.7rem", marginTop: "2px" }}>
                                AMP Score: {currentPick.recommendation.ampRank}
                              </div>
                            )}
                          </div>
                        </div>
                        {currentPick.alternatives.length > 0 && (
                          <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                            <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.4)", marginBottom: "4px" }}>Also consider:</div>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {currentPick.alternatives.map((alt, i) => (
                                <div
                                  key={i}
                                  onClick={(e) => { e.stopPropagation(); handleMockDraftUserPick(alt.player); }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    background: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                                >
                                  <span style={{
                                    color: "#fff",
                                    fontSize: "0.55rem",
                                    fontWeight: "800",
                                    background: posColors[alt.position] || '#6b7280',
                                    padding: "1px 5px",
                                    borderRadius: "3px",
                                  }}>{alt.position}</span>
                                  <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.7rem" }}>
                                    {alt.player.first_name?.[0]}. {alt.player.last_name}
                                  </span>
                                  <span style={{ color: "rgba(255,215,0,0.5)", fontSize: "0.6rem" }}>
                                    {alt.reason}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{ textAlign: "right", marginTop: "6px" }}>
                          <span style={{ fontSize: "0.6rem", color: "rgba(255,215,0,0.5)", fontStyle: "italic" }}>Click to draft</span>
                        </div>
                      </div>
                    )}

                    {/* Upcoming Picks Roadmap */}
                    {futurePicks.length > 0 && (
                      <div>
                        <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.4)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Upcoming Picks
                        </div>
                        {futurePicks.slice(0, 4).map((fp, i) => (
                          <div key={i} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "5px 8px",
                            borderRadius: "6px",
                            background: "rgba(255,255,255,0.02)",
                            marginBottom: "3px",
                          }}>
                            <span style={{
                              color: "rgba(255,255,255,0.5)",
                              fontSize: "0.7rem",
                              fontWeight: "700",
                              minWidth: "32px",
                            }}>{fp.pickLabel}</span>
                            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem" }}>&rarr;</span>
                            <span style={{
                              color: "#fff",
                              fontSize: "0.55rem",
                              fontWeight: "800",
                              padding: "1px 6px",
                              borderRadius: "3px",
                              background: posColors[fp.positionTarget] || '#6b7280',
                            }}>{fp.positionTarget}</span>
                            {fp.recommendation && (
                              <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem" }}>
                                {fp.recommendation.player.first_name?.[0]}. {fp.recommendation.player.last_name}
                                <span style={{ color: "rgba(255,255,255,0.25)", fontStyle: "italic", marginLeft: "4px" }}>
                                  (if available)
                                </span>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Summary line */}
                    {personalizedDraftPlan.summary && (
                      <div style={{
                        marginTop: "10px",
                        paddingTop: "8px",
                        borderTop: "1px solid rgba(255,215,0,0.15)",
                        fontSize: "0.7rem",
                        color: "rgba(255,255,255,0.45)",
                        fontStyle: "italic",
                      }}>
                        {personalizedDraftPlan.summary}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Draft Plan Paywall Teaser for Free Users */}
              {personalizedDraftPlan && personalizedDraftPlan.picks.length > 0 && isPaid !== 1 && window.location.hostname !== 'localhost' && (
                <div style={{
                  marginBottom: "16px",
                  background: "rgba(255,215,0,0.04)",
                  border: "1px solid rgba(255,215,0,0.15)",
                  borderRadius: "12px",
                  padding: "20px 16px",
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                }}>
                  <div style={{
                    position: "absolute",
                    top: 0, left: 0, right: 0, bottom: 0,
                    backdropFilter: "blur(2px)",
                    zIndex: 0,
                  }} />
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontSize: "1.5rem", marginBottom: "8px" }}>&#x1F512;</div>
                    <div style={{
                      color: "#FFD700",
                      fontSize: "0.95rem",
                      fontWeight: "800",
                      marginBottom: "6px",
                    }}>
                      Unlock Your Personalized Draft Plan
                    </div>
                    <div style={{
                      color: "rgba(255,255,255,0.55)",
                      fontSize: "0.8rem",
                      marginBottom: "14px",
                      maxWidth: "320px",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}>
                      See exactly who to draft at every pick based on your roster needs. Live-adapting strategy powered by AMP.
                    </div>
                    <button
                      onClick={() => window.open('https://fantasyamp.com/products', '_blank')}
                      style={{
                        padding: "8px 24px",
                        fontSize: "0.85rem",
                        fontWeight: "800",
                        borderRadius: "8px",
                        border: "none",
                        background: "linear-gradient(135deg, #FFD700, #FFA500)",
                        color: "#1a1a2e",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 4px 15px rgba(255,215,0,0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,215,0,0.3)";
                      }}
                    >
                      Upgrade
                    </button>
                  </div>
                </div>
              )}

              {/* Search + Position Filter + Player List */}
              <div style={{ marginBottom: "8px" }}>
                <input
                  type="text"
                  placeholder="Search players..."
                  value={mockDraftSearch}
                  onChange={(e) => setMockDraftSearch(e.target.value)}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "8px 14px",
                    fontSize: "0.9rem",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,215,0,0.4)",
                    background: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
                {['All', 'QB', 'RB', 'WR', 'TE'].map(pos => (
                  <button
                    key={pos}
                    onClick={() => setMockDraftPositionFilter(pos)}
                    style={{
                      padding: "4px 12px",
                      fontSize: "0.75rem",
                      fontWeight: mockDraftPositionFilter === pos ? "700" : "500",
                      borderRadius: "6px",
                      border: mockDraftPositionFilter === pos ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                      background: mockDraftPositionFilter === pos ? "rgba(255,215,0,0.2)" : "transparent",
                      color: mockDraftPositionFilter === pos ? "#FFD700" : "rgba(255,255,255,0.5)",
                      cursor: "pointer",
                    }}
                  >
                    {pos}
                  </button>
                ))}
              </div>
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                maxHeight: "320px",
                overflowY: "auto",
              }}>
                {mockDraftSearchResults.map(player => {
                  const profileUrl = `https://fantasyamp.com/player/${(player.first_name + '_' + player.last_name).toLowerCase().replace(/[^a-z_]/g, '')}`;
                  const pickNumber = mockDraftCurrentPick + 1;
                  const isFalling = player.rank && pickNumber - player.rank >= 8;
                  return (
                    <div
                      key={player.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        border: isFalling ? "1px solid rgba(16,185,129,0.3)" : "1px solid rgba(255,255,255,0.1)",
                        background: isFalling ? "rgba(16,185,129,0.08)" : "rgba(255,255,255,0.05)",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(player.headshot_url || player.image) && (
                        <img
                          src={player.headshot_url || player.image}
                          alt=""
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                        />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener"
                            style={{
                              color: "#fff",
                              textDecoration: "none",
                              fontWeight: "600",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {player.first_name} {player.last_name}
                          </a>
                          {isFalling && (
                            <span style={{
                              fontSize: "0.55rem",
                              fontWeight: "800",
                              color: "#10b981",
                              background: "rgba(16,185,129,0.15)",
                              padding: "1px 5px",
                              borderRadius: "3px",
                              flexShrink: 0,
                              letterSpacing: "0.03em",
                            }}>
                              FALLING
                            </span>
                          )}
                        </div>
                        {(isLoggedIn || yearFilter === 2026) && (player.amp_analytics_score || player.amp_momentum_score || player.amp_production_score) && (
                          <div style={{ display: "flex", gap: "4px", marginTop: "3px", flexWrap: "wrap" }}>
                            {player.amp_analytics_score && (
                              <span style={{ fontSize: "0.6rem", fontWeight: "600", color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.06)", padding: "1px 5px", borderRadius: "4px" }}>
                                Adj. Comp {Math.round(player.amp_analytics_score)}
                              </span>
                            )}
                            {player.amp_momentum_score && (
                              <span style={{ fontSize: "0.6rem", fontWeight: "600", color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.06)", padding: "1px 5px", borderRadius: "4px" }}>
                                Momentum {Math.round(player.amp_momentum_score)}
                              </span>
                            )}
                            {player.amp_production_score && (
                              <span style={{ fontSize: "0.6rem", fontWeight: "600", color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.06)", padding: "1px 5px", borderRadius: "4px" }}>
                                Production {Math.round(player.amp_production_score)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <span style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0, marginLeft: "8px" }}>
                        <span style={{
                          color: "#FFD700",
                          fontSize: "0.7rem",
                          fontWeight: "700",
                          background: "rgba(255,215,0,0.15)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}>
                          {player.metrics?.position || "—"}
                        </span>
                        {isCffDraft ? (
                          <span style={{ color: "#10b981", fontSize: "0.72rem", fontWeight: "700" }}>
                            CFF #{player.cff_rank}
                          </span>
                        ) : (isLoggedIn || mockDraftYear === 2026 || mockDraftYear === 1) && (
                          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}>
                            #{player.rank}
                          </span>
                        )}
                        <button
                          onClick={() => handleMockDraftUserPick(player)}
                          style={{
                            padding: "4px 12px",
                            fontSize: "0.75rem",
                            fontWeight: "700",
                            borderRadius: "6px",
                            border: "none",
                            background: "linear-gradient(135deg, #FFD700, #FFA500)",
                            color: "#1a1a2e",
                            cursor: "pointer",
                          }}
                        >
                          Draft
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Draft Limit Warning Banner */}
          {showDraftLimitWarning && (
            <div style={{
              position: "fixed", bottom: "24px", left: "50%", transform: "translateX(-50%)",
              background: "linear-gradient(135deg, #1a1a2e, #16213e)",
              border: "2px solid rgba(255,215,0,0.5)", borderRadius: "12px",
              padding: "16px 24px", zIndex: 9998, display: "flex",
              alignItems: "center", gap: "16px", maxWidth: "480px", width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            }}>
              <span style={{ fontSize: "1.4rem" }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#FFD700", fontWeight: "700", fontSize: "0.95rem", marginBottom: "2px" }}>
                  {MOCK_DRAFT_LIMIT - mockDraftCount} free mock drafts remaining
                </div>
                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>
                  Upgrade to AMPLive for unlimited access. $10/mo, cancel anytime
                </div>
              </div>
              <a
                href="https://fantasyamp.com/products"
                style={{
                  padding: "8px 16px", borderRadius: "8px", whiteSpace: "nowrap",
                  background: "linear-gradient(135deg, #FFD700, #FFA500)",
                  color: "#1a1a2e", fontWeight: "800", fontSize: "0.8rem",
                  textDecoration: "none",
                }}
              >
                Upgrade
              </a>
              <button
                onClick={() => setShowDraftLimitWarning(false)}
                style={{
                  background: "transparent", border: "none",
                  color: "rgba(255,255,255,0.3)", fontSize: "1.1rem",
                  cursor: "pointer", lineHeight: 1, padding: "0 4px",
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Draft Limit Modal */}
          {showDraftLimitModal && (
            <div
              onClick={() => setShowDraftLimitModal(false)}
              style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 10000, backdropFilter: "blur(4px)",
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                  border: "2px solid rgba(255,215,0,0.4)",
                  borderRadius: "16px", padding: "40px 36px",
                  maxWidth: "420px", width: "90%", textAlign: "center",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "12px" }}>🔒</div>
                <div style={{ color: "#FFD700", fontSize: "1.4rem", fontWeight: "800", marginBottom: "10px" }}>
                  You've used all 100 free mock drafts
                </div>
                <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.95rem", marginBottom: "28px", lineHeight: "1.6" }}>
                  Upgrade to AMPLive for unlimited mock drafts, player profiles, and 2028 mock draft access.
                </div>
                <a
                  href="https://fantasyamp.com/products"
                  style={{
                    display: "block", padding: "14px 32px", borderRadius: "10px",
                    background: "linear-gradient(135deg, #FFD700, #FFA500)",
                    color: "#1a1a2e", fontWeight: "800", fontSize: "1rem",
                    textDecoration: "none", marginBottom: "12px",
                  }}
                >
                  Upgrade to AMPLive for $10/mo →
                </a>
                <button
                  onClick={() => setShowDraftLimitModal(false)}
                  style={{
                    background: "transparent", border: "none",
                    color: "rgba(255,255,255,0.35)", fontSize: "0.85rem",
                    cursor: "pointer",
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Mock Draft Paywall Modal */}
          {showMockPaywall && (
            <div
              onClick={() => setShowMockPaywall(false)}
              style={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.75)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10000,
                backdropFilter: "blur(4px)",
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                  border: "2px solid rgba(255,215,0,0.4)",
                  borderRadius: "16px",
                  padding: "40px 36px",
                  maxWidth: "420px",
                  width: "90%",
                  textAlign: "center",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
                }}
              >
                <div style={{ fontSize: "1.4rem", fontWeight: "800", color: "#FFD700", marginBottom: "12px" }}>
                  AMPLive Subscribers Only
                </div>
                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", lineHeight: "1.6", marginBottom: "24px" }}>
                  AMPLive's mock drafts are for 2026, 2027, and 2028. Play chess while your leaguemates play checkers.
                </div>
                <button
                  onClick={() => window.open('https://fantasyamp.com/products', '_blank')}
                  style={{
                    padding: "12px 32px",
                    fontSize: "1rem",
                    fontWeight: "700",
                    borderRadius: "10px",
                    border: "none",
                    background: "linear-gradient(135deg, #FFD700, #FFA500)",
                    color: "#1a1a2e",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(255,215,0,0.3)",
                    marginBottom: "12px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,215,0,0.3)";
                  }}
                >
                  Unlock Mocks for $10/mo
                </button>
                <div>
                  <button
                    onClick={() => setShowMockPaywall(false)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "rgba(255,255,255,0.4)",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Override Pick Modal */}
          {overridingPickIndex != null && sleeperDraftBoard?.picks?.[overridingPickIndex] && (() => {
            const overridePick = sleeperDraftBoard.picks[overridingPickIndex];
            const currentResult = mockDraftResults[overridingPickIndex];
            return (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.85)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                }}
                onClick={() => { setOverridingPickIndex(null); setOverrideSearch(''); }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(255,255,255,0.15)",
                    maxWidth: "480px",
                    width: "100%",
                    maxHeight: "80vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {/* Header */}
                  <div style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: "#fff" }}>
                        Override Pick {overridePick.pick_label}
                      </div>
                      {currentResult && (
                        <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", marginTop: "2px" }}>
                          Current: {currentResult.player.first_name} {currentResult.player.last_name} ({currentResult.player.metrics?.position || '—'})
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => { setOverridingPickIndex(null); setOverrideSearch(''); }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "rgba(255,255,255,0.6)",
                        fontSize: "1.4rem",
                        cursor: "pointer",
                        padding: "4px 8px",
                        lineHeight: "1",
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Search */}
                  <div style={{ padding: "12px 20px 8px" }}>
                    <input
                      type="text"
                      placeholder="Search players..."
                      value={overrideSearch}
                      onChange={(e) => setOverrideSearch(e.target.value)}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.2)",
                        background: "rgba(255,255,255,0.06)",
                        color: "#fff",
                        fontSize: "0.9rem",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Player List */}
                  <div style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "4px 20px 16px",
                  }}>
                    {overrideAvailablePlayers.length === 0 && (
                      <div style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: "20px 0", fontSize: "0.85rem" }}>
                        No players found
                      </div>
                    )}
                    {overrideAvailablePlayers.map(player => {
                      const pos = player.metrics?.position || player.position || '—';
                      const posColor = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' }[pos] || 'rgba(255,255,255,0.5)';
                      return (
                        <div
                          key={player.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 0",
                            borderBottom: "1px solid rgba(255,255,255,0.06)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flex: 1 }}>
                            {(player.headshot_url || player.image) && (
                              <img
                                src={player.headshot_url || player.image}
                                alt=""
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  flexShrink: 0,
                                  border: "1px solid rgba(255,255,255,0.15)",
                                }}
                              />
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                color: "#fff",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}>
                                {player.first_name} {player.last_name}
                              </div>
                              <div style={{ fontSize: "0.7rem", color: posColor, fontWeight: "600" }}>
                                {pos} {player.rank ? `· Rank ${player.rank}` : ''}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleOverridePick(player)}
                            style={{
                              background: "rgba(245,158,11,0.15)",
                              border: "1px solid rgba(245,158,11,0.4)",
                              color: "#f59e0b",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              padding: "5px 12px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              flexShrink: 0,
                              marginLeft: "8px",
                            }}
                          >
                            Swap
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
          </>)}

        </div>
      )}

      {/* Share Draft Card Button */}
      {viewMode === 'mock-draft' && mockDraftComplete && summaryTeamOwnerId && sleeperDraftBoard && sleeperLeagueInfo?.teams && (
        <div style={{ maxWidth: "800px", margin: "30px auto 0", display: "flex", justifyContent: "center", gap: "12px" }}>
          <button
            onClick={resetMockDraft}
            style={{
              padding: "12px 28px",
              fontSize: "1rem",
              fontWeight: "700",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #FFD700, #FFA500)",
              color: "#1a1a2e",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 15px rgba(255,215,0,0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,215,0,0.3)";
            }}
          >
            Reset Draft
          </button>
          <button
            onClick={downloadMockDraftResults}
            style={{
              padding: "12px 28px",
              fontSize: "1rem",
              fontWeight: "700",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.3)",
              background: "transparent",
              color: "#fff",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#FFD700";
              e.currentTarget.style.color = "#FFD700";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
              e.currentTarget.style.color = "#fff";
            }}
          >
            Download Results
          </button>
          <button
            onClick={shareDraftResults}
            style={{
              padding: "12px 32px",
              fontSize: "1rem",
              fontWeight: "700",
              borderRadius: "10px",
              border: "1px solid rgba(255,215,0,0.4)",
              background: "linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,165,0,0.1))",
              color: "#FFD700",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 15px rgba(255,215,0,0.15)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,215,0,0.25), rgba(255,165,0,0.2))";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,165,0,0.1))";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,215,0,0.15)";
            }}
          >
            Share Draft Card
          </button>
        </div>
      )}

      {/* Mock Draft Summary - Post-Draft Roster View */}
      {viewMode === 'mock-draft' && mockDraftComplete && summaryTeamOwnerId && sleeperDraftBoard && sleeperLeagueInfo?.teams && (
        (() => {
          const positionColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b', K: '#a855f7', DEF: '#6366f1', DL: '#ec4899', LB: '#14b8a6', DB: '#f97316' };
          // Build drafted players grouped by owner
          const draftedByOwner = {};
          (sleeperDraftBoard.picks || []).forEach((pick, idx) => {
            const result = mockDraftResults[idx];
            if (result) {
              const ownerId = pick.current_owner_id;
              if (!draftedByOwner[ownerId]) draftedByOwner[ownerId] = [];
              draftedByOwner[ownerId].push({
                player: result.player,
                pickLabel: pick.pick_label,
                isUserPick: result.isUserPick,
              });
            }
          });

          const selectedTeam = sleeperLeagueInfo.teams.find(t => t.owner_id === summaryTeamOwnerId);
          const teamName = selectedTeam?.display_name || 'Team';
          const drafted = draftedByOwner[summaryTeamOwnerId] || [];

          // Build existing roster grouped by position
          const existingPlayerIds = (sleeperRosters && sleeperRosters[summaryTeamOwnerId]) || [];
          const existingPlayers = existingPlayerIds.map(id => sleeperPlayerNames?.[id]).filter(Boolean);

          // Merge existing + drafted into position groups
          const grouped = {};
          existingPlayers.forEach(p => {
            const pos = p.position || 'OTHER';
            if (!grouped[pos]) grouped[pos] = { existing: [], drafted: [] };
            grouped[pos].existing.push(p);
          });
          drafted.forEach(d => {
            const pos = d.player.metrics?.position || d.player.position || 'OTHER';
            if (!grouped[pos]) grouped[pos] = { existing: [], drafted: [] };
            grouped[pos].drafted.push(d);
          });

          const posOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'DL', 'LB', 'DB', 'OTHER'];
          const sortedPositions = Object.keys(grouped).sort((a, b) => {
            const ia = posOrder.indexOf(a);
            const ib = posOrder.indexOf(b);
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
          });

          return (
            <div style={{
              maxWidth: "800px",
              margin: "40px auto 0",
              background: "rgba(255,255,255,0.04)",
              border: "2px solid rgba(255,215,0,0.3)",
              borderRadius: "16px",
              padding: "24px",
            }}>
              <h3 style={{
                textAlign: "center",
                fontSize: "1.4rem",
                fontWeight: "800",
                color: "#FFD700",
                marginBottom: "4px",
              }}>
                Post-Draft Rosters
              </h3>
              <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "20px" }}>
                Existing roster + mock draft selections
              </p>

              {/* Team selector tabs */}
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                justifyContent: "center",
                marginBottom: "24px",
              }}>
                {sleeperLeagueInfo.teams.map(team => (
                  <button
                    key={team.owner_id}
                    onClick={() => setSummaryTeamOwnerId(team.owner_id)}
                    style={{
                      padding: "6px 14px",
                      fontSize: "0.8rem",
                      fontWeight: summaryTeamOwnerId === team.owner_id ? "700" : "500",
                      borderRadius: "8px",
                      border: summaryTeamOwnerId === team.owner_id ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                      background: summaryTeamOwnerId === team.owner_id ? "rgba(255,215,0,0.2)" : "transparent",
                      color: summaryTeamOwnerId === team.owner_id ? "#FFD700" : "rgba(255,255,255,0.6)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {team.display_name}
                  </button>
                ))}
              </div>

              {/* Team name + draft picks summary */}
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <h4 style={{ color: "#fff", fontSize: "1.2rem", fontWeight: "700", marginBottom: "4px" }}>
                  {teamName}
                </h4>
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
                  {drafted.length} pick{drafted.length !== 1 ? 's' : ''} in this draft
                </span>
              </div>

              {/* Position groups */}
              {(() => {
                // Compute relative position group stars across all teams
                const summaryTeamStars = (() => {
                  if (!playerValues || !sleeperLeagueInfo?.teams || !sleeperRosters || !sleeperPlayerNames) return {};
                  const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };
                  const posScores = {};
                  sleeperLeagueInfo.teams.forEach(team => {
                    const oid = team.owner_id;
                    const pids = sleeperRosters[oid] || [];
                    const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
                    Object.entries(topNC).forEach(([pos, topN]) => {
                      const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
                      if (rks.length > 0) {
                        if (!posScores[pos]) posScores[pos] = {};
                        posScores[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
                      }
                    });
                  });
                  const res = {};
                  Object.entries(posScores).forEach(([pos, scores]) => {
                    const vals = Object.values(scores);
                    const mn = Math.min(...vals);
                    const mx = Math.max(...vals);
                    const rng = mx - mn;
                    Object.entries(scores).forEach(([oid, sc]) => {
                      if (!res[oid]) res[oid] = {};
                      res[oid][pos] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
                    });
                  });
                  return res[summaryTeamOwnerId] || {};
                })();
                return sortedPositions.map(pos => {
                  const stars = summaryTeamStars[pos];
                  const sColor = stars != null ? (stars >= 4 ? '#FFD700' : stars >= 3 ? '#10b981' : stars >= 2 ? '#f59e0b' : '#ef4444') : null;
                  return (
                  <div key={pos} style={{ marginBottom: "14px" }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: '700',
                        padding: '2px 10px', borderRadius: '6px',
                        background: (positionColors[pos] || '#888') + '33',
                        color: positionColors[pos] || '#888',
                        border: '1px solid ' + (positionColors[pos] || '#888') + '66',
                      }}>
                        <span>{pos}</span>
                        {stars != null && (
                          <span style={{ fontSize: '0.7rem', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                            {[1,2,3,4,5].map(i => {
                              if (i <= Math.floor(stars)) return <span key={i} style={{ color: sColor }}>★</span>;
                              if (i - 0.5 <= stars) return <span key={i} style={{ position: 'relative', display: 'inline-block' }}><span style={{ color: 'rgba(255,255,255,0.15)' }}>★</span><span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: '50%', color: sColor }}>★</span></span>;
                              return <span key={i} style={{ color: 'rgba(255,255,255,0.15)' }}>★</span>;
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Drafted players first, highlighted */}
                    {grouped[pos].drafted.map((d, j) => {
                      const dpv = playerValues?.[(d.player.first_name + ' ' + d.player.last_name).trim()];
                      return (
                      <div key={'d-' + j} style={{
                        padding: '4px 0 4px 8px',
                        fontSize: '0.85rem',
                        color: '#FFD700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <span style={{
                          fontSize: '0.65rem',
                          fontWeight: '700',
                          background: 'rgba(255,215,0,0.2)',
                          border: '1px solid rgba(255,215,0,0.4)',
                          borderRadius: '4px',
                          padding: '1px 6px',
                          flexShrink: 0,
                        }}>
                          {d.pickLabel}
                        </span>
                        <span style={{ fontWeight: '600' }}>
                          {d.player.first_name} {d.player.last_name}
                        </span>
                        {dpv?.amp_rank != null && (
                          <span style={{ fontSize: '0.65rem', color: 'rgba(255,215,0,0.7)', fontWeight: '600' }}>
                            {dpv.amp_rank}
                          </span>
                        )}
                      </div>
                      );
                    })}
                    {/* Existing roster players */}
                    {grouped[pos].existing.sort((a, b) => {
                      const aRank = playerValues?.[a.name.trim()]?.amp_rank;
                      const bRank = playerValues?.[b.name.trim()]?.amp_rank;
                      if (aRank != null && bRank != null) return bRank - aRank;
                      if (aRank != null) return -1;
                      if (bRank != null) return 1;
                      return a.searchRank - b.searchRank;
                    }).map((player, j) => {
                      const pv = playerValues?.[player.name.trim()];
                      const rank = pv?.amp_rank;
                      const tierColor = rank != null ? (rank >= 80 ? '#FFD700' : rank >= 60 ? '#10b981' : rank >= 40 ? '#fff' : 'rgba(255,255,255,0.45)') : 'rgba(255,255,255,0.45)';
                      return (
                        <div key={'e-' + j} style={{ padding: '3px 0 3px 8px', fontSize: '0.85rem', color: tierColor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{player.name.trim()}</span>
                          {rank != null && (
                            <span style={{ fontSize: '0.65rem', color: 'rgba(255,215,0,0.7)', fontWeight: '600' }}>
                              {rank}
                            </span>
                          )}
                          {player.team && <span style={{ color: 'rgba(255,255,255,0.3)' }}>— {player.team}</span>}
                        </div>
                      );
                    })}
                  </div>
                  );
                });
              })()}
              {sortedPositions.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontStyle: 'italic' }}>No roster data available</div>
              )}
            </div>
          );
        })()
      )}

      {/* Download Full NFL Draft */}
      {viewMode !== 'tier-builder' && viewMode !== 'pick-value' && yearFilter === 2026 && <div
        style={{
          marginTop: "60px",
          marginBottom: "40px",
          padding: "40px 30px",
          textAlign: "center",
          maxWidth: "900px",
          margin: "60px auto 40px auto",
          background: "linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(85, 104, 211, 0.15) 100%)",
          border: "3px solid #667eea",
          borderRadius: "20px",
          boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)",
          backdropFilter: "blur(10px)",
        }}
      >
        <h2
          style={{
            fontSize: "1.8rem",
            fontWeight: "800",
            color: "#fff",
            marginBottom: "20px",
            textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            letterSpacing: "0.5px",
          }}
        >
          2026 NFL Draft Results
        </h2>
        <p style={{
          color: "rgba(255,255,255,0.85)",
          fontSize: "1.05rem",
          fontWeight: "500",
          marginBottom: "20px",
        }}>
          Fantasy Relevant NFL Draft Players
        </p>
        <button
          onClick={() => {
            const drafted = formatAdjustedPlayers
              .filter(p => p.nfl_draft_year === 2026 && p.nfl_draft_round)
              .sort((a, b) => (a.nfl_draft_selection || 999) - (b.nfl_draft_selection || 999));
            const rows = drafted.map(p => ({
              Pick: p.nfl_draft_selection || '',
              Round: p.nfl_draft_round,
              Name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
              Position: p.metrics?.position || p.position || '',
              'NFL Team': p.nfl_team || '',
              College: p.metrics?.college_team || '',
            }));
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'NFL Draft 2026');
            XLSX.writeFile(wb, 'NFL_Draft_2026_Results.xlsx');
          }}
          style={{
            padding: "16px 40px",
            fontSize: "1.1rem",
            fontWeight: "700",
            color: "#fff",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.5)",
            letterSpacing: "0.5px",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(16, 185, 129, 0.5)";
          }}
        >
          Download Excel
        </button>
      </div>}



      {/* Tier Builder View */}
      {viewMode === 'tier-builder' && tierBuilderData && (
        <div style={{
          padding: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
        }}>
          <div style={{
            background: "rgba(0,0,0,0.25)",
            borderRadius: "16px",
            padding: isMobileView ? "16px" : "24px",
          }}>
            {presentationMode && (
              <div style={{ textAlign: "center", marginBottom: "32px" }}>
                <h1 style={{
                  fontSize: "2.2rem",
                  fontWeight: 900,
                  margin: 0,
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  letterSpacing: "0.5px",
                  textShadow: "none",
                }}>
                  My Tiers on FantasyAmp.com
                </h1>
              </div>
            )}
            {!presentationMode && (
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobileView ? "12px" : "16px",
                marginBottom: "8px",
                flexWrap: "wrap",
              }}>
                <img
                  src="/assets/amp_logo.png"
                  alt="Fantasy Amp"
                  style={{
                    width: isMobileView ? "56px" : "72px",
                    height: isMobileView ? "56px" : "72px",
                    borderRadius: "50%",
                    border: "2px solid rgba(255, 215, 0, 0.5)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(28, 36, 78, 1)",
                    background: "#1C244E",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <h2 style={{
                  fontSize: "2rem",
                  fontWeight: "800",
                  color: "#FFD700",
                  textShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  margin: 0,
                }}>
                  Tier Builder
                </h2>
              </div>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem", marginBottom: "12px" }}>
                {yearFilter === 1
                  ? "Build your dynasty startup tiers using the full player pool ranked by ADP."
                  : "Rank the top prospects into tiers. Use arrows to move players."}
              </p>
              {isPaid !== 1 && (
                <p style={{ color: "#f59e0b", fontSize: "0.8rem", marginBottom: "12px", fontWeight: "600" }}>
                  {tierViewSource === 'amp'
                    ? "You're viewing the official AMP tiers. Upgrade to AMPLive to build and save your own."
                    : "Upgrade to AMPLive to save tiers across sessions."}
                </p>
              )}
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                {[{ yr: 1, label: 'Dynasty', locked: false }, { yr: 2026, label: '2026', locked: false }, { yr: 2027, label: '2027', locked: !isLoggedIn }, { yr: 2028, label: '2028', locked: !isLoggedIn }, { yr: 2029, label: '2029', locked: !isLoggedIn }].map(({ yr, label, locked }) => (
                  <button
                    key={yr}
                    onClick={() => {
                      if (locked) return;
                      if (yr !== yearFilter) {
                        setYearFilter(yr);
                        setTierBuilderData(null);
                      }
                    }}
                    style={{
                      padding: "6px 18px",
                      fontSize: "0.85rem",
                      fontWeight: "700",
                      borderRadius: "8px",
                      border: yearFilter === yr ? `2px solid ${yr === 1 ? '#C084FC' : '#FFD700'}` : "1px solid rgba(255,255,255,0.2)",
                      background: yearFilter === yr ? (yr === 1 ? "rgba(147,51,234,0.2)" : "rgba(255,215,0,0.2)") : "rgba(255,255,255,0.05)",
                      color: locked ? "rgba(255,255,255,0.25)" : yearFilter === yr ? (yr === 1 ? "#C084FC" : "#FFD700") : "rgba(255,255,255,0.7)",
                      cursor: locked ? "not-allowed" : "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {label}{locked ? ' 🔒' : ''}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <div style={{ display: "inline-flex", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.2)" }}>
                  {[
                    { src: 'amp', label: 'AMP Default' },
                    { src: 'mine', label: 'My Tiers' },
                  ].map(({ src, label }) => (
                    <button
                      key={src}
                      onClick={() => {
                        if (src === tierViewSource) return;
                        if (src === 'mine' && isPaid !== 1) {
                          openUpgradePrompt('tier_builder', 'Tier Builder is an AMPLive feature', 'Build your own tiered draft board with full drag-and-drop control. It autosaves as you go.');
                          return;
                        }
                        if (tierBuilderSaveTimer.current) {
                          clearTimeout(tierBuilderSaveTimer.current);
                          tierBuilderSaveTimer.current = null;
                        }
                        if (src === 'amp') saveTierBuilder(); // flush pending edits before swapping boards
                        setTierViewSource(src);
                        setTierBuilderData(null); // effect reloads from the chosen source
                        logUserAction('tier_view_source', { source: src });
                      }}
                      style={{
                        padding: "8px 16px",
                        fontSize: "0.8rem",
                        fontWeight: "700",
                        border: "none",
                        background: tierViewSource === src ? "rgba(255,215,0,0.25)" : "rgba(0,0,0,0.4)",
                        color: tierViewSource === src ? "#FFD700" : "rgba(255,255,255,0.55)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {tierViewSource !== 'amp' && <button
                  onClick={() => { initializeTierBuilderDefaults(); debouncedSaveTierBuilder(); }}
                  style={{
                    padding: "8px 20px",
                    fontSize: "0.8rem",
                    fontWeight: "700",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,215,0,0.4)",
                    background: "rgba(0,0,0,0.4)",
                    color: "#FFD700",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.15)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.4)"; }}
                >
                  Reset Tiers
                </button>}
                <button
                  onClick={() => { setRevealMode(m => !m); setRevealCount(0); }}
                  title="Use reveal mode to show players one at a time; useful for content creators sharing tiered rankings with their audience in videos."
                  style={{
                    padding: "8px 20px",
                    fontSize: "0.8rem",
                    fontWeight: "700",
                    borderRadius: "8px",
                    border: revealMode ? "1px solid #10b981" : "1px solid rgba(255,255,255,0.2)",
                    background: revealMode ? "rgba(16,185,129,0.2)" : "rgba(0,0,0,0.4)",
                    color: revealMode ? "#10b981" : "rgba(255,255,255,0.6)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {revealMode ? '✕ Exit Reveal' : 'Reveal Mode'}
                </button>
                {revealMode && (
                  <button
                    onClick={() => setPresentationMode(true)}
                    title="Hide the chrome (nav, tabs, controls) and enlarge the tier board for clean screen recording. Press Esc or click Exit to return."
                    style={{
                      padding: "8px 20px",
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      borderRadius: "8px",
                      border: "1px solid rgba(255, 215, 0, 0.4)",
                      background: "linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,165,0,0.08))",
                      color: "#FFD700",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    🎥 Presentation Mode
                  </button>
                )}
                {tierBuilderSaveStatus && (
                  <span style={{
                    fontSize: "0.75rem",
                    color: tierBuilderSaveStatus === 'saved' ? '#10b981' : 'rgba(255,255,255,0.5)',
                    fontWeight: '600',
                    transition: 'opacity 0.3s ease',
                  }}>
                    {tierBuilderSaveStatus === 'saving' ? 'Saving...' : 'Saved'}
                  </span>
                )}
              </div>

              {/* What's Reveal Mode? expandable explainer */}
              <div style={{ marginTop: "10px", maxWidth: "480px", marginLeft: "auto", marginRight: "auto" }}>
                <button
                  onClick={() => setRevealInfoOpen(prev => !prev)}
                  style={{
                    width: "100%",
                    padding: "8px 14px",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.04)",
                    color: "rgba(255,255,255,0.65)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    transition: "all 0.2s ease",
                    fontFamily: "inherit",
                  }}
                >
                  <span>What's Reveal Mode?</span>
                  <span style={{
                    display: "inline-block",
                    fontSize: "0.7rem",
                    opacity: 0.55,
                    transform: revealInfoOpen ? "rotate(90deg)" : "rotate(0)",
                    transition: "transform 0.2s ease",
                  }}>▶</span>
                </button>
                {revealInfoOpen && (
                  <div style={{
                    marginTop: "6px",
                    padding: "10px 14px",
                    fontSize: "0.78rem",
                    color: "rgba(255,255,255,0.72)",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    lineHeight: "1.55",
                    textAlign: "left",
                  }}>
                    Use reveal mode to show players one at a time; useful for content creators sharing tiered rankings with their audience in videos.
                  </div>
                )}
              </div>
            </div>
            )}

              {/* Reveal Mode Controls — floating, follows scroll on the right edge */}
              {revealMode && (() => {
                const totalPlayers = TIER_KEYS.reduce((sum, k) => sum + (tierBuilderData[k]?.length || 0), 0);
                const allRevealed = revealCount >= totalPlayers;
                const idleOpacity = allRevealed ? 0.4 : 0.55;
                if (revealPanelHidden) {
                  return (
                    <div
                      onClick={() => setRevealPanelHidden(false)}
                      title="Show reveal controls"
                      style={{
                        position: "fixed",
                        top: "50%",
                        right: isMobileView ? "6px" : "10px",
                        transform: "translateY(-50%)",
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        background: "rgba(15, 27, 45, 0.6)",
                        border: "1px solid rgba(16, 185, 129, 0.3)",
                        cursor: "pointer",
                        opacity: 0.2,
                        transition: "opacity 0.2s ease",
                        zIndex: 100,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.2; }}
                    />
                  );
                }
                return (
                  <div
                    style={{
                      position: "fixed",
                      top: "50%",
                      right: isMobileView ? "12px" : "24px",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                      padding: isMobileView ? "10px 12px" : "14px 16px",
                      background: "rgba(15, 27, 45, 0.85)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      borderRadius: "12px",
                      border: "1px solid rgba(16, 185, 129, 0.35)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
                      opacity: idleOpacity,
                      transition: "opacity 0.2s ease, transform 0.2s ease",
                      zIndex: 100,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = idleOpacity;
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.opacity = 1;
                    }}
                  >
                    <div style={{
                      color: "rgba(255,255,255,0.75)",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.5px",
                      whiteSpace: "nowrap",
                    }}>
                      {allRevealed ? 'ALL REVEALED' : `${revealCount} / ${totalPlayers}`}
                    </div>
                    <button
                      onClick={() => { if (!allRevealed) setRevealCount(c => c + 1); }}
                      disabled={allRevealed}
                      style={{
                        padding: isMobileView ? "10px 20px" : "12px 28px",
                        fontSize: isMobileView ? "0.85rem" : "0.95rem",
                        fontWeight: "800",
                        borderRadius: "10px",
                        border: "none",
                        background: allRevealed
                          ? "rgba(255,255,255,0.1)"
                          : "linear-gradient(135deg, #10b981, #059669)",
                        color: allRevealed ? "rgba(255,255,255,0.3)" : "#fff",
                        cursor: allRevealed ? "not-allowed" : "pointer",
                        boxShadow: allRevealed ? "none" : "0 4px 16px rgba(16,185,129,0.4)",
                        transition: "all 0.2s ease",
                        letterSpacing: "0.5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {allRevealed ? '✓ Complete' : 'Next Player'}
                    </button>
                    {presentationMode && (
                      <button
                        onClick={() => setPresentationMode(false)}
                        style={{
                          padding: "5px 10px",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.2)",
                          background: "rgba(255,255,255,0.04)",
                          color: "rgba(255,255,255,0.6)",
                          cursor: "pointer",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                        }}
                      >
                        Exit Presentation
                      </button>
                    )}
                    <button
                      onClick={() => setRevealPanelHidden(true)}
                      title="Hide this panel. Use the right arrow key to reveal the next player, or click the small dot on the right edge to bring the panel back."
                      style={{
                        padding: "5px 10px",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        borderRadius: "6px",
                        border: "1px solid rgba(255,255,255,0.2)",
                        background: "rgba(255,255,255,0.04)",
                        color: "rgba(255,255,255,0.6)",
                        cursor: "pointer",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Hide Panel
                    </button>
                    <div style={{
                      color: "rgba(255,255,255,0.35)",
                      fontSize: "0.6rem",
                      whiteSpace: "nowrap",
                    }}>
                      → key reveals next
                    </div>
                  </div>
                );
              })()}

              {/* Player Search / Add */}
              {!revealMode && tierViewSource !== 'amp' && <div style={{ marginTop: "16px", position: "relative", maxWidth: "360px", marginLeft: "auto", marginRight: "auto" }}>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input
                    type="text"
                    placeholder="Search player to add..."
                    value={tierBuilderSearch}
                    onChange={(e) => setTierBuilderSearch(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.06)",
                      color: "#fff",
                      outline: "none",
                    }}
                  />
                  <select
                    value={tierBuilderAddTier}
                    onChange={(e) => setTierBuilderAddTier(e.target.value)}
                    style={{
                      padding: "8px 8px",
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.06)",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    {TIER_KEYS.map(k => <option key={k} value={k}>{k} Tier</option>)}
                  </select>
                </div>
                {tierBuilderSearch.trim().length >= 2 && (() => {
                  const q = tierBuilderSearch.trim().toLowerCase();
                  const allInTiers = new Set();
                  TIER_KEYS.forEach(k => (tierBuilderData[k] || []).forEach(p => { if (p.id) allInTiers.add(p.id); }));
                  const results = formatAdjustedPlayers
                    .filter(p => p.nfl_draft_year === yearFilter && p.rank != null)
                    .filter(p => !allInTiers.has(p.id))
                    .filter(p => {
                      const full = ((p.first_name || '') + ' ' + (p.last_name || '')).toLowerCase();
                      return full.includes(q);
                    })
                    .slice(0, 8);
                  if (results.length === 0) return null;
                  const posColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
                  return (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: "4px",
                      background: "rgba(20,20,30,0.98)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: "10px",
                      overflow: "hidden",
                      zIndex: 50,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                    }}>
                      {results.map(player => {
                        const pos = player.position || player.metrics?.position || '';
                        const posColor = posColors[pos] || '#6b7280';
                        return (
                          <div
                            key={player.id}
                            onClick={() => handleTierBuilderAdd(player, tierBuilderAddTier)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "8px 12px",
                              cursor: "pointer",
                              borderBottom: "1px solid rgba(255,255,255,0.06)",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            {(player.headshot_url || player.image) ? (
                              <img src={player.headshot_url || player.image} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                            ) : (
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", color: "rgba(255,255,255,0.4)" }}>
                                {(player.first_name?.[0] || '')}{(player.last_name?.[0] || '')}
                              </div>
                            )}
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <div style={{ fontSize: "0.8rem", fontWeight: "700", color: "#fff" }}>
                                {player.first_name} {player.last_name}
                              </div>
                              <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.4)" }}>
                                <span style={{ color: posColor, fontWeight: "700" }}>{pos}</span> {player.draft_pick_capital ? `\u2022 ${player.draft_pick_capital}` : ''}
                              </div>
                            </div>
                            <span style={{
                              fontSize: "0.65rem",
                              color: "#FFD700",
                              fontWeight: "600",
                              opacity: 0.7,
                            }}>
                              + {tierBuilderAddTier}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>}

            {/* Position filter */}
            {!presentationMode && (
            <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginTop: "12px", marginBottom: "4px" }}>
              {['All', 'QB', 'RB', 'WR', 'TE'].map(pos => (
                <button
                  key={pos}
                  onClick={() => setTierBuilderPosFilter(pos)}
                  style={{
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: tierBuilderPosFilter === pos ? "700" : "500",
                    borderRadius: "6px",
                    border: tierBuilderPosFilter === pos ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                    background: tierBuilderPosFilter === pos ? "rgba(255,215,0,0.2)" : "transparent",
                    color: tierBuilderPosFilter === pos ? "#FFD700" : "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                  }}
                >
                  {pos}
                </button>
              ))}
            </div>
            )}

            {(() => {
              let runningRank = 0;
              return TIER_KEYS.map((tierKey) => {
              const tierColors = { S: '#10b981', A: '#3b82f6', B: '#8b5cf6', C: '#f59e0b', D: '#f97316', E: '#ef4444', F: '#6b7280', G: '#64748b', H: '#78716c', I: '#a1a1aa', J: '#737373' };
              const posColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
              const color = tierColors[tierKey];
              const players = tierBuilderData[tierKey] || [];
              const tierStartRank = runningRank;
              runningRank += players.length;
              const visiblePlayers = revealMode ? players.slice(0, Math.max(0, revealCount - tierStartRank)) : players;
              return (
                <div key={tierKey} style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: presentationMode ? "14px" : "8px",
                  padding: presentationMode ? "18px 0" : "12px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}>
                  <div style={{
                    width: presentationMode ? "60px" : "40px",
                    height: presentationMode ? "60px" : "40px",
                    minWidth: presentationMode ? "60px" : "40px",
                    borderRadius: "8px",
                    background: color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: presentationMode ? "1.8rem" : "1.1rem",
                    fontWeight: "800",
                    color: "#fff",
                    marginTop: "4px",
                  }}>
                    {tierKey}
                  </div>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: presentationMode ? "12px" : "8px",
                    flex: 1,
                  }}>
                    {visiblePlayers.map((player, pIdx) => {
                      const pos = player.position || player.metrics?.position || '';
                      const posColor = posColors[pos] || '#6b7280';
                      const displayName = presentationMode
                        ? ((player.first_name || '') + ' ' + (player.last_name || '')).trim()
                        : (player.first_name?.[0] || '') + '. ' + (player.last_name || '');
                      const headSize = presentationMode ? 80 : (isMobileView ? 28 : 36);
                      const teamLogo = player.nfl_team_image || player.team_image || player.metrics?.team_logo || null;
                      const posMatch = tierBuilderPosFilter === 'All' || pos === tierBuilderPosFilter;
                      return (
                        <div key={player.id || pIdx} style={{
                          width: presentationMode ? "160px" : (isMobileView ? "85px" : "100px"),
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderTop: `3px solid ${posColor}`,
                          borderRadius: "10px",
                          padding: "6px 4px 6px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "3px",
                          opacity: posMatch ? 1 : 0.2,
                          transition: "opacity 0.2s ease",
                          position: "relative",
                          overflow: "hidden",
                          boxSizing: "border-box",
                        }}>
                          {/* Remove button */}
                          {tierViewSource !== 'amp' && <button
                            onClick={() => handleTierBuilderRemove(tierKey, pIdx)}
                            style={{
                              position: "absolute",
                              top: "2px",
                              right: "2px",
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              border: "none",
                              background: "rgba(255,255,255,0.1)",
                              color: "rgba(255,255,255,0.35)",
                              fontSize: "0.55rem",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              lineHeight: "1",
                              padding: 0,
                              transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.5)"; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.35)"; }}
                          >
                            ×
                          </button>}
                          {/* Up / Down arrows above headshot */}
                          {!presentationMode && tierViewSource !== 'amp' && <div style={{ display: "flex", gap: "3px" }}>
                            <button
                              onClick={() => handleTierMove(tierKey, pIdx, 'up')}
                              style={{
                                fontSize: "0.7rem",
                                background: "rgba(16,185,129,0.15)",
                                border: "1px solid rgba(16,185,129,0.3)",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                color: "#10b981",
                                transition: "all 0.15s ease",
                                lineHeight: "1",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(16,185,129,0.35)"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(16,185,129,0.15)"; e.currentTarget.style.color = "#10b981"; }}
                            >
                              {'\u2191'}
                            </button>
                            <button
                              onClick={() => handleTierMove(tierKey, pIdx, 'down')}
                              style={{
                                fontSize: "0.7rem",
                                background: "rgba(239,68,68,0.15)",
                                border: "1px solid rgba(239,68,68,0.3)",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                color: "#ef4444",
                                transition: "all 0.15s ease",
                                lineHeight: "1",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.35)"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.15)"; e.currentTarget.style.color = "#ef4444"; }}
                            >
                              {'\u2193'}
                            </button>
                          </div>}
                          {/* Headshot */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: (revealMode || presentationMode) && teamLogo ? 6 : 0, width: "100%", overflow: "hidden" }}>
                            {/* Headshot wrapper — clipped so AMP logo stays inside */}
                            <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: headSize, height: headSize, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
                              <img
                                src="/assets/amp_logo.png"
                                alt=""
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  opacity: 0.3,
                                  zIndex: 0,
                                }}
                              />
                              {(player.headshot_url || player.image) ? (
                                <img
                                  src={player.headshot_url || player.image}
                                  alt={displayName}
                                  style={{
                                    width: headSize,
                                    height: headSize,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    position: "relative",
                                    zIndex: 1,
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: headSize,
                                  height: headSize,
                                  borderRadius: "50%",
                                  background: "rgba(255,255,255,0.1)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.6rem",
                                  color: "rgba(255,255,255,0.4)",
                                  position: "relative",
                                  zIndex: 1,
                                }}>
                                  {(player.first_name?.[0] || '')}{(player.last_name?.[0] || '')}
                                </div>
                              )}
                            </div>
                            {/* Team logo side-by-side in reveal/presentation mode */}
                            {(revealMode || presentationMode) && teamLogo && (
                              <img
                                src={teamLogo}
                                alt="team"
                                style={{
                                  width: headSize * 0.6,
                                  height: headSize * 0.6,
                                  objectFit: "contain",
                                  filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))",
                                  flexShrink: 1,
                                  minWidth: 0,
                                }}
                              />
                            )}
                          </div>
                          {/* Name */}
                          <a
                            href={`https://fantasyamp.com/player/${(player.first_name + '_' + player.last_name).toLowerCase().replace(/[^a-z_]/g, '')}`}
                            target="_blank"
                            rel="noopener"
                            style={{
                              fontSize: presentationMode ? "1.05rem" : "0.75rem",
                              fontWeight: presentationMode ? "800" : "700",
                              color: "#fff",
                              textAlign: "center",
                              lineHeight: "1.2",
                              overflow: "hidden",
                              textOverflow: presentationMode ? "clip" : "ellipsis",
                              whiteSpace: presentationMode ? "normal" : "nowrap",
                              wordBreak: presentationMode ? "break-word" : "normal",
                              maxWidth: "100%",
                              textDecoration: "none",
                              letterSpacing: presentationMode ? "0.3px" : "0",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.color = '#FFD700'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; e.currentTarget.style.color = '#fff'; }}
                          >
                            {displayName}
                          </a>
                          {/* Position + Draft Capital */}
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{
                              fontSize: presentationMode ? "0.85rem" : "0.6rem",
                              fontWeight: "700",
                              color: "#fff",
                              background: posColor,
                              borderRadius: "4px",
                              padding: "1px 4px",
                            }}>
                              {pos}
                            </span>
                            <span style={{
                              fontSize: presentationMode ? "0.85rem" : "0.6rem",
                              color: "rgba(255,255,255,0.65)",
                              fontWeight: presentationMode ? "700" : "500",
                            }}>
                              {(() => { const r = tierStartRank + pIdx + 1; const rd = Math.ceil(r / 12); const pk = r - (rd - 1) * 12; return `${rd}.${String(pk).padStart(2, '0')}`; })()}
                            </span>
                          </div>
                          {/* Left / Right arrows below */}
                          {!presentationMode && tierViewSource !== 'amp' && <div style={{ display: "flex", gap: "3px", marginTop: "2px" }}>
                            <button
                              onClick={() => handleTierMove(tierKey, pIdx, 'left')}
                              style={{
                                fontSize: "0.7rem",
                                background: "rgba(255,215,0,0.12)",
                                border: "1px solid rgba(255,215,0,0.3)",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                color: "#FFD700",
                                transition: "all 0.15s ease",
                                lineHeight: "1",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.3)"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.12)"; e.currentTarget.style.color = "#FFD700"; }}
                            >
                              {'\u2190'}
                            </button>
                            <button
                              onClick={() => handleTierMove(tierKey, pIdx, 'right')}
                              style={{
                                fontSize: "0.7rem",
                                background: "rgba(255,215,0,0.12)",
                                border: "1px solid rgba(255,215,0,0.3)",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                color: "#FFD700",
                                transition: "all 0.15s ease",
                                lineHeight: "1",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.3)"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,215,0,0.12)"; e.currentTarget.style.color = "#FFD700"; }}
                            >
                              {'\u2192'}
                            </button>
                          </div>}
                        </div>
                      );
                    })}
                    {players.length === 0 && (
                      <span style={{ color: "rgba(255,255,255,0.3)", fontStyle: "italic", fontSize: "0.8rem", padding: "8px" }}>
                        Empty tier
                      </span>
                    )}
                    {tierKey === 'S' && players.length > 0 && (
                      <span style={{
                        alignSelf: "center",
                        marginLeft: "8px",
                        fontSize: "0.85rem",
                        fontWeight: 800,
                        color: "#FFD700",
                        letterSpacing: "0.5px",
                        textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                        whiteSpace: "nowrap",
                      }}>
                        fantasyamp.com
                      </span>
                    )}
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </div>
      )}

      {/* Access CTA — bottom of every accessible tab.
          Logged-out: sign-in / create-account prompt.
          Logged-in free: upgrade prompt.
          Paid users: nothing (full access). */}
      {!isLoggedIn && process.env.NODE_ENV !== 'development' && (
        <div style={{
          maxWidth: "700px",
          margin: "40px auto",
          padding: "40px 24px",
          textAlign: "center",
          background: "linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)",
          borderRadius: "20px",
          border: "2px solid rgba(255, 215, 0, 0.3)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}>
          <div style={{
            fontSize: "1.8rem",
            fontWeight: "800",
            color: "#FFD700",
            marginBottom: "12px",
            textShadow: "0 4px 12px rgba(255, 215, 0, 0.4)",
          }}>
            Create your free account
          </div>
          <div style={{
            fontSize: "1rem",
            color: "rgba(255, 255, 255, 0.7)",
            marginBottom: "28px",
          }}>
            Sign in to unlock more rounds, run mock drafts, and build your own board. Always free.
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
            <a
              href={COGNITO_LOGIN_URL}
              style={{
                display: "inline-block",
                padding: "14px 48px",
                fontSize: "1.15rem",
                fontWeight: "700",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#fff",
                textDecoration: "none",
                borderRadius: "50px",
                boxShadow: "0 8px 24px rgba(16, 185, 129, 0.5)",
                transition: "all 0.3s ease",
                border: "3px solid #10b981",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05) translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 32px rgba(16, 185, 129, 0.7)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.5)";
              }}
            >
              Login / Create Account
            </a>
            <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)" }}>
              By signing up you'll also receive the free Fantasy Amp newsletter. Unsubscribe anytime.
            </div>
          </div>
        </div>
      )}

      {isLoggedIn && isPaid !== 1 && (
        <div style={{
          maxWidth: "700px",
          margin: "40px auto",
          padding: "32px 24px",
          textAlign: "center",
          background: "linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)",
          borderRadius: "20px",
          border: "2px solid rgba(255, 215, 0, 0.3)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}>
          <div style={{
            fontSize: "1.6rem",
            fontWeight: "800",
            color: "#FFD700",
            marginBottom: "10px",
            textShadow: "0 4px 12px rgba(255, 215, 0, 0.4)",
          }}>
            Upgrade to AMPLive
          </div>
          <div style={{
            fontSize: "1rem",
            color: "rgba(255, 255, 255, 0.7)",
            marginBottom: "24px",
          }}>
            Reveal every AMP Score, run unlimited mock drafts, and build your own tiers. $10/mo, cancel anytime.
          </div>
          <a
            href="/products"
            style={{
              display: "inline-block",
              padding: "14px 48px",
              fontSize: "1.15rem",
              fontWeight: "700",
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#1a202c",
              textDecoration: "none",
              borderRadius: "50px",
              boxShadow: "0 8px 24px rgba(255, 215, 0, 0.4)",
              transition: "all 0.3s ease",
              border: "3px solid #FFD700",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05) translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(255, 215, 0, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1) translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(255, 215, 0, 0.4)";
            }}
          >
            Upgrade to AMPLive for $10/mo
          </a>
        </div>
      )}

      {/* Footer */}
      {viewMode !== 'pick-value' && <footer
        style={{
          textAlign: "center",
          padding: "30px 20px",
          marginTop: "60px",
          color: "rgba(255, 255, 255, 0.7)",
          fontSize: "0.9rem",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        2026 Amp Analytics LLC
      </footer>}

{/* FOOTER - COMMENTED OUT
<footer
  style={{
    marginTop: "60px",
    padding: "24px",
    textAlign: "center",
    fontSize: "0.95rem",
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.5)",
    borderTop: "2px solid rgba(255,215,0,0.3)",
    borderRadius: "16px 16px 0 0",
    boxShadow: "0 -8px 32px rgba(0,0,0,0.2)",
  }}
>
  <a
    href="https://fantasyamp.com/subscribe"
    target="_blank"
    rel="noopener"
    style={{
      color: "#FFD700",
      fontWeight: "bold",
      textDecoration: "none",
      fontSize: "1.1rem",
      textShadow: "0 2px 8px rgba(255,215,0,0.4)",
      transition: "all 0.3s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = "#FFA500";
      e.currentTarget.style.textShadow = "0 4px 12px rgba(255,165,0,0.6)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = "#FFD700";
      e.currentTarget.style.textShadow = "0 2px 8px rgba(255,215,0,0.4)";
    }}
  >
    Full Launch 144 2026 prospects and profiles on January 17th, subscribe for Early Access - fantasyamp.com/subscribe
  </a>

  {isLoggedIn && (
    <div style={{ marginTop: "20px" }}>
      <button
        onClick={() => {
          logUserAction('logout', {});
          localStorage.removeItem('cognito_sub');
          localStorage.removeItem('id_token');
          localStorage.removeItem('access_token');
          setIsLoggedIn(false);
          setIsPaid(0);
          setCognitoSub(null);
          window.location.reload();
        }}
        style={{
          background: "rgba(255, 255, 255, 0.1)",
          border: "2px solid rgba(255, 255, 255, 0.3)",
          color: "#fff",
          padding: "10px 24px",
          fontSize: "0.9rem",
          fontWeight: "600",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(220, 38, 38, 0.8)";
          e.currentTarget.style.borderColor = "#dc2626";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        Logout
      </button>
    </div>
  )}
</footer>
*/}

      {/* Account Management & Logout Buttons */}
      {isLoggedIn && (
        <div
          style={{
            marginTop: "60px",
            marginBottom: "40px",
            padding: "40px 24px",
            textAlign: "center",
            maxWidth: "800px",
            margin: "60px auto 40px auto",
            display: "flex",
            gap: "20px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <a
            href="https://billing.stripe.com/p/login/5kQ00l8Mg2bxcpG9Pj4Ja00"
            style={{
              display: "inline-block",
              padding: "16px 48px",
              fontSize: "1.2rem",
              fontWeight: "700",
              background: "linear-gradient(135deg, #667eea 0%, #5568d3 100%)",
              color: "#fff",
              textDecoration: "none",
              borderRadius: "50px",
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.6)",
              transition: "all 0.3s ease",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05) translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(102, 126, 234, 0.8)";
              e.currentTarget.style.borderColor = "#FFD700";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1) translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(102, 126, 234, 0.6)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
            }}
          >
            Manage Account
          </a>

          <button
            onClick={() => {
              localStorage.clear();
              window.location.href = "https://fantasyamp.com/logout";
            }}
            style={{
              display: "inline-block",
              padding: "16px 48px",
              fontSize: "1.2rem",
              fontWeight: "700",
              background: "rgba(255, 255, 255, 0.1)",
              color: "#fff",
              textDecoration: "none",
              borderRadius: "50px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
              transition: "all 0.3s ease",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05) translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(0, 0, 0, 0.5)";
              e.currentTarget.style.borderColor = "#FFD700";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1) translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.3)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
            }}
          >
            Logout
          </button>
        </div>
      )}
      {/* Roster Detail Modal */}
      {rosterModalOwnerId && sleeperRosters && sleeperPlayerNames && (() => {
        const playerIds = sleeperRosters[rosterModalOwnerId] || [];
        const positionOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'DL', 'LB', 'DB'];
        const positionColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b', K: '#a855f7', DEF: '#6366f1', DL: '#ec4899', LB: '#14b8a6', DB: '#f97316' };
        const grouped = {};
        playerIds.forEach(id => {
          const player = sleeperPlayerNames[id];
          if (player) {
            const pos = player.position || 'OTHER';
            if (!grouped[pos]) grouped[pos] = [];
            grouped[pos].push(player);
          }
        });
        const rosterPosOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'DL', 'LB', 'DB'];
        const sortedPositions = Object.keys(grouped).sort((a, b) => {
          const ia = rosterPosOrder.indexOf(a);
          const ib = rosterPosOrder.indexOf(b);
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
        const teamDisplayName = sleeperTeamNeeds?.teams?.[rosterModalOwnerId]?.display_name || 'Team Roster';
        // Compute relative position group stars across all teams
        const teamStars = (() => {
          if (!playerValues || !sleeperLeagueInfo?.teams) return {};
          const topNC = { QB: 2, RB: 2, WR: 4, TE: 2 };
          const posScores = {};
          sleeperLeagueInfo.teams.forEach(team => {
            const oid = team.owner_id;
            const pids = sleeperRosters[oid] || [];
            const pls = pids.map(id => sleeperPlayerNames[id]).filter(Boolean);
            Object.entries(topNC).forEach(([pos, topN]) => {
              const rks = pls.filter(p => p.position === pos).map(p => playerValues[p.name.trim()]?.amp_rank).filter(r => r != null).sort((a, b) => b - a).slice(0, topN);
              if (rks.length > 0) {
                if (!posScores[pos]) posScores[pos] = {};
                posScores[pos][oid] = rks.reduce((s, r) => s + r, 0) / rks.length;
              }
            });
          });
          const res = {};
          Object.entries(posScores).forEach(([pos, scores]) => {
            const vals = Object.values(scores);
            const mn = Math.min(...vals);
            const mx = Math.max(...vals);
            const rng = mx - mn;
            Object.entries(scores).forEach(([oid, sc]) => {
              if (!res[oid]) res[oid] = {};
              res[oid][pos] = rng === 0 ? 3 : Math.round(((sc - mn) / rng) * 10) / 2;
            });
          });
          return res[rosterModalOwnerId] || {};
        })();
        return (
          <div
            onClick={() => setRosterModalOwnerId(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#1a1a2e', border: '2px solid #FFD700', borderRadius: '16px',
                maxWidth: '420px', width: '90%', maxHeight: '80vh', overflowY: 'auto',
                padding: '24px', position: 'relative',
              }}
            >
              <button
                onClick={() => setRosterModalOwnerId(null)}
                style={{
                  position: 'absolute', top: '12px', right: '12px',
                  background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)',
                  fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1,
                }}
              >
                ✕
              </button>
              <h3 style={{ color: '#FFD700', fontSize: '1.3rem', fontWeight: '700', margin: '0 0 12px 0', textAlign: 'center' }}>
                {teamDisplayName}
              </h3>
              {sortedPositions.map(pos => {
                const stars = teamStars[pos];
                const sColor = stars != null ? (stars >= 4 ? '#FFD700' : stars >= 3 ? '#10b981' : stars >= 2 ? '#f59e0b' : '#ef4444') : null;
                return (
                <div key={pos} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: '700',
                      padding: '2px 10px', borderRadius: '6px',
                      background: (positionColors[pos] || '#888') + '33',
                      color: positionColors[pos] || '#888',
                      border: '1px solid ' + (positionColors[pos] || '#888') + '66',
                    }}>
                      <span>{pos}</span>
                      {stars != null && (
                        <span style={{ fontSize: '0.7rem', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                          {[1,2,3,4,5].map(i => {
                            if (i <= Math.floor(stars)) return <span key={i} style={{ color: sColor }}>★</span>;
                            if (i - 0.5 <= stars) return <span key={i} style={{ position: 'relative', display: 'inline-block' }}><span style={{ color: 'rgba(255,255,255,0.15)' }}>★</span><span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: '50%', color: sColor }}>★</span></span>;
                            return <span key={i} style={{ color: 'rgba(255,255,255,0.15)' }}>★</span>;
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  {grouped[pos].sort((a, b) => {
                    const aRank = playerValues?.[a.name.trim()]?.amp_rank;
                    const bRank = playerValues?.[b.name.trim()]?.amp_rank;
                    if (aRank != null && bRank != null) return bRank - aRank;
                    if (aRank != null) return -1;
                    if (bRank != null) return 1;
                    return a.searchRank - b.searchRank;
                  }).map((player, j) => {
                    const pv = playerValues?.[player.name.trim()];
                    const rank = pv?.amp_rank;
                    const tierColor = rank != null ? (rank >= 80 ? '#FFD700' : rank >= 60 ? '#10b981' : rank >= 40 ? '#fff' : 'rgba(255,255,255,0.45)') : 'rgba(255,255,255,0.45)';
                    return (
                    <div key={j} style={{ padding: '3px 0 3px 8px', fontSize: '0.85rem', color: tierColor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{player.name.trim()}</span>
                      {rank != null && (
                        <span style={{ fontSize: '0.65rem', color: 'rgba(255,215,0,0.7)', fontWeight: '600' }}>
                          {rank}
                        </span>
                      )}
                      {player.team && <span style={{ color: 'rgba(255,255,255,0.4)' }}>— {player.team}</span>}
                    </div>
                    );
                  })}
                </div>
                );
              })}
              {playerIds.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontStyle: 'italic' }}>No players on roster</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Newsletter Subscribe Footer */}
      {viewMode !== 'pick-value' && viewMode !== 'tier-builder' && <div style={{
        textAlign: "center",
        padding: "32px 20px",
        marginTop: "40px",
        borderTop: "1px solid rgba(255,255,255,0.1)",
      }}>
        <a
          href="https://magic.beehiiv.com/v1/d9a1b2e9-ab0f-4549-a66a-0e4b3cdfbc6a"
          target="_blank"
          rel="noopener"
          style={{
            display: "inline-block",
            padding: "12px 32px",
            fontSize: "0.95rem",
            fontWeight: "700",
            color: "#000",
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            borderRadius: "50px",
            textDecoration: "none",
            boxShadow: "0 4px 15px rgba(255,215,0,0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,215,0,0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,215,0,0.3)";
          }}
        >
          Subscribe to the FantasyAMP Newsletter
        </a>
      </div>}

      {/* Rookie Pick Value View */}
      {false && (() => {
        const posColors = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
        const startupEquivMap = [
          '1.12','1.08','1.10','1.11','1.09','2.01','2.02','2.03',
          '2.04','2.05','2.06','2.07','2.08','2.09','2.10','2.11',
          '2.12','3.01','3.02','3.03','3.04','3.05','3.06','3.07',
        ];

        // Pull ranked players from live data for selected year
        const ranked2027 = formatAdjustedPlayers
          .filter(p => p.nfl_draft_year === pickValueYear && p.rank != null)
          .sort((a, b) => a.rank - b.rank)
          .slice(0, 24)
          .map((p, i) => {
            const round = i < 12 ? 1 : 2;
            const pickInRound = (i % 12) + 1;
            return {
              rookiePick: `${round}.${String(pickInRound).padStart(2, '0')}`,
              player: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.name || '',
              pos: p.metrics?.position || p.position || '',
              school: p.metrics?.school || p.school || '',
              startupEquiv: startupEquivMap[i] || '—',
              round,
              pickInRound,
              rankIdx: i,
              _raw: p,
            };
          });

        const yr = pickValueYear;

        const avgAmpScore = (picks) => {
          const scores = picks.map(p => parseFloat(calculateCompositeAmpScore(p._raw))).filter(s => s > 0);
          if (scores.length === 0) return null;
          return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
        };

        const categories = [
          { key: 'early1', label: `Early ${yr} 1st`, desc: '1.01 – 1.04', color: '#FFD700', picks: ranked2027.filter(p => p.round === 1 && p.pickInRound <= 4) },
          { key: 'mid1',   label: `Mid ${yr} 1st`,   desc: '1.05 – 1.08', color: '#FFD700', picks: ranked2027.filter(p => p.round === 1 && p.pickInRound >= 5 && p.pickInRound <= 8) },
          { key: 'late1',  label: `Late ${yr} 1st`,  desc: '1.09 – 1.12', color: '#FFD700', picks: ranked2027.filter(p => p.round === 1 && p.pickInRound >= 9) },
          { key: 'early2', label: `Early ${yr} 2nd`, desc: '2.01 – 2.04', color: '#C0C0C0', picks: ranked2027.filter(p => p.round === 2 && p.pickInRound <= 4) },
          { key: 'mid2',   label: `Mid ${yr} 2nd`,   desc: '2.05 – 2.08', color: '#C0C0C0', picks: ranked2027.filter(p => p.round === 2 && p.pickInRound >= 5 && p.pickInRound <= 8) },
          { key: 'late2',  label: `Late ${yr} 2nd`,  desc: '2.09 – 2.12', color: '#C0C0C0', picks: ranked2027.filter(p => p.round === 2 && p.pickInRound >= 9) },
        ];

        return (
          <div style={{ padding: '24px', maxWidth: '860px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
              <h2 style={{ fontSize: '2.2rem', fontWeight: '800', color: '#FFD700', marginBottom: '12px' }}>Rookie Pick Value</h2>
              <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.08)', borderRadius: '12px', padding: '4px', gap: '4px', marginBottom: '16px' }}>
                {[2027, 2028, 2029].map(y => (
                  <button
                    key={y}
                    onClick={() => { setPickValueYear(y); setPickValueOpen({}); }}
                    style={{
                      padding: '8px 28px',
                      borderRadius: '9px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '1rem',
                      background: pickValueYear === y ? 'linear-gradient(135deg, rgba(255,215,0,0.25), rgba(255,165,0,0.15))' : 'transparent',
                      color: pickValueYear === y ? '#FFD700' : 'rgba(255,255,255,0.4)',
                      boxShadow: pickValueYear === y ? '0 2px 8px rgba(255,215,0,0.2)' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >{y}</button>
                ))}
              </div>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', maxWidth: '560px', margin: '0 auto' }}>
                Rookie draft picks converted to startup draft equivalents. Tap a category to see the players.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {categories.map(cat => {
                const isOpen = !!pickValueOpen[cat.key];
                return (
                  <div key={cat.key} style={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid rgba(255,255,255,0.1)` }}>
                    {/* Category Header */}
                    <button
                      onClick={() => setPickValueOpen(prev => ({ ...prev, [cat.key]: !prev[cat.key] }))}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        background: isOpen ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: cat.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '1.05rem', fontWeight: '800', color: cat.color }}>{cat.label}</span>
                        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', fontWeight: '500' }}>{cat.desc}</span>
                        {(() => {
                          const avg = avgAmpScore(cat.picks);
                          if (!avg) return null;
                          const tier = getAmpTier(parseFloat(avg));
                          return (
                            <span style={{
                              fontSize: '0.72rem', fontWeight: '800',
                              color: tier.color, background: tier.bg,
                              padding: '2px 8px', borderRadius: '6px',
                              letterSpacing: '0.02em',
                            }}>
                              AMP {avg}
                            </span>
                          );
                        })()}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {!isOpen && cat.picks.length > 0 && (
                          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>
                            {cat.picks.map(p => p.player.split(' ').slice(-1)[0]).join(', ')}
                          </span>
                        )}
                        <span style={{ color: cat.color, fontSize: '1.1rem', fontWeight: '700', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', display: 'inline-block' }}>▾</span>
                      </div>
                    </button>

                    {/* Expanded Players */}
                    {isOpen && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'rgba(0,0,0,0.2)' }}>
                        {cat.picks.length === 0 ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>No 2027 prospects ranked yet</div>
                        ) : cat.picks.map(pick => (
                          <div key={pick.rookiePick} style={{
                            display: 'grid',
                            gridTemplateColumns: '70px 1fr 80px',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '14px 20px',
                            background: 'rgba(255,255,255,0.03)',
                            borderTop: '1px solid rgba(255,255,255,0.04)',
                          }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '1.05rem', fontWeight: '800', color: cat.color }}>{pick.rookiePick}</div>
                              <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px', letterSpacing: '0.05em' }}>ROOKIE</div>
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#fff' }}>{pick.player}</span>
                                {pick.pos && (
                                  <span style={{ fontSize: '0.62rem', fontWeight: '800', color: posColors[pick.pos] || '#fff', background: `${posColors[pick.pos] || '#fff'}22`, padding: '1px 6px', borderRadius: '4px' }}>{pick.pos}</span>
                                )}
                              </div>
                              {pick.school && <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>{pick.school}</div>}
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', marginBottom: '3px', fontWeight: '600', letterSpacing: '0.05em' }}>STARTUP</div>
                              <div style={{
                                fontSize: '0.95rem', fontWeight: '800', color: '#10b981',
                                background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
                                borderRadius: '8px', padding: '4px 8px',
                              }}>{pick.startupEquiv}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: '24px', padding: '14px 18px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
              * Startup equivalents based on a 12-team, 1QB dynasty league. Values shift in superflex — QBs move up 1-2 slots. Rankings update live as the 2026 college season progresses.
            </div>
          </div>
        );
      })()}

    </div>
    </>
  );
}
